import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useMemo, useContext } from 'react';
import { flushSync } from 'react-dom';
import * as uuid from 'uuid';
import PouchComponent from './PouchComponent';
import GroupComponent, { PendingGroupComponent } from './GroupComponent';
import { LegendsComponent } from './LegendsComponent';
import { Pouch, ActiveSheetState, POUCH_COLORS, GROUP_COLORS, Point, Group, PouchColorLegend, Sheet, HistoryActionType } from '../types';
import { detectTouchDevice } from '../utils/detectTouchDevice';
import { timePouchStateFromEffectiveMinutes } from '../utils/timePouchState';
import {
  animateDocumentScrollBy,
  animateScrollEdgeBufferToZero,
  getDocumentScrollElement,
  getRevealScrollMinVisibleLeft,
  UNDO_REDO_TRANSITION_MS,
} from '../utils/documentAutoScroll';
import { getPouchScaledValue, clampRoundUnitToScaledCap, clampRoundUnitToAbsoluteCap } from '../utils/pouchScaleAndRound';
import {
  formatEnterAndDigitsCreatePouchHtml,
  formatLayerGuideShortcutsHtml,
  formatSheetTitleGuideShortcutHtml,
  formatKeyDescLine,
  formatTooltip,
} from '../utils/tooltipFormatter';
import { useLocale } from '../contexts/LocaleContext';
import { createPouch, updatePouch, clonePouch } from '../logic/PouchLogic';
import {
  buildSplitPouches,
  buildSplitGroup,
  buildMergedScalarPouchFromLeaves,
  canSplitPouchByMultiplier,
  canHalfSplitPouchByMultiplier,
  getGroupScalarMergeLeaves,
  allScalarMergeLeavesAreTimePouches,
  inheritPouchTitleToParentGroupIfEmpty,
  inheritPouchColorToParentGroup,
  splitPouchScalars,
} from '../logic/SplitPouchLogic';
import {
  computePlacementViewport,
  computeRevealScrollDelta,
  computeUndoRedoRevealBounds,
  computeSplitGroupInsets,
  expandPlacementViewportForSplit,
  expandViewportForSplitRelocate,
  getGroupPathBounds,
  expandViewportSearchExtents,
  planSplitPlacement,
  type Rect,
} from '../logic/PouchPlacementLogic';
import {
  POUCH_VALUE_DECIMAL_PLACES,
  POUCH_VALUE_STEP,
  roundPouchValue,
} from '../utils/pouchValueRounding';
import {
  pouchWithLayerValue,
  resolveActiveLayerId,
  setPouchLayerValue,
  applyPouchScalarForSheet,
  syncSheetPouchesToActiveLayer,
  mergeTwoPouchesOnCanvas,
  buildGroupMergePouchLayerData,
  renderGroupInclusiveGtpForSheet,
  applyPouchRoundUnitForLayer,
  getPouchLayerRoundUnit,
  getPouchLayerRoundUnitWhenLastExpression,
  getLayerValue,
  getPouchLayerIsTime,
  getPouchLayerMultiplier,
  getPrimaryTimeLayerId,
  mirrorPouchesForLayer,
  pouchUsesLayerValues,
  syncMergedTimePouchTopLevel,
} from '../logic/LayerLogic';
import { calculateColorTotal, updateColorLegend } from '../logic/LegendsLogic';
import { 
  startGroupDrawing, 
  continueGroupDrawing, 
  finishGroupDrawing, 
  isPointInPath,
  getPathBounds,
  calculateGroupSubtotal as calculateGroupTotal,
  translatePath,
  generateRoundedRectPath,
  isPouchInGroup,
  changeGroupColor,
  updateGTPReferencesAfterClone,
  cloneGroup,
  isGroupPhysicallyContained,
  collectPouchIdsForGroupSubtree,
  getContainedGroups,
  deleteGroupWithContents,
  getMatchingPouchColor,
  shouldShowTotalLabel,
  snapToGrid,
  findGroupInclusiveGtp,
  renderGroupInclusiveGtp,
  getGroupTotalPouchRoundUnitScaledCap,
} from '../logic/GroupLogic';
import { HistoryLogic } from '../utils/HistoryLogic';
import { GroupZIndexManager } from '../utils/GroupZIndexManager';
import { computeAllGroupDisplayTotals } from '../utils/groupDisplayTotals';
import { isEditableElement } from '../utils/pouchColorHotkeys';
import { calcKeydownShouldYieldToLayerShortcut } from '../utils/layerKeyboardShortcuts';
import './styles/GroupComponent.scss'; // 必要なスタイルをインポート
import { initialState } from '../App';
import { GuideContext } from '../contexts/GuideContext';

// uuidv4関数をインポートから取得
const uuidv4 = uuid.v4;

const CALC_AREA_GUIDE_HOVER_MS = 2000;

/** 計算エリア空白の数字バッファ用。e.key のみでは JIS 等で取り逃すため e.code / 全角にも対応 */
function getDigitCharForEmptyCalcKeydown(e: KeyboardEvent): string | null {
  if (e.altKey) {
    return null;
  }
  const digitMatch = /^Digit([0-9])$/.exec(e.code);
  if (digitMatch) {
    return digitMatch[1];
  }
  const numpadMatch = /^Numpad([0-9])$/.exec(e.code);
  if (numpadMatch) {
    return numpadMatch[1];
  }
  if (e.key.length === 1) {
    const c = e.key.charCodeAt(0);
    if (c >= 0xff10 && c <= 0xff19) {
      return String.fromCharCode(c - 0xff10 + 0x30);
    }
  }
  if (/^[0-9]$/.test(e.key)) {
    return e.key;
  }
  return null;
}

function isMinusKeyForEmptyCalc(e: KeyboardEvent): boolean {
  if (e.altKey) {
    return false;
  }
  return (
    e.code === 'Minus' ||
    e.code === 'NumpadSubtract' ||
    e.key === '-' ||
    e.key === '−' ||
    e.key === '‐'
  );
}

/** 空白からキーボード新規パウチを始めるときの「:」（0:◯ など時間入力用） */
function isColonKeyForEmptyCalc(e: KeyboardEvent): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return false;
  }
  return e.key === ':' || e.key === '：';
}

/**
 * 既存パウチの値編集を「1文字目で置換」で開始するときのその1文字。
 * ガイドには列挙しないが、数式に入力可能な記号（乗除・%^~・括弧・区切り等）も含む。
 */
function getPouchFormulaEditTriggerChar(e: KeyboardEvent): string | null {
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return null;
  }
  const digit = getDigitCharForEmptyCalcKeydown(e);
  if (digit) {
    return digit;
  }
  if (isMinusKeyForEmptyCalc(e)) {
    return '-';
  }

  const fromCode: Record<string, string> = {
    NumpadAdd: '+',
    NumpadMultiply: '*',
    NumpadDivide: '/',
    NumpadDecimal: '.',
  };
  const mapped = fromCode[e.code];
  if (mapped) {
    return mapped;
  }

  if (e.key.length !== 1) {
    return null;
  }
  const k = e.key;
  if (k === '×') {
    return '*';
  }
  if (k === '÷') {
    return '/';
  }
  const fw: Record<string, string> = {
    '＋': '+',
    '－': '-',
    '＊': '*',
    '／': '/',
    '％': '%',
    '：': ':',
  };
  const n = fw[k] ?? k;
  if ('+-*/%^~().,:?xX='.includes(n)) {
    return n;
  }
  return null;
}

/** 既存パウチ上でキーから値編集に入るときの対象 ID（ボタン上は除外） */
function getPouchIdForKeyboardValueEditShortcut(
  areaEl: HTMLDivElement,
  clientX: number,
  clientY: number
): string | null {
  const el = document.elementFromPoint(clientX, clientY);
  if (!el || !areaEl.contains(el)) {
    return null;
  }
  if (el.closest('button') || el.closest('.group-title-input') || el.closest('.pouch-title')) {
    return null;
  }
  const container = el.closest('.pouch-container[data-pouch-id]') as HTMLElement | null;
  if (!container) {
    return null;
  }
  return container.getAttribute('data-pouch-id');
}

/**
 * ダブルクリックで新規パウチを作る条件と揃える（handleMouseUp 内の isButtonOrInButton 相当）。
 * ホバー ref はグループ内の空白やタイミングでズレるため、pointer の elementFromPoint で判定する。
 *
 * keyboardContinuePouchId: 数字キー新規の保留中／値編集合流中は、マウスがそのパウチ上に乗っても
 * 続きのキーを受け付ける（描画直後にポインタがパウチ直下になり pointer-not-eligible になる問題の回避）。
 */
function isPointerEligibleForNewPouchKeyboardShortcuts(
  areaEl: HTMLDivElement,
  clientX: number,
  clientY: number,
  keyboardContinuePouchId: string | null = null
): boolean {
  const el = document.elementFromPoint(clientX, clientY);
  if (!el || !areaEl.contains(el)) {
    return false;
  }
  const overPouchWrapper = el.closest('.pouch-wrapper');
  if (overPouchWrapper) {
    if (
      keyboardContinuePouchId &&
      overPouchWrapper.querySelector(`[data-pouch-id="${keyboardContinuePouchId}"]`)
    ) {
      return true;
    }
    return false;
  }
  const isButtonOrInButton =
    el.closest('button') !== null ||
    el.closest('.group-buttons') !== null ||
    el.closest('.pouch-color-button, .pouch-delete-button, .pouch-split-button, .pouch-toggle-sign-button') !== null ||
    el.closest('.group-title-input') !== null;
  if (isButtonOrInButton) {
    return false;
  }
  return true;
}

/** キーボード新規の入力バッファから初期表示用の数値（式は undefined＝last 値のまま） */
function parseSeedBufferToInitialPouchValue(buf: string): number | undefined {
  if (buf === '' || buf === '-') {
    return 0;
  }
  if (/^-?\d+$/.test(buf)) {
    return parseInt(buf, 10);
  }
  return undefined;
}

// Z-indexの定数定義
const MIN_POUCH_Z_INDEX = 200001;
const MAX_POUCH_Z_INDEX = 299999;
const POUCH_Z_INDEX_THRESHOLD = 299900; // z-index振り直しの閾値

// 操作タイプの定義
type InteractionType = 
  | 'CLICK'           // PCクリック・モバイルタップ
  | 'DOUBLE_CLICK'    // PCダブルクリック・モバイルダブルタップ
  | 'RIGHT_CLICK'     // PC右クリック
  | 'DRAG'            // PCドラッグ・モバイルドラッグ
  | 'LONG_PRESS'      // モバイル長押し
  | 'LONG_CLICK'      // PC長押し
  | 'LONG_PRESS_DRAG'  // モバイル長押し後ドラッグ
  | 'LONG_CLICK_DRAG'  // PC長押し後ドラッグ
  | 'GROUP_DRAG'      // グループドラッグ
  | 'GROUP_RESIZE';   // グループリサイズ

// 操作状態を管理する型
interface InteractionState {
  type: InteractionType | null;
  targetId: string | null;
  targetType: 'pouch' | 'group' | 'resize-handle' | 'pending-pouch' | null;
  startPosition: { x: number, y: number };
  currentPosition: { x: number, y: number };
  startTime: number;
  lastClickTime: number;
  isLongPressDetected: boolean;
  longPressTimer: number | null;
  isTouchEvent: boolean;
  dragOffset: { x: number, y: number }; // ドラッグ時のオフセット
  isDrawingGroup: boolean; // グループ描画中フラグ
  groupStartPoint: Point | null; // グループ描画開始点
  currentGroupPath: string; // 現在描画中のグループパス
  groupOriginalPath?: string; // グループリサイズ前の元のパス
  draggedFromOtherSheet?: boolean; // 他のシートからドラッグされたかどうか
  draggedGroupId?: string; // ドラッグ中のグループID
  groupInitialBounds?: { left: number; top: number; right: number; bottom: number } | null; // リサイズ開始時のグループの境界
  isCloning?: boolean; // 複製フラグ
  isShiftPressed?: boolean; // Shiftキーが押されているかどうか
  hasError?: boolean; // エラーフラグ（historyに記録しないようにするため）
  groupPouches: {id:string, x:number, y:number}[]; // グループドラッグ開始時の所属パウチの元の位置
  containedGroups: {id: string, path: string}[]; // 完全に内包されているグループ
  clonedGroup?: Group; // 複製したグループを参照するための一時的な保存領域
  lastClickPosition?: { x: number, y: number }; // 最後にクリックされた位置を保存
  isFirstMoveAfterCreateDragged?: boolean; // createdragging後の初回移動フラグ
  originalPouchId?: string; // 複製元のパウチID（複製操作時に使用）
  longPressStartTime?: number; // 長押し判定開始時刻
  multiplierChangeTimer1?: number; // 1秒後のタイマーID
  gtpBoundaryChecks?: { [gtpId: string]: { nearRight: boolean; nearBottom: boolean } }; // GTPの境界判定結果
  gtpRelativePositions?: { [gtpId: string]: { relativeToRight?: number; relativeToBottom?: number } }; // GTPの相対位置
  resizeCorner?: 'br' | 'bl' | 'tr' | 'tl'; // リサイズ中の角（br=右下, bl=左下, tr=右上, tl=左上）
  multiplierChangeTimer2?: number; // 2秒後のタイマーID
  phaseTimers?: number[]; // 段階変更のタイマーID配列
  originalMultiplier?: number; // 元のマルチ値
  cloneMultiplier?: number; // 複製パウチのマルチ値
  sourceMultiplier?: number; // 元パウチのマルチ値
  disablePhaseChange?: boolean; // 段階マルチ変更を無効化するフラグ
  modifierKeysOnHold?: { ctrlOption: boolean; shift: boolean }; // 長押しホールド時の修飾キー状態
  /** タッチ2本指でのページパン（1本指はグループ描画用のため） */
  isTwoFingerPan?: boolean;
}

/** パウチ／グループドラッグ中、ポインタが画面端付近のときの自動スクロール */
const DRAG_EDGE_SCROLL_MARGIN_PX = 50;
const DRAG_EDGE_SCROLL_MAX_DELTA = 12;

interface DragEdgeScrollLayout {
  isMobile: boolean;
  isLeftSidebarOpen: boolean;
}

function shouldApplyDragEdgeAutoScroll(interaction: InteractionState): boolean {
  const it = interaction.type;
  return (
    it === 'GROUP_DRAG' ||
    it === 'LONG_CLICK_DRAG' ||
    it === 'LONG_PRESS_DRAG' ||
    (it === 'DRAG' && (interaction.targetType === 'pouch' || interaction.targetType === 'group'))
  );
}

function applyDragEdgeAutoScroll(
  clientX: number,
  clientY: number,
  layout: DragEdgeScrollLayout,
  afterScroll?: () => void,
): void {
  const EDGE = DRAG_EDGE_SCROLL_MARGIN_PX;
  const MAX = DRAG_EDGE_SCROLL_MAX_DELTA;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let sidebarWidth = 0;
  if (!layout.isMobile && layout.isLeftSidebarOpen) {
    const sb = document.querySelector('.left-sidebar.open') as HTMLElement | null;
    if (sb) {
      sidebarWidth = sb.getBoundingClientRect().width;
    }
  }
  const leftZoneOuter = sidebarWidth + EDGE;

  let dx = 0;
  let dy = 0;

  if (clientX < leftZoneOuter) {
    const depth = leftZoneOuter - clientX;
    const t = Math.min(1, depth / EDGE);
    dx -= MAX * t;
  }
  const rightZoneInner = vw - EDGE;
  if (clientX > rightZoneInner) {
    const depth = clientX - rightZoneInner;
    const t = Math.min(1, depth / EDGE);
    dx += MAX * t;
  }
  if (clientY < EDGE) {
    const depth = EDGE - clientY;
    const t = Math.min(1, depth / EDGE);
    dy -= MAX * t;
  }
  const bottomZoneInner = vh - EDGE;
  if (clientY > bottomZoneInner) {
    const depth = clientY - bottomZoneInner;
    const t = Math.min(1, depth / EDGE);
    dy += MAX * t;
  }

  if (dx === 0 && dy === 0) {
    return;
  }

  const el = getDocumentScrollElement();
  const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
  const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
  const nextLeft = Math.min(maxScrollLeft, Math.max(0, el.scrollLeft + dx));
  const nextTop = Math.min(maxScrollTop, Math.max(0, el.scrollTop + dy));
  const applyX = nextLeft - el.scrollLeft;
  const applyY = nextTop - el.scrollTop;
  if (applyX !== 0 || applyY !== 0) {
    window.scrollBy(applyX, applyY);
    afterScroll?.();
  }
}

const SPLIT_REVEAL_SCROLL_PADDING_PX = 48;

function applyScrollToRevealContentBounds(
  areaEl: HTMLElement,
  bounds: Rect,
  layout: DragEdgeScrollLayout,
  afterScroll?: () => void,
): void {
  const areaRect = areaEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const minVisibleLeft = getRevealScrollMinVisibleLeft(areaRect, layout);

  const { dx, dy } = computeRevealScrollDelta(bounds, areaRect, vw, vh, {
    padding: SPLIT_REVEAL_SCROLL_PADDING_PX,
    minVisibleLeft,
  });
  if (dx === 0 && dy === 0) {
    return;
  }

  void animateDocumentScrollBy(dx, dy).then(() => afterScroll?.());
}

// 保留中のパウチの型定義
interface PendingPouch {
  id: string;
  x: number;
  y: number;
  createdAt: number;
  timerId: number | null;
  color: string;
}

// パウチの型定義
interface PouchWithDrag extends Pouch {
  _isDragging?: boolean;
  _isMoving?: boolean; // パウチ移動完了フラグを追加
  _isCloneSource?: boolean; // 複製元フラグを追加
  _isCloning?: boolean; // 複製パウチフラグを追加
  _isFromOtherSheet?: boolean; // シート間移動フラグを追加
  hasError?: boolean; // エラー状態フラグを追加
}

// グループの型定義
interface GroupWithDrag extends Group {
  _isDragging?: boolean;
}

interface ActiveSheetComponentProps {
  activeSheetId: string;
  initialPouches: Pouch[];
  initialGroups?: Group[];
  lastPouchValue: number;
  lastPouchColor: string;
  lastGroupColor: string;
  defaultValue: number;
  isInitialLoading?: boolean; // リロード時とシート切替時のフラグ
  colorLegends?: PouchColorLegend[]; // 追加
  onUpdateActiveSheet?: (pouches: Pouch[] | null, groups: Group[] | null, isCritical?: boolean, saveToDb?: boolean, sheetParams?: Partial<Sheet>, historyActionType?: HistoryActionType, forceNewEntry?: boolean, sourceSheetId?: string, stripAddPouchHistoryForPouchId?: string) => void; // 統合更新用
  isTouchDevice?: boolean; // タッチデバイスフラグのデフォルト値
  onUpdateDeviceType?: (isTouchDevice: boolean) => void; // デバイスタイプ変更通知用
  onExportGroup?: (groupId: string) => void; // グループ書き出し機能用
  onToggleGroupSign?: (groupId: string, currentPouches?: Pouch[], currentGroups?: Group[]) => void; // グループ正負転換機能用
  currentSheet?: Sheet; // 現在のシート情報
  isDraggingToSheet?: boolean; // シート間ドラッグ状態
  onStartSheetDrag?: (groupData: {
    group: Group;
    pouches: Pouch[];
    containedGroups: Group[];
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  }) => void; // シート間ドラッグ開始通知
  globalInteractionRef?: React.RefObject<any>; // グローバルなドラッグ状態管理用
  onRemoveGroupFromSourceSheet?: (groupId: string, sourceSheetId: string) => Promise<void>; // 元シートからグループ削除用
  sourceSheetGroupRef?: React.RefObject<Group | null>; // 元シートのグループ参照
  draggedGroupDataRef?: React.RefObject<any>; // ドラッグされたグループデータの参照
  onStartSheetPouchDrag?: (pouchData: {
    pouch: Pouch;
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  }) => void; // シート間パウチドラッグ開始通知
  onRemovePouchFromSourceSheet?: (pouchId: string, sourceSheetId: string) => Promise<void>; // 元シートからパウチ削除用
  sourceSheetPouchRef?: React.RefObject<Pouch | null>; // 元シートのパウチ参照
  draggedPouchDataRef?: React.RefObject<any>; // ドラッグされたパウチデータの参照
  onResetSheetDragState?: () => void; // シート間ドラッグ状態リセット用
  onTableHoverChange?: (groupId: string | null, pouchId: string | null) => void; // テーブルhover状態変更通知用
  onToggleBalancingPouch?: (pouchId: string) => void; // 帳尻パウチ（BP）トグル
  /** 計算エリア空白で F2 / Space 時にアクティブシートタイトルへフォーカス */
  onRequestFocusSheetTitle?: () => void;
  /** ヘッダのシートタイトル入力がフォーカス中（計算エリアツールチップを編集モードに） */
  isSheetTitleEditing?: boolean;
  /** モバイルレイアウト（ドラッグ時の端オートスクロールで左サイドバー分を無視） */
  isMobile?: boolean;
  /** 左メニューが開いている（PCのみ、ドラッグ端オートスクロールの左端をサイドバー幅＋余白に） */
  isLeftSidebarOpen?: boolean;
}

// ActiveSheetComponentのref型定義
export interface ActiveSheetComponentRef {
  createPouchAtCenter: () => void;
  getInteractionState: () => InteractionState;
  setPouches: (pouches: Pouch[]) => void;
  setGroups: (groups: Group[]) => void;
  forceUpdate: () => void;
  setError: (pouchId: string, hasError: boolean) => void;
  createDraggedContent: (data: {
    group: Group;
    pouches: Pouch[];
    containedGroups: Group[];
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  }) => void;
  createDraggedPouch: (data: {
    pouch: Pouch;
    finalPosition: { x: number, y: number };
    sourceSheetId: string;
  }) => void;
  transferDragFromOtherSheet: (interactionState: InteractionState) => void;
  removeAllEventListeners: () => void;
  removeCrossSheetElements: () => void;
  clearCanvasHover: () => void;
  revealScrollForUndoRedo: (
    prevPouches: Pouch[],
    prevGroups: Group[],
    nextPouches: Pouch[],
    nextGroups: Group[],
  ) => void;
}

const ActiveSheetComponent: React.ForwardRefRenderFunction<ActiveSheetComponentRef, ActiveSheetComponentProps> = ({
  activeSheetId,
  initialPouches,
  initialGroups = [],
  lastPouchValue,
  lastPouchColor,
  lastGroupColor,
  defaultValue = 100,
  isInitialLoading = false,
  colorLegends = [], // 追加
  onUpdateActiveSheet = () => {}, // 統合更新用
  isTouchDevice = false, // タッチデバイスフラグのデフォルト値
  onUpdateDeviceType = () => {}, // デバイスタイプ変更通知用
  onExportGroup = () => {}, // グループ書き出し機能用
  onToggleGroupSign = () => {}, // グループ正負転換機能用
  currentSheet, // 現在のシート情報
  isDraggingToSheet = false, // シート間ドラッグ状態
  onStartSheetDrag = () => {}, // シート間ドラッグ開始通知
  globalInteractionRef, // グローバルなドラッグ状態管理用
  onRemoveGroupFromSourceSheet, // 元シートからグループ削除用
  sourceSheetGroupRef, // 元シートのグループ参照
  draggedGroupDataRef, // ドラッグされたグループデータの参照
  onStartSheetPouchDrag, // シート間パウチドラッグ開始通知
  onRemovePouchFromSourceSheet, // 元シートからパウチ削除用
  sourceSheetPouchRef, // 元シートのパウチ参照
  draggedPouchDataRef, // ドラッグされたパウチデータの参照
  onResetSheetDragState, // シート間ドラッグ状態リセット用
  onTableHoverChange = () => {}, // テーブルhover状態変更通知用
  onToggleBalancingPouch, // 帳尻パウチ（BP）トグル
  onRequestFocusSheetTitle = () => {},
  isSheetTitleEditing = false,
  isMobile = false,
  isLeftSidebarOpen = false,
}, ref) => {
  const dragEdgeScrollLayoutRef = useRef<DragEdgeScrollLayout>({
    isMobile,
    isLeftSidebarOpen,
  });
  dragEdgeScrollLayoutRef.current = { isMobile, isLeftSidebarOpen };

  // シートの状態管理
  const [pouches, setPouches] = useState<PouchWithDrag[]>(initialPouches || []);
  
  // パウチのrefを管理するためのMap
  const pouchRefs = useRef<
    Map<
      string,
      React.RefObject<{
        revertToOriginalValues: () => void;
        handleMouseUp: () => void;
        keyboardSimulatePressStart: (
          type: 'value' | 'multiplier' | 'roundUnit',
          direction: 'increase' | 'decrease',
          shiftKey: boolean
        ) => boolean;
        keyboardSimulatePressEnd: () => void;
      } | null>
    >
  >(new Map());
  /** パウチホバー中の矢印キー（値／マルチ調整）の keyup までの対象 ID */
  const pouchArrowAdjustActivePouchIdRef = useRef<string | null>(null);
  // パウチエラー表示のタイマーを管理するMap（パウチIDごとにタイマーを管理）
  const pouchErrorTimersRef = useRef<Map<string, number>>(new Map());
  const showPouchErrorRef = useRef<(pouchId: string) => void>(() => {});
  const [groups, setGroups] = useState<Group[]>(initialGroups || []);
  
  // デバイスフラグ状態
  const [isCurrentTouchDevice, setIsCurrentTouchDevice] = useState<boolean>(isTouchDevice);
  // デバイスフラグ用ref
  const isTouchDeviceRef = useRef<boolean>(isTouchDevice);

  const [calcAreaGuideHoverTier, setCalcAreaGuideHoverTier] = useState<0 | 1 | 2>(0);
  const calcAreaGuideHoverTimerRef = useRef<number | null>(null);
  const isGuideEnabled = useContext(GuideContext);
  const { tooltipCopy: tt } = useLocale();
  
  // ドラッグ開始時の元シートIDを保持するref
  const dragStartSheetIdRef = useRef<string | null>(null);
  
  // リサイズ完了後のuseEffect上書きをスキップするためのフラグ
  const skipCurrentSheetUpdateRef = useRef<boolean>(false);
  
  // シート切り替え時に最新データで初期化
  useEffect(() => {
    if (currentSheet) {
      // リサイズ中/完了直後はスキップ（古いcurrentSheetで上書きされるのを防ぐ）
      if (interactionRef.current?.type === 'GROUP_RESIZE' || skipCurrentSheetUpdateRef.current) {
        return;
      }
      
      // currentSheetの最新データを使用（レイヤー切替後は pouch.value を activeLayer に同期）
      const sheetSynced = syncSheetPouchesToActiveLayer(currentSheet);
      const latestPouches = sheetSynced.pouches.map(p => ({
        ...p,
        _isDragging: false,
        _isCloneSource: false,
        _isCloning: false,
        _isFromOtherSheet: false
      }));
      
      const latestGroups = currentSheet.groups.map(g => ({
        ...g,
        _isDragging: false,
        _isCloning: false
      }));
      
      setPouches(latestPouches);
      setGroups(latestGroups);
      pouchesRef.current = latestPouches;
      groupsRef.current = latestGroups;
    }
  }, [currentSheet?.id, currentSheet?.activeLayerId, currentSheet?.pouches, currentSheet?.groups]);
  
  // プラットフォーム判定関数
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const getCtrlOptionModifierKey = (e: KeyboardEvent | MouseEvent) => {
    return isMac ? e.altKey : e.ctrlKey;
  };
  const getAltControlModifierKey = (e: KeyboardEvent | MouseEvent) => {
    return isMac ? e.ctrlKey : e.altKey;
  };
  
  // テーブル選択状態管理
  const [selectedGroups, setSelectedGroups] = useState<{[groupId: string]: boolean}>({});
  const [selectedPouches, setSelectedPouches] = useState<{[pouchId: string]: boolean}>({});
  
  // ホバー状態管理
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  /** setState より先に同期更新（グループ色ホットキーが直後の keydown で前グループに効かないようにする） */
  const hoveredGroupIdRef = useRef<string | null>(null);
  const [hoveredPouchId, setHoveredPouchId] = useState<string | null>(null);
  /** パウチホバー同期（キーボードショートカットが直後の keydown で前パウチに効かないようにする） */
  const hoveredPouchIdRef = useRef<string | null>(null);
  /** 計算エリア上にマウスがあるか（空白ホットキー用） */
  const isMouseOverCalcAreaRef = useRef(false);
  /** 計算エリア内の最終マウス位置（area 相対、新規パウチ用） */
  const lastMouseInAreaLocalRef = useRef({ x: 0, y: 0 });
  /** elementFromPoint 用（画面座標） */
  const lastPointerClientRef = useRef({ x: 0, y: 0 });
  /** 空白エリア: Enter なしで新規パウチに入力する文字列（デバウンス中） */
  const newPouchKeyBufferRef = useRef('');
  const keyboardValueEditSeedRef = useRef<{
    pouchId: string;
    text?: string;
    caretAtEnd?: boolean;
    selectAllOnFocus?: boolean;
  } | null>(null);
  /** 数字キーで作成済み・まだ値編集を開いていないパウチ */
  const pendingNewPouchIdRef = useRef<string | null>(null);
  const pendingNewPouchKeyboardTimerRef = useRef<number | null>(null);
  /** 数字新規: microtask で flush を1回だけ予約（rAF より早く値編集を開き 1 文字目からキャレットを出す） */
  const pendingFlushScheduleGenRef = useRef(0);
  /** 値編集オープン時の DOM 待ち rAF（スケジュールと別 ref で競合回避） */
  const valueEditMountRetryRafRef = useRef<number | null>(null);
  const flushPendingNewPouchKeyboardRef = useRef<(() => void) | null>(null);
  const handleDeletePouchForKeyboardRef = useRef<(pouchId: string) => void>(() => {});
  /** Esc でホバー中パウチ／グループの正負反転（実装は handleUpdateValue 定義後の useEffect で代入） */
  const keyboardEscToggleSignRef = useRef<() => void>(() => {});
  /** キーボード新規→値編集オープン直後のみ Esc で履歴なし削除を許可 */
  const keyboardNewPouchFirstValueEditPouchIdRef = useRef<string | null>(null);
  /**
   * キーボード新規で値編集を開いた直後〜input がフォーカスを取るまでの隙間に打った文字を同じパウチへ合流する
   * （約100ms付近だけ新規パウチ扱いになるデッドゾーン対策）
   */
  const keyboardOpenFocusGracePouchIdRef = useRef<string | null>(null);
  const keyboardGraceClearTimeoutRef = useRef<number | null>(null);
  const keyboardOpenExtraCharsRef = useRef<{ pouchId: string; chars: string } | null>(null);
  const [keyboardGraceAppendBump, setKeyboardGraceAppendBump] = useState(0);
  const [gtpHoveredGroupId, setGtpHoveredGroupId] = useState<string | null>(null);
  const [hoveredLegendColorId, setHoveredLegendColorId] = useState<string | null>(null);
  
  // ホバー解除用のタイマー
  const groupHoverTimerRef = useRef<number | null>(null);
  const pouchHoverTimerRef = useRef<number | null>(null);
  const delayedTouchResetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const gtpGroupHoverTimerRef = useRef<number | null>(null);

  // ホバーイベントハンドラー
  const handleGroupHover = useCallback((groupId: string | null) => {
    // 既存のタイマーをクリア
    if (groupHoverTimerRef.current) {
      clearTimeout(groupHoverTimerRef.current);
      groupHoverTimerRef.current = null;
    }
    
    if (groupId) {
      hoveredGroupIdRef.current = groupId;
      // ホバー開始時は即座に状態更新
      setHoveredGroupId(groupId);
      // パウチのhover状態をクリア
      hoveredPouchIdRef.current = null;
      setHoveredPouchId(null);
      // テーブルにhover状態を通知
      onTableHoverChange(groupId, null);
    } else {
      hoveredGroupIdRef.current = null;
      // ホバー解除時は即座に状態更新（遅延なし）
      setHoveredGroupId(null);
      // テーブルのhover状態をクリア
      onTableHoverChange(null, null);
    }
  }, [onTableHoverChange]);

  const clearCanvasHover = useCallback(() => {
    if (groupHoverTimerRef.current) {
      clearTimeout(groupHoverTimerRef.current);
      groupHoverTimerRef.current = null;
    }
    if (pouchHoverTimerRef.current) {
      clearTimeout(pouchHoverTimerRef.current);
      pouchHoverTimerRef.current = null;
    }
    hoveredGroupIdRef.current = null;
    hoveredPouchIdRef.current = null;
    setHoveredGroupId(null);
    setHoveredPouchId(null);
    onTableHoverChange(null, null);
  }, [onTableHoverChange]);

  const handlePouchHover = useCallback((pouchId: string | null) => {
    // 既存のタイマーをクリア
    if (pouchHoverTimerRef.current) {
      clearTimeout(pouchHoverTimerRef.current);
      pouchHoverTimerRef.current = null;
    }
    
    if (pouchId) {
      hoveredGroupIdRef.current = null;
      hoveredPouchIdRef.current = pouchId;
      // ホバー開始時は即座に状態更新
      setHoveredPouchId(pouchId);
      // グループのhover状態をクリア
      setHoveredGroupId(null);
      // テーブルにhover状態を通知
      onTableHoverChange(null, pouchId);
    } else {
      hoveredPouchIdRef.current = null;
      // ホバー解除時は即座に状態更新（遅延なし）
      setHoveredPouchId(null);
      // テーブルのhover状態をクリア
      onTableHoverChange(null, null);
    }
  }, [onTableHoverChange]);

  const handleGtpGroupHover = useCallback((groupId: string | null) => {
    // 既存のタイマーをクリア
    if (gtpGroupHoverTimerRef.current) {
      clearTimeout(gtpGroupHoverTimerRef.current);
      gtpGroupHoverTimerRef.current = null;
    }
    
    if (groupId) {
      // ホバー開始時は即座に状態更新
      setGtpHoveredGroupId(groupId);
    } else {
      // ホバー解除時は少し遅延を追加
      gtpGroupHoverTimerRef.current = window.setTimeout(() => {
        setGtpHoveredGroupId(null);
        gtpGroupHoverTimerRef.current = null;
      }, 100);
    }
  }, []);

  // 凡例ホバーハンドラー
  const handleLegendHover = useCallback((colorId: string | null) => {
    setHoveredLegendColorId(colorId);
  }, []);
  
  // gtpHoveredGroupIdの変更を検知して該当するグループに直接クラスを適用
  useEffect(() => {
    // 全てのグループからgroup-related-gtp-hoveredクラスを削除
    const allGroupElements = document.querySelectorAll('.group-wrapper');
    allGroupElements.forEach(element => {
      element.classList.remove('group-related-gtp-hovered');
    });
    
    // テーブル行からもgroup-related-gtp-hoveredクラスを削除
    document.querySelectorAll('tr.group-related-gtp-hovered').forEach(element => {
      element.classList.remove('group-related-gtp-hovered');
    });
    
    // gtpHoveredGroupIdが設定されている場合、該当するグループにクラスを追加
    if (gtpHoveredGroupId) {
      const targetGroupElement = document.querySelector(`[data-group-id="${gtpHoveredGroupId}"]`);
      if (targetGroupElement) {
        targetGroupElement.classList.add('group-related-gtp-hovered');
      }
      
      // テーブルの対応するグループ行にもクラスを追加
      const targetGroupRows = document.querySelectorAll(`tr[data-group-id="${gtpHoveredGroupId}"]:not([data-pouch-id])`);
      targetGroupRows.forEach(element => {
        element.classList.add('group-related-gtp-hovered');
      });
    }
  }, [gtpHoveredGroupId]);

  // hoveredLegendColorIdの変更を検知して該当するパウチとテーブル行にlegend-hoveredクラスを適用
  useEffect(() => {
    // 全てのパウチからlegend-hoveredクラスを削除
    document.querySelectorAll('.pouch-container.legend-hovered').forEach(element => {
      element.classList.remove('legend-hovered');
    });
    
    // テーブル行からもlegend-hoveredクラスを削除
    document.querySelectorAll('tr.legend-hovered').forEach(element => {
      element.classList.remove('legend-hovered');
    });
    
    // hoveredLegendColorIdが設定されている場合、該当する色のパウチとテーブル行にクラスを追加
    if (hoveredLegendColorId) {
      // 該当する色のパウチにクラスを追加
      document.querySelectorAll(`[data-pouch-id]`).forEach(element => {
        const pouchId = element.getAttribute('data-pouch-id');
        if (pouchId) {
          const pouch = pouches.find(p => p.id === pouchId);
          if (pouch && pouch.color === hoveredLegendColorId) {
            element.classList.add('legend-hovered');
          }
        }
      });
      
      // テーブルの対応する色の行にもクラスを追加
      document.querySelectorAll(`tr[data-pouch-id]`).forEach(element => {
        const pouchId = element.getAttribute('data-pouch-id');
        if (pouchId) {
          const pouch = pouches.find(p => p.id === pouchId);
          if (pouch && pouch.color === hoveredLegendColorId) {
            element.classList.add('legend-hovered');
          }
        }
      });
    }
  }, [hoveredLegendColorId, pouches]);

  // hoveredGroupIdの変更を検知して関連するGTPのテーブル行にクラスを適用
  useEffect(() => {
    // テーブル行からpouch-related-group-hoveredクラスを削除
    document.querySelectorAll('tr.pouch-related-group-hovered').forEach(element => {
      element.classList.remove('pouch-related-group-hovered');
    });
    
    // hoveredGroupIdが設定されている場合、そのグループを参照しているGTPのテーブル行にクラスを追加
    if (hoveredGroupId) {
      // そのグループを参照しているGTPを検索
      const relatedGtpPouches = pouches.filter(pouch => 
        pouch.isGroupTotalPouch && pouch.referencedGroupId === hoveredGroupId
      );
      
      // 各GTPのテーブル行にクラスを追加
      relatedGtpPouches.forEach(gtpPouch => {
        const targetPouchRows = document.querySelectorAll(`tr[data-pouch-id="${gtpPouch.id}"]`);
        targetPouchRows.forEach(element => {
          element.classList.add('pouch-related-group-hovered');
        });
      });
    }
  }, [hoveredGroupId, pouches]);

  // グローバルな修飾キー状態を追跡するためのuseEffect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        (window as any).__ctrlKeyPressed = true;
      } else if (e.key === 'Alt') {
        (window as any).__altKeyPressed = true;
      } else if (e.key === 'Shift') {
        (window as any).__shiftKeyPressed = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        (window as any).__ctrlKeyPressed = false;
      } else if (e.key === 'Alt') {
        (window as any).__altKeyPressed = false;
      } else if (e.key === 'Shift') {
        (window as any).__shiftKeyPressed = false;
      }
    };

    // 初期化
    (window as any).__ctrlKeyPressed = false;
    (window as any).__altKeyPressed = false;
    (window as any).__shiftKeyPressed = false;

    // イベントリスナーを追加
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    // クリーンアップ
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  /** スクロールで表示端に近づいたとき、計算エリアを一時的に広げる（ポインタを離したら余白のみ戻す） */
  const scrollEdgeBufferRef = useRef(0);

  // 計算エリアのサイズを更新する関数
  const updateCalcAreaSize = useCallback((extraBounds?: Rect) => {
    // 内容の境界を計算
    let contentRight = 0;
    let contentBottom = 0;
    
    // パウチの境界をチェック
    pouchesRef.current.forEach(pouch => {
      if (pouch._isDeleting) return;
      
      const pouchSize = 80; // パウチの基本サイズ
      const pouchRight = pouch.x + pouchSize;
      const pouchBottom = pouch.y + pouchSize;
      
      contentRight = Math.max(contentRight, pouchRight);
      contentBottom = Math.max(contentBottom, pouchBottom);
    });
    
    // グループの境界をチェック
    groupsRef.current.forEach(group => {
      if (!group.path) return;
      
      // グループの境界を取得
      const bounds = getGroupPathBounds(group);
      if (!bounds) return;
      
      contentRight = Math.max(contentRight, bounds.right);
      contentBottom = Math.max(contentBottom, bounds.bottom);
    });

    if (extraBounds) {
      contentRight = Math.max(contentRight, extraBounds.right);
      contentBottom = Math.max(contentBottom, extraBounds.bottom);
    }
    
    const buf = scrollEdgeBufferRef.current;
    const shell = document.querySelector('.calculator-area-shell') as HTMLElement | null;
    const area = document.querySelector('.calculator-area') as HTMLElement | null;
    const marginLeftPx = area
      ? parseFloat(getComputedStyle(area).marginLeft) || 0
      : (shell ? parseFloat(getComputedStyle(shell).paddingLeft) || 0 : 0);
    const minAreaWidthPx = Math.max(0, window.innerWidth - marginLeftPx);
    // コンテンツ実寸は --calcarea-width/height、一時余白は --calcarea-edge-buffer（max 内で 30vw/vh に加算）
    document.documentElement.style.setProperty('--calcarea-min-width', `${minAreaWidthPx}px`);
    document.documentElement.style.setProperty('--calcarea-width', `${contentRight}px`);
    document.documentElement.style.setProperty('--calcarea-height', `${contentBottom}px`);
    document.documentElement.style.setProperty('--calcarea-edge-buffer', `${buf}px`);
  }, []);

  const bumpScrollEdgeBufferIfNearViewportEdge = useCallback(() => {
    const el = getDocumentScrollElement();
    const sl = el.scrollLeft;
    const st = el.scrollTop;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    const sw = el.scrollWidth;
    const sh = el.scrollHeight;
    const NEAR = 160;
    const MAX_BUF = 8000;
    // 画面に見えている範囲に合わせて一気に広げる（端ギリギリで足りない問題の緩和）
    const baseChunk = Math.max(280, Math.round(Math.min(cw, ch) * 0.5));

    let b = scrollEdgeBufferRef.current;
    let add = 0;
    if (sl + cw >= sw - NEAR) {
      add = Math.max(add, baseChunk);
    }
    if (st + ch >= sh - NEAR) {
      add = Math.max(add, baseChunk);
    }
    if (add > 0) {
      b = Math.min(MAX_BUF, b + add);
    }
    if (b !== scrollEdgeBufferRef.current) {
      scrollEdgeBufferRef.current = b;
      updateCalcAreaSize();
    }
  }, [updateCalcAreaSize]);

  const resetScrollEdgeBufferWithTransition = useCallback(() => {
    void animateScrollEdgeBufferToZero({
      getBuffer: () => scrollEdgeBufferRef.current,
      setBuffer: (value) => {
        scrollEdgeBufferRef.current = value;
      },
      updateCalcAreaSize,
    });
  }, [updateCalcAreaSize]);

  useEffect(() => {
    const onScroll = () => bumpScrollEdgeBufferIfNearViewportEdge();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [bumpScrollEdgeBufferIfNearViewportEdge]);
  
  
  
  // 最新のデータを常に参照できるようにrefで状態を保存
  const pouchesRef = useRef<PouchWithDrag[]>(initialPouches || []);
  const groupsRef = useRef<Group[]>(initialGroups || []);
  
  // 状態が更新されたらrefも更新する
  useEffect(() => {
    pouchesRef.current = pouches;
  }, [pouches]);
  
  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);
  
  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (groupHoverTimerRef.current) {
        clearTimeout(groupHoverTimerRef.current);
      }
      if (pouchHoverTimerRef.current) {
        clearTimeout(pouchHoverTimerRef.current);
      }
      if (gtpGroupHoverTimerRef.current) {
        clearTimeout(gtpGroupHoverTimerRef.current);
      }
    };
  }, []);
  
  // App.tsxからの状態更新を反映するための追加useEffect
  useEffect(() => {  // シート間移動完了直後はパウチのリセットをスキップ
    // 親から新しいpouchesが渡されたときは常に内部状態を更新
    // リロード時やシート切り替え時にパウチをリセット
    
    const resetPouches = (initialPouches || []).map(pouch => {
      const resetPouch = {
        ...pouch,
        _animateMultiplier: false,
      };
      
      // _animateSignChangeフラグがある場合は保持
      if ((pouch as any)._animateSignChange) {
        (resetPouch as any)._animateSignChange = true;
      }
      
      return resetPouch;
    });
    
    setPouches(resetPouches);
    
    // 直接DOMからvalue-errorクラスを削除
    const errorRows = document.querySelectorAll('tr.value-error');
    errorRows.forEach(row => {
      row.classList.remove('value-error');
    });
    if (errorRows.length > 0) {
    }
    
    unlockUpdates();
  }, [initialPouches, activeSheetId]);
  
  useEffect(() => {
    // 親から新しいgroupsが渡されたときは常に内部状態を更新
    
    const resetGroups = initialGroups || [];
    
    
    setGroups(resetGroups);
    unlockUpdates();
  }, [initialGroups, activeSheetId]);
  
  // シート切替状態の管理
  const prevActiveSheetIdRef = useRef<string | null>(null);
  const isSheetSwitchingRef = useRef<boolean>(false);
  
  // グループの描画状態管理
  const [isDrawingGroup, setIsDrawingGroup] = useState(false);
  const [groupStartPoint, setGroupStartPoint] = useState<Point | null>(null);
  const [currentGroupPath, setCurrentGroupPath] = useState<string>('');
  const [maxGroupZIndex, setMaxGroupZIndex] = useState(100001);
  const [pendingGroup, setPendingGroup] = useState<string>(''); // 描画中のグループパス
  
  // 保留中のパウチ状態
  const [pendingPouch, setPendingPouch] = useState<PendingPouch | null>(null);
  
  // グローバルなタイマーIDを保持するref
  const pendingTimerRef = useRef<number | null>(null);
  
  // 計算エリアのref
  const areaRef = useRef<HTMLDivElement>(null);
  
  // Z-indexの管理
  const [maxPouchZIndex, setMaxPouchZIndex] = useState(
    initialPouches && initialPouches.length > 0
      ? Math.max(...initialPouches.map(p => p.zIndex || MIN_POUCH_Z_INDEX), MIN_POUCH_Z_INDEX)
      : MIN_POUCH_Z_INDEX
  );
  
  // 操作状態の管理
  const interactionRef = useRef<InteractionState>({
    type: null,
    targetId: null,
    targetType: null,
    startPosition: { x: 0, y: 0 },
    currentPosition: { x: 0, y: 0 },
    startTime: 0,
    lastClickTime: 0,
    isLongPressDetected: false,
    longPressTimer: null,
    isTouchEvent: false,
    dragOffset: { x: 0, y: 0 }, // ドラッグ時のオフセット
    isDrawingGroup: false, // グループ描画中フラグ
    groupStartPoint: null as Point | null, // グループ描画開始点
    currentGroupPath: '', // 現在描画中のグループパス
    isShiftPressed: false, // Shiftキーの状態
    groupPouches: [], // グループドラッグ開始時の所属パウチ
    containedGroups: [], // 完全に内包されているグループ
  });
  
  // シート間ドラッグ完了後の保護期間
  const crossSheetDragProtectionRef = useRef<number | null>(null);
  // パウチの一時的な位置を保持するref
  const tempPouchPositionRef = useRef<{ id: string; x: number; y: number } | null>(null);
  /** 2本指パンの直前フレームの中点（client 座標） */
  const twoFingerMidRef = useRef<{ x: number; y: number } | null>(null);

  // 一時的なパウチ位置を取得する関数
  const getTempPouchPosition = useCallback(() => {
    return tempPouchPositionRef.current ? {
      x: tempPouchPositionRef.current.x,
      y: tempPouchPositionRef.current.y
    } : null;
  }, []);

  // ちぎる（shift+ドラッグ）のロジックを関数化
  const performTearOperation = useCallback((sourcePouch: Pouch, clonedPouch: Pouch) => {
    const originalMultiplier = sourcePouch.multiplier;
    const originalSign = originalMultiplier < 0;
    
    // 1. 元の答えを出す
    const originalResult = originalMultiplier >= 0 ? 
      sourcePouch.value * originalMultiplier : 
      sourcePouch.value / Math.abs(originalMultiplier);
    
    // 2. *1固定する側を決定する。(元の答え-元の値)が元の値未満だと、元を固定、そうでなければ先を固定。
    const diffResult = originalResult - sourcePouch.value;
    
    if (diffResult === 0) {
      // エラー: diffResult === 0 の場合
      return { success: false, error: 'diffResult is zero' };
    }
    
    const isClonedFixed = Math.abs(diffResult) >= Math.abs(sourcePouch.value);
    
    // 固定側と非固定側を決定
    const fixedPouch = isClonedFixed ? clonedPouch : sourcePouch;
    const nonFixedPouch = isClonedFixed ? sourcePouch : clonedPouch;
    
    // 3. 固定側の値を元の値、マルチを*1とする。
    fixedPouch.multiplier = 1;
    
    // 4. 非固定側の答えを元の答え-固定側の答えとする。
    const fixedResult = fixedPouch.value * fixedPouch.multiplier;
    const nonFixedResult = originalResult - fixedResult;
    
    // 5. 非固定側の純マルチを、非固定側の答え/非固定側の値で計算する。
    const pureMultiplier = nonFixedResult / nonFixedPouch.value;
    
    // 6. 元のマルチの符号が/なら、非固定側のマルチの符号を"/"にして、マルチ値を純マルチの逆数にする。
    if (originalSign) {
      // 元が割り算の場合、常に符号を"/"にして、マルチ値を純マルチの逆数にする
      if (pureMultiplier < 0) {
        // マルチに負数を入れられないので、符号を"/"にして、マルチ値を正にして、値を正負転換
        nonFixedPouch.multiplier = -Math.abs(1 / pureMultiplier);
        nonFixedPouch.value = -nonFixedPouch.value;
      } else {
        // 符号を"/"にして、マルチ値を純マルチの逆数にする
        nonFixedPouch.multiplier = -Math.abs(1 / pureMultiplier);
      }
    } else {
      // 元が掛け算の場合、正の値で設定
      if (pureMultiplier < 0) {
        // マルチに負数を入れられないので、符号を"/"にして、マルチ値を正にして、値を正負転換
        nonFixedPouch.multiplier = -Math.abs(1 / pureMultiplier);
        nonFixedPouch.value = -nonFixedPouch.value;
      } else {
        nonFixedPouch.multiplier = Math.abs(pureMultiplier);
      }
    }
    
    return { success: true, sourcePouch, clonedPouch };
  }, []);

  // はんぶんこ（shift+alt+ドラッグ）のロジックを関数化
  const performSplitOperation = useCallback((sourcePouch: Pouch, clonedPouch: Pouch, originalValue?: number) => {
    const originalMultiplier = sourcePouch.multiplier;
    const originalSign = originalMultiplier < 0;
    
    // 純マルチに変換（割り算の場合は逆数にする）
    const pureMultiplier = originalSign ? 
      1 / Math.abs(originalMultiplier) : 
      originalMultiplier;
    
    // 純マルチ値が4未満の場合はエラー（複製側が≤1になるため）
    if (pureMultiplier < 4) {
      return { success: false, error: 'pure multiplier too small' };
    }
    
    // 複製側を整数で小さい方に設定
    const clonedPureMultiplier = Math.floor(pureMultiplier / 2);
    const sourcePureMultiplier = pureMultiplier - clonedPureMultiplier;
    
    // 分割後のマルチ値の小数桁数が5桁以上かチェック
    const clonedDecimalPlaces = (clonedPureMultiplier.toString().split('.')[1] || '').length;
    const sourceDecimalPlaces = (sourcePureMultiplier.toString().split('.')[1] || '').length;
    if (clonedDecimalPlaces >= 5 || sourceDecimalPlaces >= 5) {
      // エラー: 小数桁数が5桁以上
      return { success: false, error: 'decimal places too many' };
    }
    
    // 元の符号に合わせて再変換
    const clonedMultiplier = originalSign ? 
      -Math.abs(1 / clonedPureMultiplier) : 
      clonedPureMultiplier;
    const sourceMultiplier = originalSign ? 
      -Math.abs(1 / sourcePureMultiplier) : 
      sourcePureMultiplier;
    
    // 値も元の値に戻す（originalValueが指定されている場合）
    const sourceValue = originalValue !== undefined ? originalValue : sourcePouch.value;
    const clonedValue = originalValue !== undefined ? originalValue : clonedPouch.value;
    
    // 複製側（小さい方）と元側（残り）に設定
    clonedPouch.multiplier = clonedMultiplier;
    clonedPouch.value = clonedValue;
    sourcePouch.multiplier = sourceMultiplier;
    sourcePouch.value = sourceValue;
    
    return { success: true, sourcePouch, clonedPouch };
  }, []);

  // 値ちぎる（×1専用）のロジックを関数化
  // ドラッグ側が1（負なら-1）、元側がvalue-1（負ならvalue+1）
  const performValueTearOperation = useCallback((sourcePouch: Pouch, clonedPouch: Pouch) => {
    const value = sourcePouch.value;
    
    // |value| < 2 の場合はエラー
    if (Math.abs(value) < 2) {
      return { success: false, error: 'value too small for tear' };
    }
    
    // ドラッグ側（clonedPouch）が1（負なら-1）
    const clonedValue = value > 0 ? 1 : -1;
    // 元側（sourcePouch）が残り
    const sourceValue = value - clonedValue;
    
    clonedPouch.value = clonedValue;
    sourcePouch.value = sourceValue;
    // マルチは×1のまま
    clonedPouch.multiplier = 1;
    sourcePouch.multiplier = 1;
    
    return { success: true, sourcePouch, clonedPouch };
  }, []);

  // 値はんぶんこ（×1専用）のロジックを関数化
  // ドラッグ側が少ない方（小数第2位刻みで切り捨て）、元側が残り
  const performValueSplitOperation = useCallback((sourcePouch: Pouch, clonedPouch: Pouch) => {
    const value = sourcePouch.value;
    const minSplit = 2 * POUCH_VALUE_STEP;

    if (Math.abs(value) < minSplit) {
      return { success: false, error: 'value too small for split' };
    }

    const absValue = Math.abs(value);
    const sign = value > 0 ? 1 : -1;
    const scale = 10 ** POUCH_VALUE_DECIMAL_PLACES;

    const clonedAbsValue = Math.floor((absValue * scale) / 2) / scale;
    const sourceAbsValue = roundPouchValue(absValue - clonedAbsValue);

    clonedPouch.value = roundPouchValue(clonedAbsValue * sign);
    sourcePouch.value = roundPouchValue(sourceAbsValue * sign);
    // マルチは×1のまま
    clonedPouch.multiplier = 1;
    sourcePouch.multiplier = 1;
    
    return { success: true, sourcePouch, clonedPouch };
  }, []);

  /** ちぎり・はんぶんこ等の結果を永続化（多層時は activeLayer の layerValues に反映） */
  const mergeClonePhasePouchScalars = useCallback(
    (existing: Pouch, result: Pouch): Pouch => {
      if (!currentSheet) {
        return {
          ...existing,
          value: result.value,
          multiplier: result.multiplier,
          isNegative: result.value < 0,
        };
      }
      return applyPouchScalarForSheet(existing, result.value, currentSheet, {
        multiplier: result.multiplier,
      });
    },
    [currentSheet]
  );
  
  // 編集確定フラグ用のref
  const justEditConfirmedRef = useRef(false);
  const editConfirmTimeoutRef = useRef<number | null>(null);
  
  // イベントリスナーの状態管理用ref
  const eventListenersRef = useRef<{
    mousemove: boolean;
    mouseup: boolean;
    touchmove: boolean;
    touchend: boolean;
  }>({
    mousemove: false,
    mouseup: false,
    touchmove: false,
    touchend: false
  });
  
  // 非アクティブ状態検知用のref
  const wasRecentlyInactiveRef = useRef(false);
  const inactiveTimeoutRef = useRef<number | null>(null);
  
  // データ更新中のロックフラグ
  const isUpdatingRef = useRef(false);
  
  // データ更新開始時にロックをかける関数
  const lockUpdates = useCallback(() => {
    isUpdatingRef.current = true;
  }, []);
  
  // データ更新完了時にロックを解除する関数
  const unlockUpdates = useCallback(() => {
    isUpdatingRef.current = false;
  }, []);

  /** 新規パウチ DOM マウント後に値編集を開く（PC キーボード新規・タッチ pending 確定など） */
  const openValueEditWhenPouchMounted = useCallback((pid: string, attempt = 0) => {
    const valueDisplay = document.querySelector(
      `[data-pouch-id="${pid}"] .value-display`
    ) as HTMLElement | null;
    if (valueDisplay) {
      keyboardNewPouchFirstValueEditPouchIdRef.current = pid;
      keyboardValueEditSeedRef.current = {
        pouchId: pid,
        selectAllOnFocus: true,
      };
      valueDisplay.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
      window.setTimeout(() => {
        unlockUpdates();
      }, 50);
      return;
    }
    if (attempt < 48) {
      valueEditMountRetryRafRef.current = window.requestAnimationFrame(() => {
        valueEditMountRetryRafRef.current = null;
        openValueEditWhenPouchMounted(pid, attempt + 1);
      });
    } else {
      unlockUpdates();
    }
  }, [unlockUpdates]);

  
  // 編集確定時のハンドラ
  const handleEditConfirm = useCallback(() => {
    justEditConfirmedRef.current = true;
    
    // 既存のタイマーをクリア
    if (editConfirmTimeoutRef.current !== null) {
      window.clearTimeout(editConfirmTimeoutRef.current);
    }
    
    // 300ms後にフラグをリセット
    editConfirmTimeoutRef.current = window.setTimeout(() => {
      justEditConfirmedRef.current = false;
      editConfirmTimeoutRef.current = null;
    }, 300);
  }, []);

  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (editConfirmTimeoutRef.current !== null) {
        window.clearTimeout(editConfirmTimeoutRef.current);
      }
    };
  }, []);
  
  // 操作状態をリセットする関数
  const resetInteraction = useCallback(() => {
    document.body.classList.remove('is-element-dragging', 'is-calculator-touching');
    if (interactionRef.current.longPressTimer) {
      window.clearTimeout(interactionRef.current.longPressTimer);
    }
    
    // 全パウチの_isDragging、_isCloneSource、_isCloningをリセット
    const resetPouches = pouchesRef.current.map(p => ({
      ...p,
      _isDragging: false,
      _isCloneSource: false,
      _isCloning: false
    }));
    setPouches(resetPouches);
    pouchesRef.current = resetPouches;

    // 全グループの_isDragging、_isCloningをリセット
    const resetGroups = groupsRef.current.map(g => ({
      ...g,
      _isDragging: false,
      _isCloning: false
    }));
    setGroups(resetGroups);
    groupsRef.current = resetGroups;
    
    twoFingerMidRef.current = null;
    interactionRef.current = {
      ...interactionRef.current,
      type: null,
      targetId: null,
      targetType: null,
      isLongPressDetected: false,
      longPressTimer: null,
      isTouchEvent: false,
      dragOffset: { x: 0, y: 0 },
      startTime: 0,
      isDrawingGroup: false,
      groupStartPoint: null,
      currentGroupPath: '',
      isCloning: false,
      isShiftPressed: false,
      groupPouches: [],
      containedGroups: [],
      draggedFromOtherSheet: false,
      clonedGroup: undefined,
      originalPouchId: undefined,
      disablePhaseChange: false,
      modifierKeysOnHold: undefined,
      isTwoFingerPan: false,
      // lastClickTime はダブルクリック判定に必要なので明示的に保持
      lastClickTime: interactionRef.current.lastClickTime
    };



    // 描画中のグループをクリア
    setIsDrawingGroup(false);
    setGroupStartPoint(null);
    setCurrentGroupPath('');
    setPendingGroup('');

    resetScrollEdgeBufferWithTransition();
  }, [resetScrollEdgeBufferWithTransition]);
  
  // ダブルクリック/タップ判定のしきい値
  const DOUBLE_CLICK_THRESHOLD = 300; // ミリ秒
  
  // 長押し判定のしきい値
  const LONG_PRESS_THRESHOLD = 800; // ミリ秒
  
  // クリック判定の移動距離しきい値（PC用）
  const CLICK_DISTANCE_THRESHOLD = 5; // ピクセル
  
  // タップ判定の移動距離しきい値（モバイル用）
  const TAP_DISTANCE_THRESHOLD = 30; // ピクセル
  
  // pendingPouchの表示時間
  const PENDING_POUCH_DURATION = 500; // ミリ秒
  
  // 距離計算関数
  const calculateDistance = (p1: { x: number, y: number }, p2: { x: number, y: number }): number => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  // パウチのマージ処理を行う関数
  const processPouchMerge = useCallback((targetPouch: Pouch, allPouches: Pouch[]): { pouches: Pouch[], isMerged: boolean } => {
    let updatedPouches = [...allPouches];
    let isMerged = false;

    const nearbyPouches = updatedPouches.filter(
      p =>
        p.id !== targetPouch.id &&
        Math.abs(p.x - targetPouch.x) <= 30 &&
        Math.abs(p.y - targetPouch.y) <= 30
    );

    const mergeCandidate = nearbyPouches.find(p => {
      const distance = Math.sqrt(Math.pow(p.x - targetPouch.x, 2) + Math.pow(p.y - targetPouch.y, 2));
      return distance <= 60;
    });

    if (mergeCandidate) {
      const mergedPouch = mergeTwoPouchesOnCanvas(targetPouch, mergeCandidate, currentSheet, {
        maxZIndex: maxPouchZIndex,
        animateMultiplier: !document.documentElement.classList.contains('undoredo'),
      });
      updatedPouches = updatedPouches
        .filter(p => p.id !== targetPouch.id)
        .map(p => (p.id === mergeCandidate.id ? mergedPouch : p));
      isMerged = true;
    }

    return { pouches: updatedPouches, isMerged };
  }, [maxPouchZIndex, currentSheet]);
  
  
  // GTPの位置がグループの境界近くにあるかどうかを判定する関数
  const isGtpNearGroupBoundary = useCallback((pouch: Pouch, group: Group, threshold: number = 120): { nearRight: boolean; nearBottom: boolean } => {
    if (!pouch.isGroupTotalPouch) {
      return { nearRight: false, nearBottom: false };
    }
    
    // GTPの中心位置
    const gtpCenterX = pouch.x;
    const gtpCenterY = pouch.y;
    
    // グループの境界をパスから取得
    const groupBounds = getPathBounds(group.path);
    if (!groupBounds) {
      return { nearRight: false, nearBottom: false };
    }
    const groupRight = groupBounds.right;
    const groupBottom = groupBounds.bottom;
    
    // 右端から80px以内かチェック
    const nearRight = (groupRight - gtpCenterX) <= threshold;
    
    // 下端から80px以内かチェック
    const nearBottom = (groupBottom - gtpCenterY) <= threshold;
    
    return { nearRight, nearBottom };
  }, []);


  // パウチの位置を更新する関数
  const updatePouchPosition = useCallback((pouchId: string, x: number, y: number) => {
    // グリッドにスナップ（ドラッグ中にカクカク）
    const snappedX = snapToGrid(x);
    const snappedY = snapToGrid(y);
    
    // ドラッグ中の位置を一時的に保存
    tempPouchPositionRef.current = { id: pouchId, x: snappedX, y: snappedY };

    // 見た目の更新のみ行う（ステートは更新しない）
    // pouch-container要素を直接選択（wrapperではなく）
    const pouchElement = document.querySelector(`.pouch-container[data-pouch-id="${pouchId}"]`);
    if (pouchElement) {
      // パウチのサイズを取得（transform: translate(-50%, -50%)を考慮して、中心が端から半径分内側に入るように）
      const pouchWidth = (pouchElement as HTMLElement).offsetWidth;
      const pouchHeight = (pouchElement as HTMLElement).offsetHeight;
      const pouchRadius = Math.max(pouchWidth, pouchHeight) / 2;

      // エリアのサイズを取得
      const areaWidth = areaRef.current?.offsetWidth || 0;
      const areaHeight = areaRef.current?.offsetHeight || 0;

      // 画面の端からはみ出さないように位置を調整（スナップ済み座標を使用）
      // パウチは中心が(x,y)で、半径分内側に入るように制限
      const adjustedX = Math.max(pouchRadius, snappedX); // Math.min(x, areaWidth - pouchRadius)をコメントアウト
      const adjustedY = Math.max(pouchRadius, snappedY); // Math.min(y, areaHeight - pouchRadius)をコメントアウト

      // 位置を更新
      (pouchElement as HTMLElement).style.left = `${adjustedX}px`;
      (pouchElement as HTMLElement).style.top = `${adjustedY}px`;
      
      // 調整後の位置を保存
      tempPouchPositionRef.current = { id: pouchId, x: adjustedX, y: adjustedY };
      
    }
  }, []);
  
  // パウチ作成関数（seedBuffer: キーボード新規時の表示値チラつき抑止用）
  const createNewPouch = useCallback((x: number, y: number, seedBuffer?: string) => {
    const currentPouches = pouchesRef.current;
    const currentGroups = groupsRef.current;
    
    // グリッドにスナップ
    const snappedX = snapToGrid(x);
    const snappedY = snapToGrid(y);
    
    // 新しいパウチを作成（アニメーションフラグは後で設定）
    let newPouch = createPouch(snappedX, snappedY, lastPouchValue, lastPouchColor, maxPouchZIndex, currentSheet ?? {
      lastPouchValue,
      lastPouchColor,
      lastGroupColor: '',
      id: '',
      title: '',
      pouches: [],
      groups: [],
      createdAt: new Date(),
      history: [],
      historyIndex: 0,
      snapshots: []
    });
    if (seedBuffer !== undefined) {
      const iv = parseSeedBufferToInitialPouchValue(seedBuffer);
      if (iv !== undefined) {
        const exprClear = {
          lastExpression: '',
          valueWhenLastExpression: undefined,
        } as const;
        if (currentSheet?.layers?.length) {
          const lid = resolveActiveLayerId(currentSheet);
          newPouch = setPouchLayerValue(
            { ...newPouch, isNegative: iv < 0 },
            lid,
            iv,
            exprClear,
            currentSheet
          );
        } else {
          newPouch = {
            ...newPouch,
            value: iv,
            isNegative: iv < 0,
            ...exprClear,
          };
        }
      }
    }
    
    // 最終的な状態を計算（追加後の状態）
    const finalPouches = [...currentPouches, newPouch];
    
    // applyAnimationFlagsを使用してアニメーション用フラグを設定
    const animationResult = HistoryLogic.applyAnimationFlags(
      currentPouches,
      finalPouches,
      currentGroups,
      currentGroups
    );
    
    // 状態を更新してアニメーションを開始
    setPouches(animationResult.pouches);
    
    // 300ms後にアニメーション用フラグを除去して最終状態に更新
    setTimeout(() => {
      const cleanFinalPouches = finalPouches.map(p => {
        const cleanPouch = { ...p };
        const pouchAny = cleanPouch as any;
        delete pouchAny._isDeleting;
        delete pouchAny._animateFromZero;
        return cleanPouch;
      });
      
      // 統合APIで直接更新
      onUpdateActiveSheet(cleanFinalPouches, currentGroups, false, true, undefined, HistoryActionType.ADD_POUCH);
    }, 300);
    
    // maxPouchZIndexを更新
    setMaxPouchZIndex(prev => {
      const newZIndex = prev + 1;
      return newZIndex;
    });
    
    return newPouch;
  }, [lastPouchValue, lastPouchColor, maxPouchZIndex, setPouches, onUpdateActiveSheet, currentSheet]);

  const handleCalcAreaMouseMove = useCallback((e: React.MouseEvent) => {
    if (!areaRef.current) {
      return;
    }
    isMouseOverCalcAreaRef.current = true;
    lastPointerClientRef.current = { x: e.clientX, y: e.clientY };
    const rect = areaRef.current.getBoundingClientRect();
    lastMouseInAreaLocalRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const handleCalcAreaMouseEnter = useCallback((e: React.MouseEvent) => {
    isMouseOverCalcAreaRef.current = true;
    lastPointerClientRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleCalcAreaMouseLeave = useCallback(() => {
    flushPendingNewPouchKeyboardRef.current?.();
    isMouseOverCalcAreaRef.current = false;
    // バッファは flush 内（DOM 確定後）でクリア。ここで空にするとマウント待ち中に欠ける
  }, []);

  // PC・計算エリア空白: 数字/マイナスで即パウチ作成→次フレームで値編集、Enterは空パウチ or 保留中フラッシュ
  useEffect(() => {
    if (isCurrentTouchDevice) {
      return;
    }

    const cancelScheduledKeyboardFlush = () => {
      pendingFlushScheduleGenRef.current += 1;
    };

    const cancelValueEditMountRetry = () => {
      if (valueEditMountRetryRafRef.current !== null) {
        window.cancelAnimationFrame(valueEditMountRetryRafRef.current);
        valueEditMountRetryRafRef.current = null;
      }
    };

    /**
     * 数字キー新規: DOM 待ち中も pending を維持し、開けた瞬間のバッファでシードする。
     * （先に pending を消すと、待ち中の2文字目が別パウチ作成になる／体感ラグになる）
     */
    const openKeyboardSeededValueEditWhenMounted = (pid: string, attempt = 0) => {
      const valueDisplay = document.querySelector(
        `[data-pouch-id="${pid}"] .value-display`
      ) as HTMLElement | null;
      if (valueDisplay) {
        const buf = newPouchKeyBufferRef.current;
        keyboardValueEditSeedRef.current = {
          pouchId: pid,
          text: buf,
          caretAtEnd: buf.length > 0,
        };
        newPouchKeyBufferRef.current = '';
        pendingNewPouchIdRef.current = null;
        keyboardOpenExtraCharsRef.current = null;
        if (keyboardGraceClearTimeoutRef.current !== null) {
          window.clearTimeout(keyboardGraceClearTimeoutRef.current);
          keyboardGraceClearTimeoutRef.current = null;
        }
        keyboardOpenFocusGracePouchIdRef.current = pid;
        keyboardGraceClearTimeoutRef.current = window.setTimeout(() => {
          keyboardOpenFocusGracePouchIdRef.current = null;
          keyboardGraceClearTimeoutRef.current = null;
        }, 450);
        keyboardNewPouchFirstValueEditPouchIdRef.current = pid;
        valueDisplay.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
        window.setTimeout(() => {
          unlockUpdates();
        }, 50);
        return;
      }
      if (attempt < 48) {
        valueEditMountRetryRafRef.current = window.requestAnimationFrame(() => {
          valueEditMountRetryRafRef.current = null;
          openKeyboardSeededValueEditWhenMounted(pid, attempt + 1);
        });
      } else {
        unlockUpdates();
        pendingNewPouchIdRef.current = null;
        newPouchKeyBufferRef.current = '';
        handleDeletePouchForKeyboardRef.current(pid);
      }
    };

    const flushPendingNewPouchKeyboard = () => {
      cancelScheduledKeyboardFlush();
      cancelValueEditMountRetry();
      if (pendingNewPouchKeyboardTimerRef.current !== null) {
        window.clearTimeout(pendingNewPouchKeyboardTimerRef.current);
        pendingNewPouchKeyboardTimerRef.current = null;
      }
      const pid = pendingNewPouchIdRef.current;
      if (!pid) {
        return;
      }
      openKeyboardSeededValueEditWhenMounted(pid);
    };

    /** 連打キーを同一タスク直後の microtask まで合体してから1回だけ flush（rAF より早くキャレット表示） */
    const scheduleFlushPendingNewPouchKeyboard = () => {
      cancelScheduledKeyboardFlush();
      const gen = pendingFlushScheduleGenRef.current;
      queueMicrotask(() => {
        if (gen !== pendingFlushScheduleGenRef.current) return;
        flushPendingNewPouchKeyboard();
      });
    };

    flushPendingNewPouchKeyboardRef.current = flushPendingNewPouchKeyboard;

    /**
     * Enter で flush した直後は同じ keydown が入力に届かない。合成 Enter で確定させる。
     * autoFocus 済みなら focus() しないこと。二重 focus で onFocus が二度走り、
     * 1 回目で valueEditFocusCaretAtEndRef が消えたあと 2 回目が t.select() 全選択になる。
     */
    const chainEnterToValueInputAfterKeyboardFlush = (pouchId: string, attempt = 0) => {
      const input = document.querySelector(
        `[data-pouch-id="${pouchId}"] .value-container input`
      ) as HTMLInputElement | null;
      if (input) {
        const sendSyntheticEnter = () => {
          input.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: 'Enter',
              code: 'Enter',
              keyCode: 13,
              bubbles: true,
              cancelable: true,
            })
          );
        };
        if (document.activeElement !== input) {
          input.focus();
        }
        requestAnimationFrame(sendSyntheticEnter);
        return;
      }
      if (attempt < 48) {
        requestAnimationFrame(() => {
          chainEnterToValueInputAfterKeyboardFlush(pouchId, attempt + 1);
        });
      }
    };

    const cancelPendingNewPouchKeyboard = () => {
      cancelScheduledKeyboardFlush();
      cancelValueEditMountRetry();
      if (keyboardGraceClearTimeoutRef.current !== null) {
        window.clearTimeout(keyboardGraceClearTimeoutRef.current);
        keyboardGraceClearTimeoutRef.current = null;
      }
      keyboardOpenFocusGracePouchIdRef.current = null;
      keyboardOpenExtraCharsRef.current = null;
      if (pendingNewPouchKeyboardTimerRef.current !== null) {
        window.clearTimeout(pendingNewPouchKeyboardTimerRef.current);
        pendingNewPouchKeyboardTimerRef.current = null;
      }
      const pid = pendingNewPouchIdRef.current;
      if (!pid) {
        return;
      }
      pendingNewPouchIdRef.current = null;
      newPouchKeyBufferRef.current = '';
      handleDeletePouchForKeyboardRef.current(pid);
      unlockUpdates();
    };

    const handler = (e: KeyboardEvent) => {
      if (!isMouseOverCalcAreaRef.current) {
        return;
      }
      if (!areaRef.current) {
        return;
      }
      const { x: cx, y: cy } = lastPointerClientRef.current;
      if (isDrawingGroup) {
        return;
      }
      if (document.body.classList.contains('is-element-dragging')) {
        return;
      }
      if ((window as any).isIMEComposing) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }

      const layerCount = currentSheet?.layers?.length ?? 0;
      if (calcKeydownShouldYieldToLayerShortcut(e, layerCount)) {
        return;
      }

      const isEnterKey = e.key === 'Enter' || e.code === 'NumpadEnter';

      if (e.key === 'Escape' && e.shiftKey) {
        const hoverPid = hoveredPouchIdRef.current;
        if (hoverPid) {
          const p = pouchesRef.current.find(x => x.id === hoverPid);
          if (p && !p._isDragging && !p.isGroupTotalPouch) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            onToggleBalancingPouch?.(hoverPid);
          }
        }
        return;
      }

      if (e.key === 'Escape') {
        if (pendingNewPouchIdRef.current) {
          e.preventDefault();
          e.stopPropagation();
          cancelPendingNewPouchKeyboard();
          return;
        }
        if (newPouchKeyBufferRef.current) {
          e.preventDefault();
          e.stopPropagation();
          newPouchKeyBufferRef.current = '';
          return;
        }
        // ホバー中パウチ／グループ: Esc で正負反転 or 帳尻解除（編集系フォーカスは上で除外済み）
        const hoverPid = hoveredPouchIdRef.current;
        const hoverGid = hoveredGroupIdRef.current;
        if (hoverPid || hoverGid) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          keyboardEscToggleSignRef.current();
          return;
        }
        return;
      }

      if (e.key === 'Backspace') {
        if (pendingNewPouchIdRef.current) {
          e.preventDefault();
          e.stopPropagation();
          newPouchKeyBufferRef.current = newPouchKeyBufferRef.current.slice(0, -1);
          if (newPouchKeyBufferRef.current === '') {
            cancelPendingNewPouchKeyboard();
          } else {
            scheduleFlushPendingNewPouchKeyboard();
          }
          return;
        }
        if (newPouchKeyBufferRef.current) {
          e.preventDefault();
          e.stopPropagation();
          newPouchKeyBufferRef.current = newPouchKeyBufferRef.current.slice(0, -1);
        }
        return;
      }

      const formulaTriggerForEdit = getPouchFormulaEditTriggerChar(e);

      const digitChar = getDigitCharForEmptyCalcKeydown(e);
      const appendMinus = isMinusKeyForEmptyCalc(e);
      const appendColon = isColonKeyForEmptyCalc(e);
      let appendChar: string | null = null;
      if (digitChar) {
        appendChar = digitChar;
      } else if (appendMinus) {
        appendChar = '-';
      } else if (appendColon) {
        appendChar = ':';
      }

      if (
        formulaTriggerForEdit !== null &&
        !e.repeat &&
        !pendingNewPouchIdRef.current &&
        !newPouchKeyBufferRef.current
      ) {
        const hoverPid = getPouchIdForKeyboardValueEditShortcut(areaRef.current, cx, cy);
        if (hoverPid && currentSheet?.balancingPouchId !== hoverPid) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          keyboardValueEditSeedRef.current = {
            pouchId: hoverPid,
            text: formulaTriggerForEdit,
            caretAtEnd: true,
          };
          const valueDisplay = document.querySelector(
            `[data-pouch-id="${hoverPid}"] .value-display`
          ) as HTMLElement | null;
          if (valueDisplay) {
            valueDisplay.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
          }
          return;
        }
      }

      const keyboardContinuePouchId =
        pendingNewPouchIdRef.current ?? keyboardOpenFocusGracePouchIdRef.current;
      const eligible = isPointerEligibleForNewPouchKeyboardShortcuts(
        areaRef.current,
        cx,
        cy,
        keyboardContinuePouchId
      );
      if (!eligible) {
        return;
      }

      if (appendChar !== null) {
        const ch = appendChar;
        if (newPouchKeyBufferRef.current.length >= 32) {
          return;
        }
        const gracePid = keyboardOpenFocusGracePouchIdRef.current;
        if (gracePid && pendingNewPouchIdRef.current === null) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const input = document.querySelector(
            `[data-pouch-id="${gracePid}"] .value-container input`
          ) as HTMLInputElement | null;
          if (input && document.activeElement !== input) {
            input.focus();
            const desc = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value'
            );
            const setter = desc?.set;
            const next = (input.value ?? '') + ch;
            setter?.call(input, next);
            input.dispatchEvent(new Event('input', { bubbles: true }));
            if (keyboardGraceClearTimeoutRef.current !== null) {
              window.clearTimeout(keyboardGraceClearTimeoutRef.current);
              keyboardGraceClearTimeoutRef.current = null;
            }
            keyboardOpenFocusGracePouchIdRef.current = null;
            return;
          }
          const ex = keyboardOpenExtraCharsRef.current;
          if (ex && ex.pouchId === gracePid) {
            ex.chars += ch;
          } else {
            keyboardOpenExtraCharsRef.current = { pouchId: gracePid, chars: ch };
          }
          setKeyboardGraceAppendBump(b => b + 1);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const isFirstKeyOfKeyboardNewPouch = pendingNewPouchIdRef.current === null;
        if (isFirstKeyOfKeyboardNewPouch) {
          if (keyboardGraceClearTimeoutRef.current !== null) {
            window.clearTimeout(keyboardGraceClearTimeoutRef.current);
            keyboardGraceClearTimeoutRef.current = null;
          }
          keyboardOpenFocusGracePouchIdRef.current = null;
          keyboardOpenExtraCharsRef.current = null;
          lockUpdates();
          const { x, y } = lastMouseInAreaLocalRef.current;
          let newPouch!: Pouch;
          flushSync(() => {
            newPouch = createNewPouch(x, y, ch);
          });
          pendingNewPouchIdRef.current = newPouch.id;
          newPouchKeyBufferRef.current = ch;
        } else {
          newPouchKeyBufferRef.current += ch;
        }
        if (isFirstKeyOfKeyboardNewPouch) {
          flushPendingNewPouchKeyboard();
        } else {
          scheduleFlushPendingNewPouchKeyboard();
        }
        return;
      }

      if (e.key === 'F2' || e.key === ' ' || e.code === 'Space') {
        if (e.repeat) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (pendingNewPouchIdRef.current) {
          cancelPendingNewPouchKeyboard();
        }
        newPouchKeyBufferRef.current = '';
        onRequestFocusSheetTitle();
        return;
      }

      if (isEnterKey) {
        if (e.repeat) {
          return;
        }
        if (pendingNewPouchIdRef.current) {
          const pidForChain = pendingNewPouchIdRef.current;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          flushPendingNewPouchKeyboard();
          chainEnterToValueInputAfterKeyboardFlush(pidForChain);
          return;
        }
        const gracePid = keyboardOpenFocusGracePouchIdRef.current;
        if (gracePid) {
          const input = document.querySelector(
            `[data-pouch-id="${gracePid}"] .value-container input`
          ) as HTMLInputElement | null;
          if (input && document.activeElement !== input) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            if (document.activeElement !== input) {
              input.focus();
            }
            requestAnimationFrame(() => {
              input.dispatchEvent(
                new KeyboardEvent('keydown', {
                  key: 'Enter',
                  code: 'Enter',
                  keyCode: 13,
                  bubbles: true,
                  cancelable: true,
                })
              );
            });
            return;
          }
          if (!input) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return;
          }
        }
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        lockUpdates();
        const { x, y } = lastMouseInAreaLocalRef.current;
        let newPouch!: Pouch;
        flushSync(() => {
          newPouch = createNewPouch(x, y);
        });
        openValueEditWhenPouchMounted(newPouch.id);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => {
      window.removeEventListener('keydown', handler, true);
      pendingFlushScheduleGenRef.current += 1;
      if (valueEditMountRetryRafRef.current !== null) {
        window.cancelAnimationFrame(valueEditMountRetryRafRef.current);
        valueEditMountRetryRafRef.current = null;
      }
      flushPendingNewPouchKeyboardRef.current = null;
      // 保留パウチの削除・デバウンスタイマー解除はしない（開発時 Strict Mode で
      // effect が即クリーンアップ→再実行され、パウチ削除やタイマー失効で一瞬で消える／
      // フラッシュされないため）。リスナー解除のみ。
    };
  }, [
    isCurrentTouchDevice,
    isDrawingGroup,
    onRequestFocusSheetTitle,
    createNewPouch,
    lockUpdates,
    unlockUpdates,
    openValueEditWhenPouchMounted,
    setKeyboardGraceAppendBump,
    currentSheet?.balancingPouchId,
    onToggleBalancingPouch,
  ]);

  // PC・パウチホバー中: 矢印で値±・マルチ±（各ボタンと同じ Shift 刻み・長押し連打。e.repeat は無視）
  useEffect(() => {
    if (isCurrentTouchDevice) {
      return;
    }
    const ARROW_ACTION: Record<
      string,
      { type: 'value' | 'multiplier'; direction: 'increase' | 'decrease' }
    > = {
      ArrowUp: { type: 'value', direction: 'increase' },
      ArrowDown: { type: 'value', direction: 'decrease' },
      ArrowLeft: { type: 'multiplier', direction: 'decrease' },
      ArrowRight: { type: 'multiplier', direction: 'increase' },
    };
    const endAdjust = () => {
      const pid = pouchArrowAdjustActivePouchIdRef.current;
      if (!pid) {
        return;
      }
      pouchArrowAdjustActivePouchIdRef.current = null;
      pouchRefs.current.get(pid)?.current?.keyboardSimulatePressEnd();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!hoveredPouchIdRef.current) {
        return;
      }
      if (isDrawingGroup) {
        return;
      }
      if (document.body.classList.contains('is-element-dragging')) {
        return;
      }
      if ((window as any).isIMEComposing) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      if (document.activeElement?.closest('.table-component')) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      const action = ARROW_ACTION[e.key];
      if (!action) {
        return;
      }
      const pid = hoveredPouchIdRef.current;
      // 長押し連打は handlePressStart 内のタイマーで処理するため keydown の repeat は無視するが、
      // preventDefault しないと同一キーのリピートでページスクロールも発火してしまう（調整が弾かれた場合も同様）
      if (e.repeat) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }
      const pref = pouchRefs.current.get(pid);
      if (!pref?.current) {
        return;
      }
      const ok = pref.current.keyboardSimulatePressStart(action.type, action.direction, e.shiftKey);
      if (!ok) {
        // BP のマルチ矢印などで調整できない場合も、スクロールだけは抑止する
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      pouchArrowAdjustActivePouchIdRef.current = pid;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!ARROW_ACTION[e.key]) {
        return;
      }
      endAdjust();
    };
    const onWinBlur = () => {
      endAdjust();
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onWinBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onWinBlur);
      endAdjust();
    };
  }, [isCurrentTouchDevice, isDrawingGroup]);

  // pendingPouchを作成する関数
  const createPendingPouch = useCallback((x: number, y: number) => {
    // 既存のタイマーがあればクリア
    if (pendingTimerRef.current !== null) {
      window.clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    
    // 一旦pendingPouchをnullに設定してDOMから完全に削除
    setPendingPouch(null);
    
    // 少し遅延させて新しいpendingPouchを作成（DOMの更新を確実にするため）
    setTimeout(() => {
      // 新しいタイマーを設定
      const newTimerId = window.setTimeout(() => {
        setPendingPouch(null);
        pendingTimerRef.current = null;
      }, PENDING_POUCH_DURATION+500);
      
      // グローバルタイマーrefに保存
      pendingTimerRef.current = newTimerId;
      
      // 新しいpendingPouchを作成
      const newPendingPouch: PendingPouch = {
        id: uuidv4(), // 一意のIDを追加
        x,
        y,
        createdAt: Date.now(),
        timerId: newTimerId,
        color: POUCH_COLORS.find(c => c.id === lastPouchColor)?.value || POUCH_COLORS[0].value
      };
      
      setPendingPouch(newPendingPouch);
    }, 20); // 遅延を少し長めに設定（20ms）
  }, [lastPouchColor]); // 依存配列からpendingPouchを削除し、pendingTimerRefを使用
  
  // パウチのドラッグ開始時の処理
  const startPouchDrag = useCallback((e: React.MouseEvent | React.TouchEvent, pouchId: string) => {
    // 最新の状態からパウチを取得
    const pouch = pouchesRef.current.find(p => p.id === pouchId);
    if (!pouch) return;
    
    // タッチ位置を取得
    const clientX = ("touches" in e ? e.touches[0] : e).clientX;
    const clientY = ("touches" in e ? e.touches[0] : e).clientY;

    // ドラッグオフセットを計算（パウチの中心からの相対位置）
    if (areaRef.current) {
      const rect = areaRef.current.getBoundingClientRect();
      const offsetX = pouch.x - (clientX - rect.left);
      const offsetY = pouch.y - (clientY - rect.top);


      // オフセットを保存
      interactionRef.current.dragOffset = { x: offsetX, y: offsetY };
      
      // シート間ドラッグ開始を通知
      if (onStartSheetPouchDrag) {
        onStartSheetPouchDrag({
          pouch: pouch,
          finalPosition: { x: pouch.x, y: pouch.y },
          sourceSheetId: dragStartSheetIdRef.current || currentSheet?.id || activeSheetId
        });
      }
    }
  }, [activeSheetId, onStartSheetPouchDrag]);  // 依存配列にactiveSheetIdとonStartSheetPouchDragを追加

  // グループをリサイズする関数
  const resizeGroup = useCallback((groupId: string, startBounds: { left: number; top: number; right: number; bottom: number } | null, currentX: number, currentY: number, offsetX: number, offsetY: number) => {
    if (!startBounds) return;
      const groupToResize = groupsRef.current.find(g => g.id === groupId);
      if (!groupToResize) return groupsRef.current;
      
      // リサイズの角を取得
      const resizeCorner = interactionRef.current.resizeCorner || 'br';
      
      // マウスとコーナーとのオフセットを考慮して位置を調整（グリッドにスナップ）
      const adjustedX = snapToGrid(currentX - offsetX);
      const adjustedY = snapToGrid(currentY - offsetY);
      
      // 角に応じて新しい境界を計算
      let newLeft = startBounds.left;
      let newTop = startBounds.top;
      let newRight = startBounds.right;
      let newBottom = startBounds.bottom;
      
      const MIN_WIDTH = 100;
      const MIN_HEIGHT = 50;
      
      switch (resizeCorner) {
        case 'tl': // 左上
          newLeft = Math.min(adjustedX, startBounds.right - MIN_WIDTH);
          newTop = Math.min(adjustedY, startBounds.bottom - MIN_HEIGHT);
          break;
        case 'tr': // 右上
          newRight = Math.max(adjustedX, startBounds.left + MIN_WIDTH);
          newTop = Math.min(adjustedY, startBounds.bottom - MIN_HEIGHT);
          break;
        case 'bl': // 左下
          newLeft = Math.min(adjustedX, startBounds.right - MIN_WIDTH);
          newBottom = Math.max(adjustedY, startBounds.top + MIN_HEIGHT);
          break;
        case 'br': // 右下
        default:
          newRight = Math.max(adjustedX, startBounds.left + MIN_WIDTH);
          newBottom = Math.max(adjustedY, startBounds.top + MIN_HEIGHT);
          break;
      }
      
      const newPath = generateRoundedRectPath(
        newLeft,
        newTop,
        newRight,
        newBottom
      );
      
      const updatedGroups = groupsRef.current.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            path: newPath,
            zIndex: maxGroupZIndex + 1 // リサイズ中のグループを最前面に
          };
        }
        return group;
      });
      setGroups(updatedGroups);
      groupsRef.current = updatedGroups; // refも同期更新（タッチ終了時の上書き防止）
      
      // GTPの位置を直接調整（同期実行）
      const updatedPouches = [...pouchesRef.current];
      const referencingGtps = updatedPouches.filter(pouch => 
        pouch.isGroupTotalPouch && 
        pouch.referencedGroupId === groupId &&
        groupToResize.pouches.includes(pouch.id)
      );
      
      referencingGtps.forEach(gtp => {
        const savedBoundaryCheck = interactionRef.current.gtpBoundaryChecks?.[gtp.id] as { nearRight: boolean; nearBottom: boolean; nearLeft?: boolean; nearTop?: boolean } | undefined;
        if (!savedBoundaryCheck) return;
        
        const savedRelativePositions = interactionRef.current.gtpRelativePositions?.[gtp.id] as { relativeToRight?: number; relativeToBottom?: number; relativeToLeft?: number; relativeToTop?: number } | undefined;
        let newX = gtp.x;
        let newY = gtp.y;
        
        // 角に応じてGTPの位置を調整
        // 右端近くの場合（右側を動かす角のとき）
        if ((resizeCorner === 'br' || resizeCorner === 'tr') && savedBoundaryCheck.nearRight && savedRelativePositions?.relativeToRight !== undefined) {
          newX = newRight + savedRelativePositions.relativeToRight;
        }
        // 左端近くの場合（左側を動かす角のとき）
        if ((resizeCorner === 'bl' || resizeCorner === 'tl') && savedBoundaryCheck.nearLeft && savedRelativePositions?.relativeToLeft !== undefined) {
          newX = newLeft + savedRelativePositions.relativeToLeft;
        }
        // 下端近くの場合（下側を動かす角のとき）
        if ((resizeCorner === 'br' || resizeCorner === 'bl') && savedBoundaryCheck.nearBottom && savedRelativePositions?.relativeToBottom !== undefined) {
          newY = newBottom + savedRelativePositions.relativeToBottom;
        }
        // 上端近くの場合（上側を動かす角のとき）
        if ((resizeCorner === 'tr' || resizeCorner === 'tl') && savedBoundaryCheck.nearTop && savedRelativePositions?.relativeToTop !== undefined) {
          newY = newTop + savedRelativePositions.relativeToTop;
        }
        
        // 位置が変更された場合は更新
        if (newX !== gtp.x || newY !== gtp.y) {
          // パウチデータを直接更新
          const pouchIndex = updatedPouches.findIndex(p => p.id === gtp.id);
          if (pouchIndex !== -1) {
            updatedPouches[pouchIndex] = {
              ...updatedPouches[pouchIndex],
              x: newX,
              y: newY
            };
          }
          
          // DOMも即座に更新
          updatePouchPosition(gtp.id, newX, newY);
        }
      });
      
      // 更新されたパウチデータを保存
      pouchesRef.current = updatedPouches;
    
    // maxGroupZIndexを更新
    setMaxGroupZIndex(prev => prev + 1);
  }, [maxGroupZIndex, onUpdateActiveSheet]);

  // マウスダウンハンドラー（グループとリサイズハンドルのイベント処理を追加）
  const handleMouseDown = useCallback((e: React.MouseEvent, targetId?: string, targetType?: 'pouch' | 'group' | 'resize-handle' | null) => {
    // console.log('[DRAG_DEBUG] handleMouseDown開始:', {
    //   activeSheetId,
    //   targetId,
    //   targetType,
    //   currentInteractionType: interactionRef.current.type,
    //   eventListeners: { ...eventListenersRef.current }
    // });
    
    // ドラッグ開始時の元シートIDを記録
    dragStartSheetIdRef.current = currentSheet?.id || activeSheetId;
    
    // テーブル選択解除のためのカスタムイベントを発火
    const customEvent = new CustomEvent('tableSelectionClear');
    document.dispatchEvent(customEvent);
    
    // イベントリスナーを最初に登録（早期リターンに関係なく確実に登録）
    if (!eventListenersRef.current.mousemove) {
      document.addEventListener('mousemove', handleMouseMove);
      eventListenersRef.current.mousemove = true;
    }
    
    if (!eventListenersRef.current.mouseup) {
      document.addEventListener('mouseup', handleMouseUp);
      eventListenersRef.current.mouseup = true;
    }

    // // データ更新中は新しい操作を受け付けない
    // if (isUpdatingRef.current) {
    //   return;
    // }
    
    // タッチイベント後のマウスイベントは無視
    if (interactionRef.current.isTouchEvent) {
      return;
    }
    // 編集確定直後はパウチクリックのみ無視（計算エリア・グループ内はパウチ作成のため許可）
    if (justEditConfirmedRef.current && targetType === 'pouch') {
      return;
    }

    // ボタン要素のクリックかどうかを判定（長押し複製を防止するため）
    const targetElement = e.target as HTMLElement;
    const isControlButton = targetElement.closest('button, .group-buttons, .multiplier-button, .value-control-button, .pouch-color-button, .pouch-delete-button, .pouch-split-button, .pouch-toggle-sign-button, .pouch-balancing-button') !== null;
    const isSubtotalClick = targetElement.closest('.group-subtotal.clickable-subtotal') !== null;

    // サブトータルクリックの場合は処理しない（ただし、ドラッグ処理は開始する）
    if (isSubtotalClick) {
      // ドラッグ処理を開始するため、早期リターンしない
      // return;
    }

    // タッチした要素以外のすべてのフォーカス可能な要素からフォーカスを外す
    if (document.activeElement instanceof HTMLElement && e.target !== document.activeElement) {
      document.activeElement.blur();
    }

    // イベント伝播を止める
    e.stopPropagation();

    document.body.classList.add('is-calculator-touching');

    // 右クリックの場合
    if (e.button === 2) {
      e.preventDefault();
      interactionRef.current = {
        ...interactionRef.current,
        type: 'RIGHT_CLICK',
        targetId: targetId || null,
        targetType: targetType || null,
        startPosition: { x: e.clientX, y: e.clientY },
        currentPosition: { x: e.clientX, y: e.clientY },
        startTime: Date.now(),
        isTouchEvent: false,
        isShiftPressed: false,
        groupPouches: [], // グループパウチIDsをリセット
        containedGroups: [] // 内包グループIDsをリセット
      };
      
      return;
    }
    
    // 左クリックの場合は一旦記録
    interactionRef.current = {
      ...interactionRef.current,
      targetId: targetId || null,
      targetType: targetType || null,
      startPosition: { x: e.clientX, y: e.clientY },
      currentPosition: { x: e.clientX, y: e.clientY },
      startTime: Date.now(),
        isLongPressDetected: false,
        type: null, // 初期値はnullのまま
        // 修飾キーの状態を保存（長押しホールド時の段階変更で使用）
        modifierKeysOnHold: {
          ctrlOption: getCtrlOptionModifierKey(e as any),
          shift: e.shiftKey
        },
        longPressTimer: isControlButton ? null : window.setTimeout(() => {
        // 長押し判定
        const interaction = interactionRef.current;
        const distance = calculateDistance(
          interaction.startPosition,
          interaction.currentPosition
        );
        
        // 長押し中に大きく動いていない場合（PC用のしきい値を使用）
        if (distance <= CLICK_DISTANCE_THRESHOLD) {
          // フォーカスが入っている要素からフォーカスを外す
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          interaction.isLongPressDetected = true;
          interaction.type = 'LONG_CLICK';
          interaction.longPressStartTime = Date.now();
          
          // ここでパウチターゲットなら複製処理を行う
          if (interaction.targetType === 'pouch' && interaction.targetId && areaRef.current && !interaction.isCloning) {
            interaction.isCloning = true;
            // 修飾キーの状態は既にマウスダウン時に保存済み
            const rect = areaRef.current.getBoundingClientRect();
            const x = interaction.startPosition.x - rect.left;
            const y = interaction.startPosition.y - rect.top;
            // 複製元のパウチの元の位置を保存
            const sourceElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`) as HTMLElement;
            const originalX = sourceElement ? parseFloat(sourceElement.style.left) : x;
            const originalY = sourceElement ? parseFloat(sourceElement.style.top) : y;
            // 最新のパウチ配列から複製元のパウチを探す
            const currentPouches = pouchesRef.current;
            const sourcePouch = currentPouches.find(p => p.id === interaction.targetId);
            if (sourcePouch) {
              // 複製元のパウチIDを保存（複製処理前に保存）
              interaction.originalPouchId = interaction.targetId;
              
              // 元のマルチ値を保存
              interaction.originalMultiplier = sourcePouch.multiplier;
              
              // clonePouch関数を使用して複製（lastExpression関連を引き継がない）
              const clonedPouch = clonePouch(sourcePouch, originalX, originalY, maxPouchZIndex + 1);
              const newPouch: PouchWithDrag = {
                ...clonedPouch,
                _isDragging: true,
                _isCloneSource: false,
                _isCloning: true
              };
              // 複製元のパウチに複製元フラグを設定
              const updatedPouches = currentPouches.map(p => {
                if (p.id === interaction.targetId) {
                  return {
                    ...p,
                    _isCloneSource: true
                  } as PouchWithDrag;
                }
                return p;
              });
              updatedPouches.push(newPouch);
              setPouches(updatedPouches);
              pouchesRef.current = updatedPouches;
              interaction.targetId = newPouch.id;
              
              // エラー条件を先に判定して実行フェーズを決定
              const currentPouchesAfterClone = pouchesRef.current;
              const sourcePouchAfterClone = currentPouchesAfterClone.find(p => p.id === interaction.originalPouchId);
              const clonedPouchAfterClone = currentPouchesAfterClone.find(p => p.id === interaction.targetId);
              
              if (sourcePouchAfterClone && clonedPouchAfterClone) {
                // 複製パウチの要素にchanging-clone-modeクラスを追加（DOM更新後に実行）
                setTimeout(() => {
                  const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
                  if (clonedPouchElement) {
                    clonedPouchElement.classList.add('changing-clone-mode');
                  } else {
                    // もう一度試してみる（Reactの再レンダリングを待つ）
                    setTimeout(() => {
                      const retryElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
                      if (retryElement) {
                        retryElement.classList.add('changing-clone-mode');
                      }
                    }, 100);
                  }
                }, 0);
                
                // 元の値を保存
                const originalValue = sourcePouchAfterClone.value;
                
                // フェーズ1（ちぎる）のエラー条件をチェック
                const originalResult = sourcePouchAfterClone.multiplier >= 0 ? 
                  sourcePouchAfterClone.value * sourcePouchAfterClone.multiplier : 
                  sourcePouchAfterClone.value / Math.abs(sourcePouchAfterClone.multiplier);
                const diffResult = originalResult - sourcePouchAfterClone.value;
                const canTear = diffResult !== 0;
                
                // フェーズ2（はんぶんこ）のエラー条件をチェック
                const canSplit = canHalfSplitPouchByMultiplier(sourcePouchAfterClone, currentSheet);
                
                
                // 修飾キー+ドラッグの場合は段階マルチ変更を無効化
                if (interaction.disablePhaseChange) {
                  return;
                }
                
                // ×1かどうかを判定
                const equivalentPositiveValue = sourcePouchAfterClone.multiplier > 0 ? 
                  sourcePouchAfterClone.multiplier : 
                  1 / Math.abs(sourcePouchAfterClone.multiplier);
                const isMultiplierOne = equivalentPositiveValue === 1;
                
                // APパウチの場合はちぎる・はんぶんこをスキップ（複製のみ許可）
                const isBalancingPouchSource = currentSheet?.balancingPouchId === interaction.originalPouchId;

                // 修飾キー+長押しホールドの場合は1段階目で修飾キー相当のマルチ値に変更
                if (interaction.modifierKeysOnHold && (interaction.modifierKeysOnHold.shift || interaction.modifierKeysOnHold.ctrlOption)) {
                  const { ctrlOption, shift } = interaction.modifierKeysOnHold;
                  
                  // 修飾キーが押されている場合のみ処理を実行
                    // エラー条件をチェック
                    let hasError = false;
                    if (isBalancingPouchSource && (shift)) {
                      // APパウチでShiftが押されている場合はちぎる・はんぶんこ不可（複製に強制）
                      hasError = false;
                    } else if (shift && !ctrlOption) {
                      // Shiftキーの場合：×1で|value|<2の場合はエラー
                      if (isMultiplierOne) {
                        hasError = Math.abs(sourcePouchAfterClone.value) < 2;
                      } else {
                        hasError = false; // ×1以外は常にOK
                      }
                    } else if (shift && ctrlOption) {
                      // Shift+Ctrl/Optionキーの場合
                      if (isMultiplierOne) {
                        // ×1の場合：|value|<2の場合はエラー
                        hasError = Math.abs(sourcePouchAfterClone.value) < 2;
                      } else {
                        // ×1以外の場合：小数桁数が5桁以上の場合はエラー
                        const originalMultiplier = sourcePouchAfterClone.multiplier;
                        const originalSign = originalMultiplier < 0;
                        const pureMultiplier = originalSign ? 
                          1 / Math.abs(originalMultiplier) : 
                          originalMultiplier;
                        const halfPureMultiplier = pureMultiplier / 2;
                        const decimalPlaces = (halfPureMultiplier.toString().split('.')[1] || '').length;
                        hasError = decimalPlaces >= 5;
                      }
                    }
                    
                    if (hasError) {
                      // エラーの場合はブルブル表示
                      showPouchError(sourcePouchAfterClone.id);
                    } else {
                      // 1段階目で修飾キー相当のマルチ値に変更
                      const timerId = window.setTimeout(() => {
                        // ドラッグが開始されていたらフェーズ変更を停止
                        if (interactionRef.current.type === 'DRAG' || interactionRef.current.type === 'LONG_CLICK_DRAG') {
                          return;
                        }
                        
                        const currentPouches = pouchesRef.current;
                        const sourcePouch = currentPouches.find(p => p.id === interaction.originalPouchId);
                        const clonedPouch = currentPouches.find(p => p.id === interaction.targetId);
                        
                        if (!sourcePouch || !clonedPouch) return;
                        
                        let result: { success: boolean; sourcePouch?: Pouch; clonedPouch?: Pouch } | null = null;
                        
                        if (isBalancingPouchSource) {
                          // APパウチの場合：常に通常の複製
                          result = { success: true, sourcePouch, clonedPouch };
                        } else if (shift && !ctrlOption) {
                          // Shiftキーの場合
                          if (isMultiplierOne) {
                            // ×1の場合：値ちぎる
                            result = performValueTearOperation(sourcePouch, clonedPouch);
                          } else {
                            // ×1以外の場合：マルチ分割複製
                            result = performTearOperation(sourcePouch, clonedPouch);
                          }
                        } else if (shift && ctrlOption) {
                          // Shift+Ctrl/Optionキーの場合
                          if (isMultiplierOne) {
                            // ×1の場合：値はんぶんこ
                            result = performValueSplitOperation(sourcePouch, clonedPouch);
                          } else {
                            // ×1以外の場合：マルチ2分割複製
                            result = performSplitOperation(sourcePouch, clonedPouch, interaction.originalMultiplier);
                          }
                        } else if (ctrlOption && !shift) {
                          // Ctrl/Optionキーの場合：通常の複製（何もしない）
                          result = { success: true, sourcePouch, clonedPouch };
                        }
                        
                        if (result && result.success && result.sourcePouch && result.clonedPouch) {
                          const updatedPouches = currentPouches.map(p => {
                            if (p.id === result!.sourcePouch!.id) return result!.sourcePouch!;
                            if (p.id === result!.clonedPouch!.id) return result!.clonedPouch!;
                            return p;
                          });
                          setPouches(updatedPouches);
                          pouchesRef.current = updatedPouches;
                        }
                      }, 0); // 即座に実行
                      
                      // タイマーIDを配列に保存
                      const phaseTimers: number[] = [timerId];
                      interaction.phaseTimers = phaseTimers;
                  }
                  return; // 修飾キー+長押しの場合は通常の段階変更処理をスキップ
                } else {
                  // 修飾キーなしの場合は通常の段階変更処理を実行
                }
                
                // 実行するフェーズの配列を作成
                const phases: number[] = [];
                
                // ×1の場合と×1以外の場合でフェーズを分岐
                if (isBalancingPouchSource) {
                  // APパウチの場合：複製のみ
                  phases.push(3);
                } else if (isMultiplierOne) {
                  // ×1の場合: 複製→値はんぶんこ→値ちぎる
                  phases.push(3); // 純粋な複製（最初）
                  if (Math.abs(originalValue) >= 2) {
                    phases.push(4); // 値はんぶんこ
                    phases.push(5); // 値ちぎる
                  }
                } else {
                  // ×1以外の場合: ちぎる→はんぶんこ→複製（従来通り）
                  if (canTear) phases.push(1); // ちぎる
                  if (canSplit) phases.push(2); // はんぶんこ
                  phases.push(3); // 純粋な複製（常に実行）
                }
                
                // フェーズを順次実行
                const phaseTimers: number[] = [];
                phases.forEach((phase, index) => {
                  const delay = index * 1000; // 0秒、1秒、2秒...
                  const timerId = window.setTimeout(() => {
                    // ドラッグが開始されていたらフェーズ変更を停止
                    if (interactionRef.current.type === 'DRAG' || interactionRef.current.type === 'LONG_CLICK_DRAG') {
                      // ドラッグ開始時もクラスを削除
                      // const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
                      // if (clonedPouchElement) {
                      //   clonedPouchElement.classList.remove('changing-clone-mode');
                      // }
                      return;
                    }
                    
                    const currentPouches = pouchesRef.current;
                    const sourcePouch = currentPouches.find(p => p.id === interaction.originalPouchId);
                    const clonedPouch = currentPouches.find(p => p.id === interaction.targetId);
                    
                    if (!sourcePouch || !clonedPouch) return;
                    
                    let result: { success: boolean; sourcePouch?: Pouch; clonedPouch?: Pouch } | null = null;
                    
                    switch (phase) {
                      case 1: // マルチちぎる
                        result = performTearOperation({ ...sourcePouch }, { ...clonedPouch });
                        break;
                      case 2: // マルチはんぶんこ
                        // 元のマルチ値から計算するため、元のマルチ値でパウチを復元
                        const sourcePouchForSplit = { ...sourcePouch, multiplier: interaction.originalMultiplier!, value: originalValue };
                        const clonedPouchForSplit = { ...clonedPouch, multiplier: interaction.originalMultiplier!, value: originalValue };
                        result = performSplitOperation(sourcePouchForSplit, clonedPouchForSplit, originalValue);
                        break;
                      case 3: // 純粋な複製
                        // 元のマルチ値と元の値に戻す
                        result = { 
                          success: true, 
                          sourcePouch: { ...sourcePouch, multiplier: interaction.originalMultiplier!, value: originalValue }, 
                          clonedPouch: { ...clonedPouch, multiplier: interaction.originalMultiplier!, value: originalValue } 
                        };
                        break;
                      case 4: // 値はんぶんこ（×1専用）
                        const sourcePouchForValueSplit = { ...sourcePouch, multiplier: 1, value: originalValue };
                        const clonedPouchForValueSplit = { ...clonedPouch, multiplier: 1, value: originalValue };
                        result = performValueSplitOperation(sourcePouchForValueSplit, clonedPouchForValueSplit);
                        break;
                      case 5: // 値ちぎる（×1専用）
                        const sourcePouchForValueTear = { ...sourcePouch, multiplier: 1, value: originalValue };
                        const clonedPouchForValueTear = { ...clonedPouch, multiplier: 1, value: originalValue };
                        result = performValueTearOperation(sourcePouchForValueTear, clonedPouchForValueTear);
                        break;
                    }
                    
                    if (result && result.success && result.sourcePouch && result.clonedPouch) {
                      const phaseSource = result.sourcePouch;
                      const phaseCloned = result.clonedPouch;
                      const updatedPouches = currentPouches.map(p => {
                        if (p.id === interaction.originalPouchId) {
                          return mergeClonePhasePouchScalars(p, phaseSource);
                        }
                        if (p.id === interaction.targetId) {
                          return mergeClonePhasePouchScalars(p, phaseCloned);
                        }
                        return p;
                      });
                      setPouches(updatedPouches);
                      pouchesRef.current = updatedPouches;
                    }
                    
                    // 最後のフェーズ（純粋な複製）が完了したらクラスを削除
                    // if (phase === 3) {
                    //   const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
                    //   if (clonedPouchElement) {
                    //     clonedPouchElement.classList.remove('changing-clone-mode');
                    //   }
                    // }
                  }, delay);
                  
                  // タイマーIDを配列に保存
                  phaseTimers.push(timerId);
                });
                
                // タイマーID配列をinteractionに保存
                interaction.phaseTimers = phaseTimers;
              }              
            }
          }
          // グループの長押し時に即座に複製処理を実行
          else if (interaction.targetType === 'group' && interaction.targetId && areaRef.current) {
            
            
            // 複製フラグを立てる
            interaction.isCloning = true;
            
            // 最新のグループ情報とパウチ情報を取得
            const currentGroups = groupsRef.current;
            const currentPouches = pouchesRef.current;
            
            // 複製元グループを見つける
            const sourceGroup = currentGroups.find(g => g.id === interaction.targetId);
            
            if (sourceGroup) {
              // 1. グループの複製
              const clonedGroup = cloneGroup(sourceGroup, 0, 0, maxGroupZIndex + 1);
              
              // _isCloning属性を追加
              clonedGroup._isCloning = true;
              
              // Shiftキーが押されている場合は、グループ自体のみを複製（中のパウチや内包グループは複製しない）
              if (interaction.modifierKeysOnHold?.shift) {
                // 空のグループとして複製
                clonedGroup.pouches = [];
                
                // 複製したグループのみを追加し、複製元グループの_isDraggingフラグを即座に解除
                const originalGroupIds = [sourceGroup.id, ...interaction.containedGroups.map(cg => cg.id)];
                const updatedGroups = currentGroups.map(g => {
                  if (originalGroupIds.includes(g.id)) {
                    return { ...g, _isDragging: false };
                  }
                  return g;
                });
                updatedGroups.push({ ...clonedGroup, _isDragging: true }); // 複製されたグループにドラッグフラグを設定
                
                // 複製元グループの所属パウチの_isDraggingフラグも即座に解除
                const originalGroupPouchIds = sourceGroup.pouches || [];
                const updatedPouches = currentPouches.map(p => {
                  if (originalGroupPouchIds.includes(p.id)) {
                    return { ...p, _isDragging: false };
                  }
                  return p;
                });
                
                setPouches(updatedPouches);
                setGroups(updatedGroups);
                // 複製元グループの_isDragging解除をgroupsRefにも即座に反映
                groupsRef.current = updatedGroups;
                pouchesRef.current = updatedPouches;
                setMaxGroupZIndex(prev => prev + 1);
                
                // 複製したグループをドラッグ対象に設定
                interaction.targetId = clonedGroup.id;
                interaction.clonedGroup = clonedGroup;
                interaction.groupPouches = [];
                interaction.containedGroups = [];
                
                return;
              }
              
              // 2. 複製元グループに含まれるパウチを複製（shiftキーが押されていない場合のみ）
              const clonedPouches: Pouch[] = [];
              const pouchIdMap: {[oldId: string]: string} = {}; // 古いID → 新しいIDのマッピング
              
              if (sourceGroup.pouches && sourceGroup.pouches.length > 0) {
                sourceGroup.pouches.forEach(pouchId => {
                  const sourcePouch = currentPouches.find(p => p.id === pouchId);
                  if (sourcePouch) {
                    // パウチを複製（位置はそのまま）
                    const clonedPouch = clonePouch(sourcePouch, sourcePouch.x, sourcePouch.y, maxPouchZIndex + clonedPouches.length + 1);
                    clonedPouches.push(clonedPouch);
                    
                    // ID変換マップを更新
                    pouchIdMap[pouchId] = clonedPouch.id;
                  }
                });
                
                // 複製したグループのパウチIDリストを更新
                clonedGroup.pouches = sourceGroup.pouches.map(id => pouchIdMap[id] || id);
              }
              
              // 3. 内包グループも複製
              const clonedContainedGroups: Group[] = [];
              const groupIdMap: {[oldId: string]: string} = {}; // 古いグループID → 新しいグループIDのマッピング
              groupIdMap[sourceGroup.id] = clonedGroup.id; // 親グループのマッピングを追加
              
              interaction.containedGroups.forEach(({ id, path }) => {
                const containedGroup = currentGroups.find(g => g.id === id);
                if (containedGroup) {
                  // グループを複製
                  const clonedContainedGroup = cloneGroup(containedGroup, 0, 0, maxGroupZIndex + 2 + clonedContainedGroups.length);
                  clonedContainedGroups.push(clonedContainedGroup);
                  
                  // ID変換マップを更新
                  groupIdMap[containedGroup.id] = clonedContainedGroup.id;
                  
                  // 内包グループに所属するパウチも複製
                  if (containedGroup.pouches && containedGroup.pouches.length > 0) {
                    containedGroup.pouches.forEach(pouchId => {
                      // すでに複製済みでなければ複製
                      if (!pouchIdMap[pouchId]) {
                        const sourcePouch = currentPouches.find(p => p.id === pouchId);
                        if (sourcePouch) {
                          const clonedPouch = clonePouch(sourcePouch, sourcePouch.x, sourcePouch.y, maxPouchZIndex + clonedPouches.length + 1);
                          clonedPouches.push(clonedPouch);
                          pouchIdMap[pouchId] = clonedPouch.id;
                        }
                      }
                    });
                    
                    // 複製した内包グループのパウチIDリストを更新
                    clonedContainedGroup.pouches = containedGroup.pouches.map(id => pouchIdMap[id] || id);
                  }
                }
              });
              
              // 4. すべての複製パウチとグループを配列に追加し、複製されたグループにドラッグフラグを設定
              const updatedPouches = [...currentPouches, ...clonedPouches];
              const updatedGroupsWithDragFlags = [...currentGroups, 
                { ...clonedGroup, _isDragging: true }, // 複製されたメイングループにドラッグフラグを設定
                ...clonedContainedGroups.map(g => ({ ...g, _isDragging: true })) // 複製された内包グループにもドラッグフラグを設定
              ];
              const updatedGroups = updatedGroupsWithDragFlags;
              
              // 5. GTPの参照を新しいグループに付け替え
              const clonedPouchIds = clonedPouches.map(p => p.id);
              const pouchesWithUpdatedGTPReferences = updateGTPReferencesAfterClone(updatedPouches, groupIdMap, clonedPouchIds);
              
              // 6. 複製されたパウチにドラッグフラグを設定
              const finalPouches = pouchesWithUpdatedGTPReferences.map(pouch => {
                if (clonedPouches.some(cp => cp.id === pouch.id)) {
                  // 新しく複製されたパウチの場合
                  return {
                    ...pouch,
                    _isDragging: true
                  };
                }
                return pouch;
              });
              // 複製元グループとその内包グループの_isDraggingフラグを即座に解除
              const originalGroupIds = [sourceGroup.id, ...interaction.containedGroups.map(cg => cg.id)];
              const updatedGroupsWithSourceReset = updatedGroups.map(g => {
                if (originalGroupIds.includes(g.id)) {
                  return { ...g, _isDragging: false };
                }
                return g;
              });
              
              // 複製元グループの所属パウチの_isDraggingフラグも即座に解除
              const originalGroupPouchIds = sourceGroup.pouches || [];
              const finalPouchesWithSourceReset = finalPouches.map(p => {
                if (originalGroupPouchIds.includes(p.id)) {
                  return { ...p, _isDragging: false };
                }
                return p;
              });
              
              setPouches(finalPouchesWithSourceReset);
              setGroups(updatedGroupsWithSourceReset);
              // 複製元グループの_isDragging解除をgroupsRefにも即座に反映
              groupsRef.current = updatedGroupsWithSourceReset;
              // 6. 統合APIで更新（DB保存なし）
              // onUpdateActiveSheet(finalPouches, updatedGroups, true, false);
              
              // 7. maxPouchZIndexとmaxGroupZIndexを更新
              setMaxPouchZIndex(prev => prev + clonedPouches.length);
              setMaxGroupZIndex(prev => prev + 1 + clonedContainedGroups.length);
              
              // 8. 制御をクローンされたグループに移す
              interaction.targetId = clonedGroup.id;
              interaction.clonedGroup = clonedGroup;
              
              // 9. 内包グループと所属パウチの情報も更新
              const clonedGroupPouches = clonedPouches
                .filter(p => clonedGroup.pouches?.includes(p.id))
                .map(p => ({ id: p.id, x: p.x, y: p.y }));
              
              interaction.groupPouches = clonedGroupPouches;
              
              interaction.containedGroups = clonedContainedGroups.map(g => ({ 
                id: g.id, 
                path: g.path 
              }));
            }
          }
        }
      }, LONG_PRESS_THRESHOLD),
      isTouchEvent: false,
      dragOffset: { x: 0, y: 0 },
      isCloning: false, // 複製フラグを必ずリセット
      isShiftPressed: false,
      groupPouches: [], // グループパウチIDsをリセット
      containedGroups: [], // 内包グループIDsをリセット
      clonedGroup: undefined // 複製したグループの参照をリセット
    };

    // パウチのドラッグ開始処理
    if (targetType === 'pouch' && targetId) {
      // パウチドラッグの共通処理を使用
      startPouchDrag(e, targetId);
    }
    // グループのドラッグ開始処理
    else if (targetType === 'group' && targetId) {
      // グループを見つける（最新のグループ情報を使用）
      const group = groupsRef.current.find(g => g.id === targetId);
      if (group && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        
        // グループの現在の境界を取得
        const bounds = getPathBounds(group.path);
        if (bounds) {
          // クリック位置とグループ左上コーナーとのオフセットを計算
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;
          
          
          // オフセットを保存（グループ左上とマウス位置の差）
          interactionRef.current.dragOffset = {
            x: mouseX - bounds.left,
            y: mouseY - bounds.top
          };
          
          // このグループと内包子グループに属するパウチの初期位置を記録
          const subtreePouchIds = collectPouchIdsForGroupSubtree(
            targetId,
            groupsRef.current,
          );
          const groupPouches = pouchesRef.current
            .filter(p => subtreePouchIds.includes(p.id))
            .map(p => ({ id: p.id, x: p.x, y: p.y }));
          
          interactionRef.current.groupPouches = groupPouches;
          
          // このグループに完全に内包されているグループを検出
          const containedGroups = groupsRef.current
            .filter(g => g.id !== targetId && isGroupPhysicallyContained(g.id, targetId, groupsRef.current))
            .map(g => ({ id: g.id, path: g.path }));
          
          interactionRef.current.containedGroups = containedGroups;
        }
      }
    }
    // リサイズハンドルのドラッグ開始処理
    else if (targetType === 'resize-handle' && targetId) {
      // リサイズ対象のグループを見つける
      const group = groupsRef.current.find(g => g.id === targetId);
      if (group && areaRef.current) {
        // グループの現在の境界を保存
        const bounds = getPathBounds(group.path);
        if (bounds) {
          interactionRef.current.groupOriginalPath = group.path;
          interactionRef.current.groupInitialBounds = bounds;
          
          // リサイズの角を取得（data-resize-corner属性から）
          const resizeCorner = (e.target as HTMLElement).dataset.resizeCorner as 'br' | 'bl' | 'tr' | 'tl' || 'br';
          interactionRef.current.resizeCorner = resizeCorner;
          
          // クリック位置と対応するコーナーとのオフセットを計算
          const rect = areaRef.current.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;
          
          // 角に応じたコーナー座標を取得
          let cornerX: number, cornerY: number;
          switch (resizeCorner) {
            case 'tl': // 左上
              cornerX = bounds.left;
              cornerY = bounds.top;
              break;
            case 'tr': // 右上
              cornerX = bounds.right;
              cornerY = bounds.top;
              break;
            case 'bl': // 左下
              cornerX = bounds.left;
              cornerY = bounds.bottom;
              break;
            case 'br': // 右下
            default:
              cornerX = bounds.right;
              cornerY = bounds.bottom;
              break;
          }
          
          // オフセットを保存（ハンドル内のどの位置をクリックしたかを記録）
          interactionRef.current.dragOffset = {
            x: mouseX - cornerX,
            y: mouseY - cornerY
          };
          
          // GTPの境界判定と相対位置を保存
          const gtpBoundaryChecks: { [gtpId: string]: { nearRight: boolean; nearBottom: boolean; nearLeft: boolean; nearTop: boolean } } = {};
          const gtpRelativePositions: { [gtpId: string]: { relativeToRight?: number; relativeToBottom?: number; relativeToLeft?: number; relativeToTop?: number } } = {};
          const referencingGtps = pouchesRef.current.filter(pouch => 
            pouch.isGroupTotalPouch && pouch.referencedGroupId === targetId
          );
          
          referencingGtps.forEach(gtp => {
            const boundaryCheck = isGtpNearGroupBoundary(gtp, group);
            gtpBoundaryChecks[gtp.id] = { ...boundaryCheck, nearLeft: false, nearTop: false };
            
            // 左端・上端近くかどうかも判定（GTPの位置がグループの左端・上端から50px以内）
            const gtpCenterX = gtp.x + 50;
            const gtpCenterY = gtp.y + 25;
            if (gtpCenterX - bounds.left < 50) {
              gtpBoundaryChecks[gtp.id].nearLeft = true;
            }
            if (gtpCenterY - bounds.top < 50) {
              gtpBoundaryChecks[gtp.id].nearTop = true;
            }
            
            // 相対位置を保存
            const relativePositions: { relativeToRight?: number; relativeToBottom?: number; relativeToLeft?: number; relativeToTop?: number } = {};
            if (boundaryCheck.nearRight) {
              relativePositions.relativeToRight = gtp.x - bounds.right;
            }
            if (boundaryCheck.nearBottom) {
              relativePositions.relativeToBottom = gtp.y - bounds.bottom;
            }
            if (gtpBoundaryChecks[gtp.id].nearLeft) {
              relativePositions.relativeToLeft = gtp.x - bounds.left;
            }
            if (gtpBoundaryChecks[gtp.id].nearTop) {
              relativePositions.relativeToTop = gtp.y - bounds.top;
            }
            gtpRelativePositions[gtp.id] = relativePositions;
          });
          
          interactionRef.current.gtpBoundaryChecks = gtpBoundaryChecks;
          interactionRef.current.gtpRelativePositions = gtpRelativePositions;
        }
      }
    }
    
  }, [startPouchDrag, calculateDistance, groups, isGroupPhysicallyContained, isGtpNearGroupBoundary]);
  
  // マウス移動ハンドラー（マウスドラッグでのグループ描画処理）修正
  const handleMouseMove = useCallback((e: MouseEvent) => {
    // グローバルなドラッグ状態がある場合のみ、アクティブシートチェックを行う
    if (globalInteractionRef?.current) {
      // 現在のシートがアクティブでない場合は処理しない
      if (globalInteractionRef.current.activeSheetId !== activeSheetId) {
        // 非アクティブシートのイベントリスナーを削除
        if (eventListenersRef.current.mousemove) {
          document.removeEventListener('mousemove', handleMouseMove);
          eventListenersRef.current.mousemove = false;
        }
        if (eventListenersRef.current.mouseup) {
          document.removeEventListener('mouseup', handleMouseUp);
          eventListenersRef.current.mouseup = false;
        }
        return;
      }
    }
    
    // マウス操作が検出されたら一時的にタッチデバイスフラグをオフに
    if (isTouchDeviceRef.current) {
      isTouchDeviceRef.current = false;
      setIsCurrentTouchDevice(false);
    }
    
    const interaction = interactionRef.current;
    
    // マウスボタンの状態チェック - ドラッグ中でボタンが押されていない場合はドラッグをキャンセル
    if (e.buttons === 0 && interaction.startTime > 0) {
      handleMouseUp(e as unknown as MouseEvent);
      return;
    }
    // マウスダウンされていない場合は何もしない（startTimeが0または存在しない場合）
    if (!interaction.startTime || justEditConfirmedRef.current) {
      handleMouseUp(e as unknown as MouseEvent);
      return;
    }
    // タッチした要素以外のすべてのフォーカス可能な要素からフォーカスを外す
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    
    interaction.currentPosition = { x: e.clientX, y: e.clientY };

    if (
      interaction.startTime &&
      !justEditConfirmedRef.current &&
      shouldApplyDragEdgeAutoScroll(interaction)
    ) {
      applyDragEdgeAutoScroll(e.clientX, e.clientY, dragEdgeScrollLayoutRef.current, () => {
        requestAnimationFrame(() => {
          bumpScrollEdgeBufferIfNearViewportEdge();
        });
      });
    }
    
    // 開始位置からの距離を計算
    const distance = calculateDistance(interaction.startPosition, interaction.currentPosition);
    
    // ドラッグ判定のしきい値を超えた場合（PC用のしきい値を使用）
    if (distance > CLICK_DISTANCE_THRESHOLD && (interaction.type === null || interaction.type === 'LONG_CLICK')) {
      // ボタン要素またはその親要素にボタンが含まれるかを判定
      const targetElement = e.target as HTMLElement;
      const isButtonOrInButton = targetElement ? 
        targetElement.closest('button') !== null || 
        targetElement.closest('.group-buttons') !== null || 
        targetElement.closest('.pouch-color-button, .pouch-delete-button, .pouch-split-button, .pouch-toggle-sign-button') !== null ||
        targetElement.closest('.group-title-input') !== null : 
        false;
      
      // ターゲットの種類によって異なるドラッグタイプを設定
      if (interaction.targetType === 'group') {
        // ドラッグ開始時のグループ情報をログ
        const targetGroup = groupsRef.current.find(g => g.id === interaction.targetId);
        if (targetGroup) {
          const groupBounds = getPathBounds(targetGroup.path);
          const containedGroups = groupsRef.current.filter(g => 
            targetGroup.pouches && targetGroup.pouches.some(pouchId => 
              g.pouches && g.pouches.includes(pouchId)
            )
          );
          const containedPouches = pouchesRef.current.filter(p => targetGroup.pouches && targetGroup.pouches.includes(p.id));
          
        }
        // Alt/Controlキーが押されている場合はグループ作成モードに切り替え（ボタン以外の場合のみ）
        if (getAltControlModifierKey(e) && !isButtonOrInButton) {
          interaction.type = 'DRAG';
          interaction.targetType = null; // nullにして既存のグループ描画処理を使用
          interaction.targetId = null;   // targetIdもnullにする
        } else {
          interaction.type = 'GROUP_DRAG';
          interaction.isShiftPressed = e.shiftKey;
          
          // グループドラッグ開始時に_isDraggingフラグを設定
          if (interaction.targetId) {
            // 最新のグループ配列を取得
            const currentGroups = [...groupsRef.current];
            // DOM要素の見た目を更新
            const groupElement = document.querySelector(`[data-group-id="${interaction.targetId}"]`);
            if (groupElement) {
              (groupElement as HTMLElement).classList.add('group-dragging');
            }
            
            // _isDraggingフラグを設定（グループ、所属パウチ、内包グループ全て）
            const targetGroup = currentGroups.find(g => g.id === interaction.targetId);
            
            // まず全フラグをクリア（前回の残留フラグを確実に削除）
            const clearedGroups = currentGroups.map(g => ({
              ...g,
              _isDragging: false,
              _isCloning: false
            }));
            const clearedPouches = pouchesRef.current.map(p => ({
              ...p,
              _isDragging: false,
              _isCloneSource: false,
              _isCloning: false
            }));
            
            // 内包グループを検出（複製後の場合はinteraction.containedGroupsを使用）
            let containedGroupIds: string[];
            if (interaction.isCloning && interaction.containedGroups && interaction.containedGroups.length > 0) {
              // 複製後の場合は、既に記録されている内包グループIDを使用
              containedGroupIds = interaction.containedGroups.map(cg => cg.id);
            } else {
              // 通常のドラッグ開始の場合は物理的な検出を行う
              containedGroupIds = currentGroups
                .filter(g => g.id !== interaction.targetId && isGroupPhysicallyContained(g.id, interaction.targetId!, currentGroups))
                .map(g => g.id);
            }
            
            
            // クリア後、必要なフラグのみを設定
            const updatedGroups = clearedGroups.map(g => {
              if (g.id === interaction.targetId || containedGroupIds.includes(g.id)) {
                return {
                  ...g,
                  _isDragging: true
                };
              }
              return g;
            });
            
            // 所属パウチにも_isDraggingフラグを設定（Shiftキーが押されていない場合のみ）
            const updatedPouches = clearedPouches.map(p => {
              if (!interaction.isShiftPressed && targetGroup?.pouches?.includes(p.id)) {
                return {
                  ...p,
                  _isDragging: true
                };
              }
              return p;
            });
            
            
            setGroups(updatedGroups);
            setPouches(updatedPouches);
            // 配列を更新（ステート更新は行わない - 直接refを更新）
            groupsRef.current = updatedGroups;
            pouchesRef.current = updatedPouches;
          }
        }
        
      } 
      else if (interaction.targetType === 'resize-handle') {
        interaction.type = 'GROUP_RESIZE';
        
        // リサイズ中もグループタイトルのイベントを無効化するためクラスを追加
        const resizeGroupElement = document.querySelector(`[data-group-id="${interaction.targetId}"]`);
        if (resizeGroupElement) {
          (resizeGroupElement as HTMLElement).classList.add('group-dragging');
        }
      }
      else if (interaction.isLongPressDetected) {
        interaction.type = 'LONG_CLICK_DRAG';
        
        // 段階変更のタイマーをクリア
        if (interaction.phaseTimers) {
          interaction.phaseTimers.forEach(timerId => {
            clearTimeout(timerId);
          });
          interaction.phaseTimers = [];
        }
        
        // ドラッグ開始時にchanging-clone-modeクラスを削除
        if (interaction.targetId) {
          const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
          if (clonedPouchElement) {
            clonedPouchElement.classList.remove('changing-clone-mode');
          }
        }
      }
      else {
        // Alt/Controlキーが押されている場合はグループ作成モードに切り替え（ボタン以外の場合のみ）
        if (getAltControlModifierKey(e) && !isButtonOrInButton) {
          interaction.type = 'DRAG';
          interaction.targetType = null; // nullにして既存のグループ描画処理を使用
          interaction.targetId = null;   // targetIdもnullにする
        } else {
          interaction.type = 'DRAG';
          
          // 段階変更のタイマーをクリア
          if (interaction.phaseTimers) {
            interaction.phaseTimers.forEach(timerId => {
              clearTimeout(timerId);
            });
            interaction.phaseTimers = [];
          }
          
          // ドラッグ開始時にchanging-clone-modeクラスを削除
          if (interaction.targetId) {
            const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
            if (clonedPouchElement) {
              clonedPouchElement.classList.remove('changing-clone-mode');
            }
          }
          
          // パウチドラッグ開始時に_isDraggingフラグを設定
          if (interaction.targetType === 'pouch' && interaction.targetId) {
            
            // ドラッグ判定時にパウチの値を元に戻す
            const pouchRef = pouchRefs.current.get(interaction.targetId);
            if (pouchRef?.current?.revertToOriginalValues) {
              pouchRef.current.revertToOriginalValues();
            }
            
            // 最新のパウチ配列を取得
            const currentPouches = [...pouchesRef.current];
            
            // _isDraggingフラグを設定
            const updatedPouches = currentPouches.map(p => {
              if (p.id === interaction.targetId) {
                return {
                  ...p,
                  _isDragging: true
                };
              }
              return p;
            });
            setPouches(updatedPouches);
            // パウチ配列を更新（ステート更新は行わない - 直接refを更新）
          }
          
        }
      }
      
      // 長押しタイマーがあればクリア
      if (interaction.longPressTimer) {
        window.clearTimeout(interaction.longPressTimer);
        interaction.longPressTimer = null;
      }
      
      // 計算エリアでのドラッグ開始でグループ描画を開始（グループやリサイズハンドルでない場合のみ）
      if (!interaction.targetId && !interaction.targetType && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const startX = interaction.startPosition.x - rect.left;
        const startY = interaction.startPosition.y - rect.top;
        
        // グループ描画開始
        const start = startGroupDrawing(startX, startY);
        
        // インタラクション状態に保存
        interaction.isDrawingGroup = true;
        interaction.groupStartPoint = start;
        
        // UIステートも更新（ただしレンダリングには直接関与しない）
        setIsDrawingGroup(true);
        setGroupStartPoint(start);
      }
    }
    
    // ドラッグ中は body にクラスを追加してツールチップを非表示にする
    if (interaction.type === 'DRAG' || interaction.type === 'GROUP_DRAG' || interaction.type === 'LONG_CLICK_DRAG' || interaction.type === 'LONG_PRESS_DRAG') {
      if (!document.body.classList.contains('is-element-dragging')) {
        document.body.classList.add('is-element-dragging');
      }
    }

    // ドラッグ中の処理
    if (interaction.type === 'DRAG') {
      // パウチドラッグの処理
      if (interaction.targetType === 'pouch' && interaction.targetId && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left + interaction.dragOffset.x;
        const y = e.clientY - rect.top + interaction.dragOffset.y;
        
        
        // Option/AltキーまたはShiftキーが押されている場合、パウチを複製
        if ((getCtrlOptionModifierKey(e) || e.shiftKey) && !interaction.isCloning) {
          // 複製フラグを立てる
          interaction.isCloning = true;
          // 修飾キー+ドラッグの場合は段階マルチ変更を無効化
          interaction.disablePhaseChange = true;
          
          // 複製元のパウチの元の位置を保存
          const sourceElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`) as HTMLElement;
          const originalX = sourceElement ? parseFloat(sourceElement.style.left) : x;
          const originalY = sourceElement ? parseFloat(sourceElement.style.top) : y;
          
          // 最新のパウチ配列から複製元のパウチを探す
          const currentPouches = pouchesRef.current;
          const sourcePouch = currentPouches.find(p => p.id === interaction.targetId);
          
          if (sourcePouch) {
            // ×1かどうかを判定
            const equivalentPositiveValue = sourcePouch.multiplier > 0 ? 
              sourcePouch.multiplier : 
              1 / Math.abs(sourcePouch.multiplier);
            const isMultiplierOne = equivalentPositiveValue === 1;
            
            // Shiftキーの場合の特別な処理（altキーは押していない場合のみ）
            if (e.shiftKey && !getCtrlOptionModifierKey(e)) {
              // ×1の場合：|value|<2ならエラー
              if (isMultiplierOne && Math.abs(sourcePouch.value) < 2) {
                showPouchError(sourcePouch.id);
                interaction.hasError = true;
                interaction.isCloning = false;
                return;
              }
            }
            
            // Shift+Altキーの場合の特別な処理（マルチはんぶんこ純粋版：小数桁数5桁以上ならエラー）
            if (e.shiftKey && getCtrlOptionModifierKey(e)) {
              const originalMultiplier = sourcePouch.multiplier;
              const originalSign = originalMultiplier < 0;
              const pureMultiplier = originalSign ? 
                1 / Math.abs(originalMultiplier) : 
                originalMultiplier;
              const halfPureMultiplier = pureMultiplier / 2;
              const decimalPlaces = (halfPureMultiplier.toString().split('.')[1] || '').length;
              if (decimalPlaces >= 5) {
                showPouchError(sourcePouch.id);
                interaction.hasError = true;
                interaction.isCloning = false;
                return;
              }
            }
            
            const clonedPouch = clonePouch(sourcePouch, x, y, maxPouchZIndex);
            
            // 複製したパウチに_isDraggingフラグと_isCloningフラグを設定
            (clonedPouch as PouchWithDrag)._isDragging = true;
            (clonedPouch as PouchWithDrag)._isCloning = true;
            
            // Shiftキーの場合の分割処理（altキーは押していない場合のみ）
            if (e.shiftKey && !getCtrlOptionModifierKey(e)) {
              if (isMultiplierOne) {
                // ×1の場合：値ちぎる
                // ドラッグ側（clonedPouch）が1（負なら-1）
                const value = sourcePouch.value;
                const clonedValue = value > 0 ? 1 : -1;
                const sourceValue = value - clonedValue;
                
                clonedPouch.value = clonedValue;
                sourcePouch.value = sourceValue;
                clonedPouch.multiplier = 1;
                sourcePouch.multiplier = 1;
              } else {
                // ×1以外の場合：マルチちぎる（従来のロジック）
                const originalMultiplier = sourcePouch.multiplier;
                const originalSign = sourcePouch.multiplier < 0;
                
                const originalResult = sourcePouch.multiplier >= 0 ? 
                  sourcePouch.value * sourcePouch.multiplier : 
                  sourcePouch.value / Math.abs(sourcePouch.multiplier);
                
                const diffResult = originalResult - sourcePouch.value;
                
                if (diffResult === 0) {
                  showPouchError(sourcePouch.id);
                  interaction.hasError = true;
                  interaction.isCloning = false;
                  return;
                }
                
                const isClonedFixed = Math.abs(diffResult) >= Math.abs(sourcePouch.value);
                
                const fixedPouch = isClonedFixed ? clonedPouch : sourcePouch;
                const nonFixedPouch = isClonedFixed ? sourcePouch : clonedPouch;
                
                fixedPouch.multiplier = 1;
                
                const fixedResult = fixedPouch.value * fixedPouch.multiplier;
                const nonFixedResult = originalResult - fixedResult;
                
                const pureMultiplier = nonFixedResult / nonFixedPouch.value;
                
                if (originalSign) {
                  if (pureMultiplier < 0) {
                    nonFixedPouch.multiplier = -Math.abs(1 / pureMultiplier);
                    nonFixedPouch.value = -nonFixedPouch.value;
                  } else {
                    nonFixedPouch.multiplier = -Math.abs(1 / pureMultiplier);
                  }
                } else {
                  if (pureMultiplier < 0) {
                    nonFixedPouch.multiplier = -Math.abs(1 / pureMultiplier);
                    nonFixedPouch.value = -nonFixedPouch.value;
                  } else {
                    nonFixedPouch.multiplier = Math.abs(pureMultiplier);
                  }
                }
              }
            }
            
            // Shift+Altキーの場合の2分割処理（マルチはんぶんこ純粋版：常にマルチを純粋に半分）
            if (e.shiftKey && getCtrlOptionModifierKey(e)) {
              const originalMultiplier = sourcePouch.multiplier;
              const originalSign = originalMultiplier < 0;
              
              const pureMultiplier = originalSign ? 
                1 / Math.abs(originalMultiplier) : 
                originalMultiplier;
              
              const halfPureMultiplier = pureMultiplier / 2;
              
              const halfMultiplier = originalSign ? 
                -Math.abs(1 / halfPureMultiplier) : 
                halfPureMultiplier;
              
              sourcePouch.multiplier = halfMultiplier;
              clonedPouch.multiplier = halfMultiplier;
            }
            
            // 複製元のパウチを元の位置に戻す
            let updatedPouches;
            if (sourceElement) {
              sourceElement.style.left = `${originalX}px`;
              sourceElement.style.top = `${originalY}px`;
              
              // 更新されたパウチ配列を作成（複製パウチ追加 + 元パウチ位置リセット + フラグ設定）
              updatedPouches = [
                ...currentPouches.map(p => 
                  p.id === sourcePouch.id ? 
                    { ...p, x: originalX, y: originalY, _isDragging: true, _isCloneSource: true, multiplier: sourcePouch.multiplier } : 
                    p
                ),
                clonedPouch
              ];
            } else {
              // 複製したパウチを追加
              updatedPouches = [
                ...currentPouches.map(p => 
                  p.id === sourcePouch.id ? 
                    { ...p, _isDragging: true, _isCloneSource: true, multiplier: sourcePouch.multiplier } : 
                    p
                ),
                clonedPouch
              ];
            }
            
            // 元のIDの代わりに新しいIDを設定
            interaction.targetId = clonedPouch.id;
            setMaxPouchZIndex(maxPouchZIndex + 1);
            
            // 最新のグループ状態を取得
            const currentGroups = groupsRef.current;
            
            // 統合APIで直接更新
            setPouches(updatedPouches);
            pouchesRef.current = updatedPouches;
          }
        } else {
          // 通常のドラッグ時は位置を更新
          updatePouchPosition(interaction.targetId, x, y);
        }
      }
      // グループ描画中の処理
      else if (interaction.isDrawingGroup && interaction.groupStartPoint && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        
        // グループパスを更新
        const path = continueGroupDrawing(interaction.groupStartPoint, currentX, currentY);
        interaction.currentGroupPath = path;
        
        // pendingGroupを更新
        setPendingGroup(path);
      }
    }

    else if (interaction.type === 'LONG_CLICK_DRAG') {
      // パウチの長押しドラッグの処理（複製機能）
      // 長押し時点で既に複製されている場合はこのステップをスキップ
      if (interaction.targetType === 'pouch' && interaction.targetId && areaRef.current && !interaction.isCloning) {
        
        
        // 複製フラグを立てる
        interaction.isCloning = true;
        
        const rect = areaRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // 複製元のパウチを探す
        const currentPouches = pouchesRef.current;
        const sourcePouch = currentPouches.find(p => p.id === interaction.targetId);
        
        if (sourcePouch) {
          // パウチを複製
          const clonedPouch = clonePouch(sourcePouch, x, y, maxPouchZIndex + 1);
          
          // 複製したパウチに_isCloningフラグを設定
          (clonedPouch as PouchWithDrag)._isCloning = true;
          
          // 複製したパウチをpouchesに追加し、元のパウチにも_isDraggingフラグを設定
          const updatedPouches = currentPouches.map(p => {
            if (p.id === sourcePouch.id) {
              return { ...p, _isDragging: true }; // 複製元にもドラッグフラグを設定
            }
            return p;
          });
          updatedPouches.push(clonedPouch);
          
          // 元のIDの代わりに新しいIDを設定
          interaction.targetId = clonedPouch.id;
          
          // Z-indexを更新
          setMaxPouchZIndex(maxPouchZIndex + 1);
          
          // 最新のグループ状態を取得
          const currentGroups = groupsRef.current;
          
          // 統合APIで直接更新（クリティカル更新、DB保存なし）
          
          // onUpdateActiveSheet(updatedPouches, currentGroups, true, false);
          
          // ドラッグオフセットをリセット（中心でドラッグ開始）
          interaction.dragOffset = { x: 0, y: 0 };
        }
      }
      
      // 複製されたパウチまたはドラッグ中のパウチの位置を更新
      if (interaction.targetType === 'pouch' && interaction.targetId && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // ポインタ位置＋ドラッグオフセットでパウチ位置を更新
        const newX = x + interaction.dragOffset.x;
        const newY = y + interaction.dragOffset.y;
        
        // パウチの位置更新
        updatePouchPosition(interaction.targetId, newX, newY);
      }
      // グループの長押しドラッグ処理
      else if (interaction.targetType === 'group' && interaction.targetId && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        
        // グループの新しい位置を計算（ドラッグオフセットを考慮、グリッドにスナップ）
        const newGroupX = snapToGrid(currentX - interaction.dragOffset.x);
        const newGroupY = snapToGrid(currentY - interaction.dragOffset.y);
        
        // 移動対象のグループを特定
        const bounds = interaction.clonedGroup 
          ? getPathBounds(interaction.clonedGroup.path)
          : (() => {
              const group = groupsRef.current.find(g => g.id === interaction.targetId);
              return group ? getPathBounds(group.path) : null;
            })();
        
        if (bounds) {
          // 移動距離を計算
          const deltaX = newGroupX - bounds.left;
          const deltaY = newGroupY - bounds.top;
          
          // 画面端に到達したかチェック
          const containerWidth = rect.width;
          const containerHeight = rect.height;
          
          // 画面内に収まるように調整
          const adjustedDeltaX = Math.max(0 - bounds.left, deltaX);
          const adjustedDeltaY = Math.max(0 - bounds.top, deltaY);
          
          // パフォーマンス向上のために、DOM操作だけで位置を更新
          // グループ要素全体を移動（CSSトランスフォームで）
          const groupWrapper = document.querySelector(`div.group-wrapper[data-group-id="${interaction.targetId}"]`);
          if (groupWrapper) {
            (groupWrapper as HTMLElement).style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
          }
          
          // グループに含まれるパウチを移動（Shiftキーが押されていない場合のみ）
          if (interaction.groupPouches && interaction.groupPouches.length > 0 && !interaction.isShiftPressed) {
            interaction.groupPouches.forEach(pouch => {
              const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
              if (pouchElement) {
                const newX = pouch.x + adjustedDeltaX;
                const newY = pouch.y + adjustedDeltaY;
                (pouchElement as HTMLElement).style.left = `${newX}px`;
                (pouchElement as HTMLElement).style.top = `${newY}px`;
              }
            });
          }
          
          // 内包されているグループも移動（Shiftキーが押されていない場合のみ）
          if (interaction.containedGroups && interaction.containedGroups.length > 0 && !interaction.isShiftPressed) {
            interaction.containedGroups.forEach(containedGroup => {
              const containedWrapper = document.querySelector(`div.group-wrapper[data-group-id="${containedGroup.id}"]`);
              if (containedWrapper) {
                (containedWrapper as HTMLElement).style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
              }
            });
          }
        }
      }
    }
    // グループドラッグ中の処理
    else if (interaction.type === 'GROUP_DRAG' && interaction.targetId && areaRef.current) {
      const rect = areaRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      
      // Option/Altキーが押されている場合、グループを複製
      if ((getCtrlOptionModifierKey(e)) && !interaction.isCloning) {
        
        // 複製フラグを立てる
        interaction.isCloning = true;
        
        // 最新のグループ情報とパウチ情報を取得
        const currentGroups = groupsRef.current;
        const currentPouches = pouchesRef.current;
        
        // 複製元グループを見つける
        const sourceGroup = currentGroups.find(g => g.id === interaction.targetId);
        
        
        if (sourceGroup) {
          // 1. グループの複製（元の位置で複製、後でtransformで移動）
          const clonedGroup = cloneGroup(sourceGroup, 0, 0, maxGroupZIndex + 1);
          
          // _isCloning属性を追加
          clonedGroup._isCloning = true;
          
          // Shiftキーが押されている場合は、グループ自体のみを複製（中のパウチや内包グループは複製しない）
          if (e.shiftKey) {
            // 空のグループとして複製
            clonedGroup.pouches = [];
            
            // 複製したグループのみを追加し、複製元グループの_isDraggingフラグを即座に解除
            const originalGroupIds = [sourceGroup.id, ...interaction.containedGroups.map(cg => cg.id)];
            const updatedGroups = currentGroups.map(g => {
              if (originalGroupIds.includes(g.id)) {
                return { ...g, _isDragging: false };
              }
              return g;
            });
            updatedGroups.push({ ...clonedGroup, _isDragging: true }); // 複製されたグループにドラッグフラグを設定
            setGroups(updatedGroups);
            // 複製元グループの_isDragging解除をgroupsRefにも即座に反映
            groupsRef.current = updatedGroups;
            setMaxGroupZIndex(prev => prev + 1);
            
            // 複製したグループをドラッグ対象に設定
            interaction.targetId = clonedGroup.id;
            interaction.clonedGroup = clonedGroup;
            interaction.groupPouches = [];
            interaction.containedGroups = [];
            
            // 複製されたグループは元のグループと同じ位置にあるため、
            // dragOffsetはそのまま使用する
            
            return;
          }
          
          // 2. 複製元グループに含まれるパウチを複製（shiftキーが押されていない場合のみ）
          const clonedPouches: Pouch[] = [];
          const pouchIdMap: {[oldId: string]: string} = {}; // 古いID → 新しいIDのマッピング
          
          if (sourceGroup.pouches && sourceGroup.pouches.length > 0) {
            sourceGroup.pouches.forEach(pouchId => {
              const sourcePouch = currentPouches.find(p => p.id === pouchId);
              if (sourcePouch) {
                // パウチを複製（位置はそのまま）
                const clonedPouch = clonePouch(sourcePouch, sourcePouch.x, sourcePouch.y, maxPouchZIndex + clonedPouches.length + 1);
                clonedPouches.push(clonedPouch);
                
                // ID変換マップを更新
                pouchIdMap[pouchId] = clonedPouch.id;
              }
            });
            
            // 複製したグループのパウチIDリストを更新
            clonedGroup.pouches = sourceGroup.pouches.map(id => pouchIdMap[id] || id);
          }
          
          // 3. 内包グループも複製
          const clonedContainedGroups: Group[] = [];
          const groupIdMap: {[oldId: string]: string} = {}; // 古いグループID → 新しいグループIDのマッピング
          groupIdMap[sourceGroup.id] = clonedGroup.id; // 親グループのマッピングを追加
          
          interaction.containedGroups.forEach(({ id, path }) => {
            const containedGroup = currentGroups.find(g => g.id === id);
            if (containedGroup) {
              // グループを複製
              const clonedContainedGroup = cloneGroup(containedGroup, 0, 0, maxGroupZIndex + 2 + clonedContainedGroups.length);
              clonedContainedGroups.push(clonedContainedGroup);
              
              // ID変換マップを更新
              groupIdMap[containedGroup.id] = clonedContainedGroup.id;
              
              // 内包グループに所属するパウチも複製
              if (containedGroup.pouches && containedGroup.pouches.length > 0) {
                containedGroup.pouches.forEach(pouchId => {
                  // すでに複製済みでなければ複製
                  if (!pouchIdMap[pouchId]) {
                    const sourcePouch = currentPouches.find(p => p.id === pouchId);
                    if (sourcePouch) {
                      const clonedPouch = clonePouch(sourcePouch, sourcePouch.x, sourcePouch.y, maxPouchZIndex + clonedPouches.length + 1);
                      clonedPouches.push(clonedPouch);
                      pouchIdMap[pouchId] = clonedPouch.id;
                    }
                  }
                });
                
                // 複製した内包グループのパウチIDリストを更新
                clonedContainedGroup.pouches = containedGroup.pouches.map(id => pouchIdMap[id] || id);
              }
            }
          });
          
          // 4. すべての複製パウチとグループを配列に追加し、複製されたグループにドラッグフラグを設定
          const updatedPouches = [...currentPouches, ...clonedPouches];
          const updatedGroupsWithDragFlags = [...currentGroups, 
            { ...clonedGroup, _isDragging: true }, // 複製されたメイングループにドラッグフラグを設定
            ...clonedContainedGroups.map(g => ({ ...g, _isDragging: true })) // 複製された内包グループにもドラッグフラグを設定
          ];
          const updatedGroups = updatedGroupsWithDragFlags;
          
          // 5. GTPの参照を新しいグループに付け替え
          const clonedPouchIds = clonedPouches.map(p => p.id);
          const pouchesWithUpdatedGTPReferences = updateGTPReferencesAfterClone(updatedPouches, groupIdMap, clonedPouchIds);
          
          // 6. 複製されたパウチにドラッグフラグを設定
          const finalPouches = pouchesWithUpdatedGTPReferences.map(pouch => {
            if (clonedPouches.some(cp => cp.id === pouch.id)) {
              // 新しく複製されたパウチの場合
              return {
                ...pouch,
                _isDragging: true
              };
            }
            return pouch;
          });
          
          // 6. 複製元グループとその内包グループの_isDraggingフラグを即座に解除
          const originalGroupIds = [sourceGroup.id, ...interaction.containedGroups.map(cg => cg.id)];
          const updatedGroupsWithSourceReset = updatedGroups.map(g => {
            if (originalGroupIds.includes(g.id)) {
              return { ...g, _isDragging: false };
            }
            return g;
          });
          
          // 複製元グループの所属パウチの_isDraggingフラグも即座に解除
          const originalGroupPouchIds = sourceGroup.pouches || [];
          const finalPouchesWithSourceReset = finalPouches.map(p => {
            if (originalGroupPouchIds.includes(p.id)) {
              return { ...p, _isDragging: false };
            }
            return p;
          });
          
          // 6. 統合APIで更新（DB保存なし）
          setGroups(updatedGroupsWithSourceReset);
          // 複製元グループの_isDragging解除をgroupsRefにも即座に反映
          groupsRef.current = updatedGroupsWithSourceReset;
          setPouches(finalPouchesWithSourceReset);
          // onUpdateActiveSheet(finalPouches, updatedGroups, true, true, undefined, HistoryActionType.CLONE_GROUP);
          
          // maxPouchZIndexとmaxGroupZIndexを更新
          setMaxPouchZIndex(prev => prev + clonedPouches.length);
          setMaxGroupZIndex(prev => prev + 1 + clonedContainedGroups.length);
          
          // 7. 制御をクローンされたグループに移す
          interaction.targetId = clonedGroup.id;
          interaction.clonedGroup = clonedGroup;
          
          // 8. 複製されたグループは元のグループと同じ位置にあるため、
          // dragOffsetはそのまま使用する
          
          // 9. 内包グループと所属パウチの情報も更新
          const clonedGroupPouches = clonedPouches
            .filter(p => clonedGroup.pouches?.includes(p.id))
            .map(p => ({ id: p.id, x: p.x, y: p.y }));
          
          interaction.groupPouches = clonedGroupPouches;
          
          interaction.containedGroups = clonedContainedGroups.map(g => ({ 
            id: g.id, 
            path: g.path 
          }));
        }
      }
      
      // グループの新しい位置を計算（ドラッグオフセットを考慮、グリッドにスナップ）
      let newGroupX = snapToGrid(currentX - interaction.dragOffset.x);
      let newGroupY = snapToGrid(currentY - interaction.dragOffset.y);
      
      // 複製かつシート間移動の場合、calc-areaの境界で制限（左と上だけ）
      if (interaction.draggedFromOtherSheet && interaction.isCloning) {
        const calculatorArea = document.querySelector('.calculator-area') as HTMLElement;
        if (calculatorArea) {
          const calcRect = calculatorArea.getBoundingClientRect();
          const areaRect = areaRef.current.getBoundingClientRect();
          
          // areaRef基準でのcalc-areaの相対位置
          const calcLeft = calcRect.left - areaRect.left;
          const calcTop = calcRect.top - areaRect.top;
          
          // calc-areaの左端・上端で制限
          newGroupX = Math.max(calcLeft, newGroupX);
          newGroupY = Math.max(calcTop, newGroupY);
        }
      }
      
      // C: createdragging後のmove1回目だけログ出力
      if (interaction.isFirstMoveAfterCreateDragged) {
        // 移動対象のグループの境界を取得（複製後の場合は clonedGroup を使用）
        const bounds = interaction.clonedGroup 
          ? getPathBounds(interaction.clonedGroup.path)
          : (() => {
              const group = groupsRef.current.find(g => g.id === interaction.targetId);
              return group ? getPathBounds(group.path) : null;
            })();
        
        interaction.isFirstMoveAfterCreateDragged = false; // フラグをリセット
      }
      
      // 移動対象のグループを特定
      // 複製済みの場合は直接 interaction.clonedGroup を使用
      // そうでなければ groupsRef.current から探す
      const bounds = interaction.clonedGroup 
        ? getPathBounds(interaction.clonedGroup.path)
        : (() => {
            const group = groupsRef.current.find(g => g.id === interaction.targetId);
            return group ? getPathBounds(group.path) : null;
          })();
      
      if (bounds) {
        // 移動距離を計算
        const deltaX = newGroupX - bounds.left;
        const deltaY = newGroupY - bounds.top;
        
        // 画面端に到達したかチェック
        const containerWidth = rect.width;
        const containerHeight = rect.height;
        
        // 画面内に収まるように調整（累積移動量を考慮）
        let adjustedDeltaX: number, adjustedDeltaY: number;
        

        adjustedDeltaX = Math.max(0 - bounds.left, deltaX);
        adjustedDeltaY = Math.max(0 - bounds.top, deltaY);
        
        
        
        // 画面端に到達したかチェック（シート間移動の準備）
        // より緩い条件で画面端を検知（境界線に近い場合も含める）
        const edgeThreshold = 10; // 10px以内を画面端とみなす
        const isAtEdge = (
          (adjustedDeltaX === 0 && deltaX !== 0) || 
          (adjustedDeltaY === 0 && deltaY !== 0) ||
          (adjustedDeltaX <= edgeThreshold && deltaX < 0) ||
          (adjustedDeltaY <= edgeThreshold && deltaY < 0)
        );
        
        
        // 画面端に到達した場合、またはグループドラッグ中の場合はシート間移動の準備を開始
        if (isAtEdge && !isDraggingToSheet) {
          const currentGroups = groupsRef.current;
          const currentPouches = pouchesRef.current;
          
          // ドラッグ中のグループと関連データを取得
          const draggedGroup = interaction.clonedGroup || currentGroups.find(g => g.id === interaction.targetId);
          if (draggedGroup) {
            // 本体グループの現在位置を取得
            const mainGroupBounds = getPathBounds(draggedGroup.path);
            if (!mainGroupBounds) return;
            
            // 本体グループのtransform: translate分を取得
            const groupElement = document.querySelector(`div.group-wrapper[data-group-id="${draggedGroup.id}"]`);
            let translateX = 0, translateY = 0;
            if (groupElement) {
              const transform = groupElement.getAttribute('style');
              if (transform) {
                const match = transform.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
                if (match) {
                  translateX = parseFloat(match[1]) || 0;
                  translateY = parseFloat(match[2]) || 0;
                }
              }
            }
            
            // パス位置 + translate分 = 実際の表示位置
            const actualMainGroupLeft = mainGroupBounds.left + translateX;
            const actualMainGroupTop = mainGroupBounds.top + translateY;
            
            // ドラッグ中の所属パウチを取得し、本体グループからの相対位置を計算
            const groupPouches = currentPouches.filter(p => 
              (p as any)._isDragging && !(p as any)._isCloneSource
            ).map(p => {
              // パウチの実際の表示位置を取得（left/topプロパティ）
              const pouchElement = document.querySelector(`[data-pouch-id="${p.id}"]`) as HTMLElement;
              let actualPouchX = p.x;
              let actualPouchY = p.y;
              
              if (pouchElement) {
                // CSSのleft/topから実際の位置を取得
                const computedStyle = window.getComputedStyle(pouchElement);
                const left = parseFloat(computedStyle.left) || 0;
                const top = parseFloat(computedStyle.top) || 0;
                
                // left/topが設定されている場合はそれを使用
                if (left !== 0 || top !== 0) {
                  actualPouchX = left;
                  actualPouchY = top;
                }
              }
              
              return {
                ...p,
                relativeX: actualPouchX - actualMainGroupLeft,
                relativeY: actualPouchY - actualMainGroupTop
              };
            });
            
            // ドラッグ中の内包グループを取得し、本体グループからの相対位置を計算
            const containedGroups = currentGroups.filter(g => 
              (g as any)._isDragging && 
              g.id !== draggedGroup.id && 
              !(g as any)._isCloneSource
            ).map(g => {
              const containedBounds = getPathBounds(g.path);
              if (!containedBounds) {
                return {
                  ...g,
                  relativeX: 0,
                  relativeY: 0
                };
              }
              
              // 内包グループのtransform: translate分を取得
              const containedElement = document.querySelector(`div.group-wrapper[data-group-id="${g.id}"]`);
              let containedTranslateX = 0, containedTranslateY = 0;
              if (containedElement) {
                const transform = containedElement.getAttribute('style');
                if (transform) {
                  const match = transform.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
                  if (match) {
                    containedTranslateX = parseFloat(match[1]) || 0;
                    containedTranslateY = parseFloat(match[2]) || 0;
                  }
                }
              }
              
              // パス位置 + translate分 = 実際の表示位置
              const actualContainedLeft = containedBounds.left + containedTranslateX;
              const actualContainedTop = containedBounds.top + containedTranslateY;
              
              return {
                ...g,
                relativeX: actualContainedLeft - actualMainGroupLeft,
                relativeY: actualContainedTop - actualMainGroupTop
              };
            });
            
            // globalInteractionRefに必要な情報を保存
            if (globalInteractionRef) {
              globalInteractionRef.current = {
                ...interactionRef.current,
                activeSheetId: activeSheetId,
                // 本体グループの位置情報（translate分を加算した実際の表示位置）
                mainGroupPosition: {
                  left: actualMainGroupLeft,
                  top: actualMainGroupTop,
                  width: mainGroupBounds.right - mainGroupBounds.left,
                  height: mainGroupBounds.bottom - mainGroupBounds.top
                },
                // translate分を記憶
                originalTranslate: {
                  x: translateX,
                  y: translateY
                },
                // 相対位置情報を含むデータ
                draggedGroupData: {
                  group: draggedGroup,
                  pouches: groupPouches,
                  containedGroups: containedGroups,
                  sourceSheetId: dragStartSheetIdRef.current || currentSheet?.id || activeSheetId
                }
              };
            }
            
            // シート間ドラッグ開始を通知（簡素化）
            onStartSheetDrag({
              group: draggedGroup,
              pouches: groupPouches,
              containedGroups: containedGroups,
              finalPosition: { x: 0, y: 0 }, // 使用しないため適当な値
              sourceSheetId: dragStartSheetIdRef.current || currentSheet?.id || activeSheetId
            });
          }
        }
        if (interaction.draggedFromOtherSheet && interaction.isCloning) {
          if (adjustedDeltaX <= 0 || adjustedDeltaY <= 0) {
            return;
          }
        }
        
        // パフォーマンス向上のために、DOM操作だけで位置を更新
        // グループ要素全体を移動（CSSトランスフォームで）- SVGも含むラッパー全体に適用
        const groupWrapper = document.querySelector(`div.group-wrapper[data-group-id="${interaction.targetId}"]`);
        if (groupWrapper) {
          (groupWrapper as HTMLElement).style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
        }
        
        // 2. グループに含まれるパウチを移動（CSSの位置で）（Shiftキーが押されていない場合のみ）
        if (interaction.groupPouches && interaction.groupPouches.length > 0 && !interaction.isShiftPressed) {
          interaction.groupPouches.forEach(pouch => {
            const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            if (pouchElement) {
              const newX = pouch.x + adjustedDeltaX;
              const newY = pouch.y + adjustedDeltaY;
              (pouchElement as HTMLElement).style.left = `${newX}px`;
              (pouchElement as HTMLElement).style.top = `${newY}px`;
            }
          });
        }
        
        // 3. 内包されているグループも移動（CSSトランスフォームで）（Shiftキーが押されていない場合のみ）
        if (interaction.containedGroups && interaction.containedGroups.length > 0 && !interaction.isShiftPressed) {
          interaction.containedGroups.forEach(containedGroup => {
            // グループの最上位ラッパー要素を移動（SVG要素も含む）
            const containedWrapper = document.querySelector(`div.group-wrapper[data-group-id="${containedGroup.id}"]`);
            if (containedWrapper) {
              (containedWrapper as HTMLElement).style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
            }
          });
        }
      }
    }
    // グループリサイズ中の処理を追加
    else if (interaction.type === 'GROUP_RESIZE' && interaction.targetId && areaRef.current) {
      const rect = areaRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      
      // リサイズ処理を実行（groupInitialBoundsがundefinedの場合はnullとして扱う）
      resizeGroup(
        interaction.targetId,
        interaction.groupInitialBounds || null,
        currentX,
        currentY,
        interaction.dragOffset.x,
        interaction.dragOffset.y
      );
    }
  }, [updatePouchPosition, calculateDistance, resizeGroup, pouches, groups, maxPouchZIndex, maxGroupZIndex, onUpdateActiveSheet, bumpScrollEdgeBufferIfNearViewportEdge]);
  
  // マウスアップハンドラー（グループとリサイズハンドルの処理を追加）修正
  const handleMouseUp = useCallback((e: MouseEvent) => {
    // console.log('[DRAG_DEBUG] handleMouseUp開始:', {
    //   activeSheetId,
    //   interactionType: interactionRef.current.type,
    //   targetId: interactionRef.current.targetId,
    //   targetType: interactionRef.current.targetType,
    //   draggingPouches: pouchesRef.current.filter(p => p._isDragging).map(p => p.id),
    //   draggingGroups: groupsRef.current.filter(g => (g as any)._isDragging).map(g => g.id),
    //   eventListeners: { ...eventListenersRef.current }
    // });
    
    // 最優先: 削除フラグチェック（無限ループ防止）
    if ((window as any)[`destroying_${activeSheetId}`]) {
      // console.log('[DRAG_DEBUG] 削除フラグにより早期リターン');
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    
    const interaction = interactionRef.current;
    
    // シート間ドラッグ終了処理が実行されたかどうかのフラグ
    let crossSheetDragCompleted = false;
    
    // シート間移動完了前は、グローバルシートIDフィルタリングをスキップ
    const isSheetDragInProgress = interaction.type === 'DRAG' && 
      ((interaction.targetType === 'pouch' && interaction.targetId && interaction.draggedFromOtherSheet) ||
       (interaction.targetType === 'group' && interaction.targetId && interaction.draggedFromOtherSheet));
    
    if (!isSheetDragInProgress) {
      // シート間移動中でない場合のみ、グローバルなアクティブシートIDと比較してフィルタリング
      const globalActiveSheetId = document.querySelector('.app-container')?.getAttribute('data-active-sheet-id');
      
      if (globalActiveSheetId && activeSheetId !== globalActiveSheetId) {
        // 削除フラグを即座に設定（これ以降の全てのイベントを無効化）
        (window as any)[`destroying_${activeSheetId}`] = true;
        
        // イベントの伝播を停止
        e.stopPropagation();
        e.preventDefault();
        
        return;
      }
    }

    // D: ドラッグ終了時だけログ出力
    if (interaction.type === 'DRAG' || interaction.type === 'GROUP_DRAG' || interaction.type === 'LONG_CLICK_DRAG') {
      if (areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // グループの場合、複製後の境界を取得
        let groupBounds = null;
        let isCloned = false;
        if (interaction.targetType === 'group') {
          if (interaction.clonedGroup) {
            groupBounds = getPathBounds(interaction.clonedGroup.path);
            isCloned = true;
          } else {
            const group = groupsRef.current.find(g => g.id === interaction.targetId);
            if (group) {
              groupBounds = getPathBounds(group.path);
            }
          }
        }
        
        // 本・内・パの位置を取得
        let logMainGroup: { x: number, y: number } | null = null;
        let logContainedGroups: { id: string, x: number, y: number }[] = [];
        let logPouches: { id: string, x: number, y: number }[] = [];
        
        if (interaction.targetType === 'group') {
          // メイングループの位置（実際のDOM要素から取得）
          const mainGroupElement = document.querySelector(`div.group-wrapper[data-group-id="${interaction.targetId}"]`);
          if (mainGroupElement) {
            const rect = mainGroupElement.getBoundingClientRect();
            const containerRect = areaRef.current?.getBoundingClientRect();
            if (containerRect) {
              logMainGroup = {
                x: rect.left - containerRect.left,
                y: rect.top - containerRect.top
              };
            }
          }
          
          // 内包グループの位置（実際のDOM要素から取得）
          if (interaction.containedGroups) {
            logContainedGroups = interaction.containedGroups.map(containedGroup => {
              const containedElement = document.querySelector(`div.group-wrapper[data-group-id="${containedGroup.id}"]`);
              if (containedElement) {
                const rect = containedElement.getBoundingClientRect();
                const containerRect = areaRef.current?.getBoundingClientRect();
                if (containerRect) {
                  return {
                    id: containedGroup.id,
                    x: rect.left - containerRect.left,
                    y: rect.top - containerRect.top
                  };
                }
              }
              return null;
            }).filter((item): item is { id: string, x: number, y: number } => item !== null);
          }
          
          // パウチの位置（実際のDOM要素から取得）
          if (interaction.groupPouches) {
            logPouches = interaction.groupPouches.map(pouch => {
              const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
              if (pouchElement) {
                const rect = pouchElement.getBoundingClientRect();
                const containerRect = areaRef.current?.getBoundingClientRect();
                if (containerRect) {
                  return {
                    id: pouch.id,
                    x: rect.left - containerRect.left,
                    y: rect.top - containerRect.top
                  };
                }
              }
              return null;
            }).filter((item): item is { id: string, x: number, y: number } => item !== null);
          }
        }
        
        // 相対位置を計算
        let relativePositions = null;
        if (logMainGroup && (logContainedGroups.length > 0 || logPouches.length > 0)) {
          relativePositions = {
            内相対: logContainedGroups.map(item => ({
              id: item.id,
              relativeX: item.x - logMainGroup!.x,
              relativeY: item.y - logMainGroup!.y
            })),
            パ相対: logPouches.map(item => ({
              id: item.id,
              relativeX: item.x - logMainGroup!.x,
              relativeY: item.y - logMainGroup!.y
            }))
          };
        }

      }
    }
    
    // mouseup と mousemove イベントリスナーを削除
    if (eventListenersRef.current.mouseup) {
      document.removeEventListener('mouseup', handleMouseUp);
      eventListenersRef.current.mouseup = false;
    }
    if (eventListenersRef.current.mousemove) {
      document.removeEventListener('mousemove', handleMouseMove);
      eventListenersRef.current.mousemove = false;
    }
    // グローバルなドラッグ状態がある場合のみ、アクティブシートチェックを行う
    if (globalInteractionRef?.current) {
      // 現在のシートがアクティブでない場合は処理しない
      if (globalInteractionRef.current.activeSheetId !== activeSheetId) {
        return;
      } else {
      }
      // グローバル状態もクリア
      globalInteractionRef.current = null;
    }
    
    // マウスダウンされていない場合は何もしない
    if (!interaction.startTime) {
      return;
    }

    try {

    // 段階変更のタイマーをクリア
    if (interaction.phaseTimers) {
      interaction.phaseTimers.forEach(timerId => {
        clearTimeout(timerId);
      });
      interaction.phaseTimers = [];
    }
    
    // 旧タイマーもクリア（互換性のため）
    if (interaction.multiplierChangeTimer1) {
      clearTimeout(interaction.multiplierChangeTimer1);
      interaction.multiplierChangeTimer1 = undefined;
    }
    if (interaction.multiplierChangeTimer2) {
      clearTimeout(interaction.multiplierChangeTimer2);
      interaction.multiplierChangeTimer2 = undefined;
    }
    
    const distance = calculateDistance(interaction.startPosition, { x: e.clientX, y: e.clientY });
    
    // 操作完了フラグ - 実際にグループやパウチが更新されたかを追跡
    let operationCompleted = false;
    
    // シート間ドラッグ終了の場合（通常のドラッグ終了処理より前に実行）
    
    if (interaction.type === 'DRAG' && interaction.targetType === 'pouch' && interaction.targetId && interaction.draggedFromOtherSheet) {
      
      // 更新ロックをかける
      lockUpdates();
      
      // ドラッグ中のパウチを取得
      const draggedPouch = pouchesRef.current.find(p => p.id === interaction.targetId);
      if (!draggedPouch) {
        unlockUpdates();
        return;
      }
      
      // パウチが削除済みかどうかをチェック
      const pouchExists = pouchesRef.current.some(p => p.id === interaction.targetId);
      if (!pouchExists) {
        unlockUpdates();
        return;
      }
      
      // パウチの位置を更新（tempPouchPositionRefを使用）
      const updatedPouches = pouchesRef.current.map(pouch => {
        if (pouch.id === interaction.targetId) {
          const finalX = tempPouchPositionRef.current?.x || draggedPouch.x;
          const finalY = tempPouchPositionRef.current?.y || draggedPouch.y;
          
          
          return {
            ...pouch,
            x: finalX,
            y: finalY,
            _isDragging: false,
            _isFromOtherSheet: false
          };
        }
        return pouch;
      });
      
      setPouches(updatedPouches);
      pouchesRef.current = updatedPouches;
      
      // 一時的な位置情報をクリア
      tempPouchPositionRef.current = null;
      
      // 統合APIで更新（移動元シートIDを渡す）
      const sourceSheetId = draggedPouchDataRef?.current?.sourceSheetId;
      onUpdateActiveSheet(updatedPouches, groupsRef.current, true, true, undefined, HistoryActionType.MOVE_POUCH, undefined, sourceSheetId);
      
      // グローバルドラッグ状態をクリア
      if (globalInteractionRef) {
        globalInteractionRef.current = null;
      }

      // 元シートからパウチを削除
      if (sourceSheetPouchRef?.current && onRemovePouchFromSourceSheet && draggedPouchDataRef?.current) {
        const sourcePouchId = sourceSheetPouchRef.current.id;
        const sourceSheetId = draggedPouchDataRef.current.sourceSheetId;
        
        // 自分自身のシートから削除しようとしている場合はスキップ
        if (sourceSheetId && sourceSheetId !== activeSheetId) {
          onRemovePouchFromSourceSheet(sourcePouchId, sourceSheetId);
        } else {
        }
      }
      
      // interaction状態をクリーンアップ（シート間移動完了後）
      resetInteraction();
      
      // シート間ドラッグ終了処理が完了したことをマーク
      crossSheetDragCompleted = true;
    }
    // ドラッグ終了の場合
    if (interaction.type === 'DRAG' || interaction.type === 'LONG_CLICK_DRAG') {
      
      // シート間ドラッグ終了処理が既に実行された場合は、通常のドラッグ終了処理をスキップ
      if (crossSheetDragCompleted) {
        return;
      }
      
      // パウチが削除済みかどうかをチェック（通常のドラッグ処理でも）
      if (interaction.targetType === 'pouch' && interaction.targetId) {
        const pouchExists = pouchesRef.current.some(p => p.id === interaction.targetId);
        if (!pouchExists) {
          // interaction状態をクリア
          resetInteraction();
          return;
        }
      }
      
      // パウチのドラッグ終了時
      if (interaction.targetType === 'pouch' && interaction.targetId) {
        // パウチの再補正処理を実行
        const pouchRef = pouchRefs.current.get(interaction.targetId);
        if (pouchRef?.current?.handleMouseUp) {
          pouchRef.current.handleMouseUp();
        }
        
        // 更新ロックをかける
        lockUpdates();
        
        // 最新のパウチデータを取得
        const currentPouches = [...pouchesRef.current];
        
        // ドラッグされたパウチの位置を更新
        let updatedPouches = currentPouches.map(p => {
          if (p.id === interaction.targetId) {
            const finalX = tempPouchPositionRef.current?.x || p.x;
            const finalY = tempPouchPositionRef.current?.y || p.y;
            return {
              ...p,
              x: finalX,
              y: finalY,
              _isDragging: false,
              _isCloneSource: false,
              _isCloning: false,
              _isMoving: true, // 位置移動フラグをオン
              zIndex: maxPouchZIndex + 1
            };
          }
          return p;
        });
        
        // GTPの位置が一時的に保存されている場合は、それも更新に含める
        if (tempPouchPositionRef.current && tempPouchPositionRef.current.id !== interaction.targetId) {
          const tempPosition = tempPouchPositionRef.current;
          updatedPouches = updatedPouches.map(p => {
            if (p.id === tempPosition.id) {
              // 実際のパウチデータと異なる場合のみ更新
              if (p.x !== tempPosition.x || p.y !== tempPosition.y) {
                return {
                  ...p,
                  x: tempPosition.x,
                  y: tempPosition.y
                };
              }
            }
            return p;
          });
        }
        
        // マージ処理をチェック（複製中でも相手がcloneSourceでなければマージ可能）
        let isMerged = false; // マージが実行されたかどうかのフラグ
        const draggedPouch = updatedPouches.find(p => p.id === interaction.targetId);
        if (draggedPouch) {
          // processPouchMerge関数を使用してマージ処理を実行
          const mergeResult = processPouchMerge(draggedPouch, updatedPouches);
          if (mergeResult.isMerged) {
            updatedPouches = mergeResult.pouches;
            isMerged = true;
          }
        }
        
        // 一時的な位置情報をクリア
        tempPouchPositionRef.current = null;
        
        // 移動フラグがついたパウチ数をログに出力
        const movingPouches = updatedPouches.filter(p => p._isMoving);
        
        // maxPouchZIndexを更新
        const newMaxZIndex = maxPouchZIndex + 1;
        setMaxPouchZIndex(newMaxZIndex);
        
        // 統合更新APIを使用（パウチだけ更新）
        
        const currentGroups = groupsRef.current;
        
        // エラーフラグが立っている場合はhistoryに記録しない
        if (interaction.hasError) {
          // エラー時はhistoryに記録せずに処理を継続
          interaction.hasError = false;
        }
        
        // 複製フラグとマージフラグに基づいて適切なアクションタイプを設定
        let actionType: HistoryActionType;
        if (interaction.isCloning) {
          actionType = HistoryActionType.CLONE_POUCH;
        } else if (isMerged) {
          actionType = HistoryActionType.MERGE_POUCH;
        } else {
          actionType = HistoryActionType.MOVE_POUCH;
        }
        
        // 複製元のパウチのフラグをリセット（onUpdateActiveSheetの前に実行）
        if (actionType === HistoryActionType.CLONE_POUCH) {
          const updatedPouchesWithResetFlags = updatedPouches.map(p => 
            p._isCloneSource ? 
              { ...p, _isDragging: false, _isCloneSource: false, _isCloning: false } : 
              p
          );
          // エラー時はhistoryに記録しない（saveToHistoryをfalseに設定）
          const saveToHistory = !interaction.hasError;
          onUpdateActiveSheet(updatedPouchesWithResetFlags, currentGroups, true, saveToHistory, undefined, actionType);
        } else {
          // エラー時はhistoryに記録しない（saveToHistoryをfalseに設定）
          const saveToHistory = !interaction.hasError;
          onUpdateActiveSheet(updatedPouches, currentGroups, true, saveToHistory, undefined, actionType);
        }
        
        // 複製フラグをリセット
        interaction.isCloning = false;
        
        operationCompleted = true;
      }
      
      // 計算エリア上でのドラッグ終了かつグループ描画中の場合
      if (!interaction.targetId && interaction.isDrawingGroup && interaction.groupStartPoint && areaRef.current) {
        // 更新ロックをかける
        lockUpdates();
        
        const rect = areaRef.current.getBoundingClientRect();
        const endX = e.clientX - rect.left;
        const endY = e.clientY - rect.top;
        
        // 最小サイズチェック
        const isTooSmall = (() => {
          if (!interaction.currentGroupPath) return true;
          const bounds = getPathBounds(interaction.currentGroupPath);
          return bounds ? 
            (Math.min(bounds.right - bounds.left, bounds.bottom - bounds.top) < 100 || Math.max(bounds.right - bounds.left, bounds.bottom - bounds.top) < 150) : 
            true;
        })();
        
        // グループ描画を終了
        const newGroup = isTooSmall ? null : finishGroupDrawing(
          interaction.groupStartPoint,
          endX,
          endY,
          pouches,
          maxGroupZIndex,
          lastGroupColor,
          undefined,
          isTooSmall,
          groupsRef.current.length // グループのインデックスを追加
        );
        
        if (isTooSmall || !newGroup) {
          // グループ描画状態をリセット
          interaction.isDrawingGroup = false;
          interaction.groupStartPoint = null;
          interaction.currentGroupPath = '';
          
          setIsDrawingGroup(false);
          setGroupStartPoint(null);
          setCurrentGroupPath('');
          setPendingGroup('');
          return;
        }
        
        // 最大Z-Indexを更新（z-indexはDOM操作で管理するため、固定値をインクリメント）
        const newMaxZIndex = maxGroupZIndex + 1;
        setMaxGroupZIndex(newMaxZIndex);
        
        // 最新のグループ配列を取得し、新しいグループを追加
        const updatedGroups = [...groupsRef.current, newGroup];
        
        // 最新のパウチデータを取得
        const currentPouches = pouchesRef.current;
        
        // 統合更新APIで直接更新
        onUpdateActiveSheet(currentPouches, updatedGroups, true, true, undefined, HistoryActionType.ADD_GROUP);
        
        operationCompleted = true;
      }
    }
    // グループドラッグ終了の場合（シート間ドラッグの場合でも実行）
    if (interaction.type === 'GROUP_DRAG' && interaction.targetId) {
      
      // 更新ロックをかける
      lockUpdates();
      
      // グループの最上位ラッパー要素からトランスフォームを取得
      const groupWrapper = document.querySelector(`div.group-wrapper[data-group-id="${interaction.targetId}"]`);
      let deltaX = 0;
      let deltaY = 0;
      
      if (groupWrapper) {
        const transform = groupWrapper.getAttribute('style');
        if (transform) {
          const match = transform.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
          if (match) {
            deltaX = parseFloat(match[1]);
            deltaY = parseFloat(match[2]);
          }
        }
        
        // 変換をリセット
        (groupWrapper as HTMLElement).style.transform = '';
      }
      
      // 内包されているグループのtransformをリセット
      if (interaction.containedGroups && interaction.containedGroups.length > 0) {
        interaction.containedGroups.forEach(containedGroup => {
          // グループの最上位ラッパー要素のスタイルをリセット
          const containedWrapper = document.querySelector(`div.group-wrapper[data-group-id="${containedGroup.id}"]`);
          if (containedWrapper) {
            (containedWrapper as HTMLElement).style.transform = '';
          }
        });
      }
      
      if (deltaX === 0 && deltaY === 0) {
        // 移動がなければ処理終了
        setTimeout(unlockUpdates, 10);
        return;
      }
      
      // グループとパウチを直接更新
      
      
      // 最新の状態を取得
      const currentPouches = pouchesRef.current;
      let currentGroups = groupsRef.current;
      
      // 1. グループパスを新しい位置に移動し、ドラッグフラグを確実に解除
      const updatedGroups = currentGroups.map(g => {
        if (g.id === interaction.targetId) {
          const translatedPath = translatePath(g.path, deltaX, deltaY);
          return { 
            ...g, 
            path: translatedPath, 
            zIndex: maxGroupZIndex + 1,
            _isDragging: false,  // ドラッグ完了時にフラグをオフに
            _isCloning: false    // 複製・移動完了時にフラグをオフに
          };
        }
        
        // 内包グループも移動（Shiftキーが押されていない場合のみ）
        if (interaction.containedGroups.some(cg => cg.id === g.id) && !interaction.isShiftPressed) {
          const translatedPath = translatePath(g.path, deltaX, deltaY);
          return { 
            ...g, 
            path: translatedPath,
            _isDragging: false,  // ドラッグ完了時にフラグをオフに
            _isCloning: false    // 内包グループのフラグもオフに
          };
        }
        
        // その他のグループもドラッグフラグをクリア（安全対策）
        if ((g as any)._isDragging) {
          return {
            ...g,
            _isDragging: false,
            _isCloning: false
          };
        }
        
        return g;
      });

      // グループマージ処理：同位置・同サイズの別グループがあればマージ
      const draggedGroupId = interaction.targetId;
      const draggedGroup = updatedGroups.find(g => g.id === draggedGroupId);
      let mergedGroups = updatedGroups;
      let targetGroupForMerge: Group | null = null;
      
      if (draggedGroup) {
        const draggedBounds = getPathBounds(draggedGroup.path);
        const POSITION_TOLERANCE = 30; // 位置の許容誤差（30px以内なら同じ位置とみなす）
        
        if (draggedBounds) {
          const draggedWidth = draggedBounds.right - draggedBounds.left;
          const draggedHeight = draggedBounds.bottom - draggedBounds.top;
          
          // 同位置・同サイズの別グループを探す
          for (const otherGroup of updatedGroups) {
            if (otherGroup.id === draggedGroupId) continue;
            if (otherGroup._isDeleting) continue;
            
            const otherBounds = getPathBounds(otherGroup.path);
            if (!otherBounds) continue;
            
            const otherWidth = otherBounds.right - otherBounds.left;
            const otherHeight = otherBounds.bottom - otherBounds.top;
            
            // 位置とサイズが一致するかチェック
            const isSamePosition = 
              Math.abs(draggedBounds.left - otherBounds.left) <= POSITION_TOLERANCE &&
              Math.abs(draggedBounds.top - otherBounds.top) <= POSITION_TOLERANCE;
            const isSameSize = 
              Math.abs(draggedWidth - otherWidth) <= POSITION_TOLERANCE &&
              Math.abs(draggedHeight - otherHeight) <= POSITION_TOLERANCE;
            
            if (isSamePosition && isSameSize) {
              targetGroupForMerge = otherGroup;
              break;
            }
          }
        }
        
        // マージ対象が見つかった場合
        if (targetGroupForMerge) {
          // ドラッグしたグループのパウチを、マージ先グループに移動
          const draggedGroupPouches = draggedGroup.pouches || [];
          const targetGroupPouches = targetGroupForMerge.pouches || [];
          const mergedPouches = [...targetGroupPouches, ...draggedGroupPouches.filter(pId => !targetGroupPouches.includes(pId))];
          
          // グループを更新：マージ先にパウチを追加し、ドラッグしたグループを削除
          mergedGroups = updatedGroups
            .filter(g => g.id !== draggedGroupId) // ドラッグしたグループを削除
            .map(g => {
              if (g.id === targetGroupForMerge!.id) {
                return { ...g, pouches: mergedPouches };
              }
              return g;
            });
        }
      }

      setGroups(mergedGroups);
      // 2. パウチ位置を移動し、ドラッグフラグを確実に解除
      // また、グループマージが発生した場合はGTPのreferencedGroupIdを更新
      const updatedPouches = currentPouches.map(pouch => {
        const groupPouch = interaction.groupPouches?.find(p => p.id === pouch.id);
        let updatedPouch = pouch;
        
        if (groupPouch && !interaction.isShiftPressed) {
          const newX = groupPouch.x + deltaX;
          const newY = groupPouch.y + deltaY;
          updatedPouch = { ...pouch, x: newX, y: newY, _isDragging: false, _isCloneSource: false, _isCloning: false };
        } else if ((pouch as any)._isDragging) {
          // その他のパウチもドラッグフラグをクリア（安全対策）
          updatedPouch = {
            ...pouch,
            _isDragging: false,
            _isCloneSource: false,
            _isCloning: false
          };
        }
        
        // グループマージが発生した場合、ドラッグしたグループを参照しているGTPのreferencedGroupIdを更新
        if (targetGroupForMerge && updatedPouch.isGroupTotalPouch && updatedPouch.referencedGroupId === draggedGroupId) {
          updatedPouch = { ...updatedPouch, referencedGroupId: targetGroupForMerge.id };
        }
        
        return updatedPouch;
      });
      
      // 3. Z-indexも更新
      setMaxGroupZIndex(prev => prev + 1);
      
      // 4. 移動したパウチのマージ処理（Shiftキーが押されていない場合のみ）
      let finalPouches = updatedPouches;
      if (!interaction.isShiftPressed) {
        // 移動したパウチのリストを取得
        const movedPouches = updatedPouches.filter(pouch => {
          const groupPouch = interaction.groupPouches?.find(p => p.id === pouch.id);
          return groupPouch;
        });
        
        // 各移動パウチに対してマージ処理を実行
        let hasMerges = true;
        let processedPouches = finalPouches;
        
        while (hasMerges) {
          hasMerges = false;
          const currentMovedPouches = processedPouches.filter(pouch => {
            const groupPouch = interaction.groupPouches?.find(p => p.id === pouch.id);
            return groupPouch;
          });
          
          for (const movedPouch of currentMovedPouches) {
            const mergeResult = processPouchMerge(movedPouch, processedPouches);
            if (mergeResult.isMerged) {
              processedPouches = mergeResult.pouches;
              hasMerges = true;
              break; // マージが発生したら、リストを更新して再チェック
            }
          }
        }
        
        finalPouches = processedPouches;
        
        // 移動したグループに属していたパウチがすべてマージされたかチェック
        const movedGroupId = interaction.targetId;
        const originalMovedPouches = interaction.groupPouches || [];
        
        // 元々パウチが存在していた場合のみチェック（空のグループは削除しない）
        if (originalMovedPouches.length > 0) {
          // 移動したグループに属していたパウチのIDリスト
          const originalPouchIds = originalMovedPouches.map(p => p.id);
          
          // 現在のパウチリストで、元々移動したグループに属していたパウチが存在するかチェック
          const remainingOriginalPouches = finalPouches.filter(pouch => 
            originalPouchIds.includes(pouch.id)
          );
          
          // 元々移動したグループに属していたパウチがすべて削除された場合、そのグループを削除
          if (remainingOriginalPouches.length === 0) {
            // 削除対象のグループIDリスト（移動したグループ + 元々属していた内包グループ）
            const groupIdsToDelete = [movedGroupId];
            
            // 移動したグループに元々属していた内包グループも削除対象に追加
            if (interaction.containedGroups && interaction.containedGroups.length > 0) {
              const originalContainedGroupIds = interaction.containedGroups.map(cg => cg.id);
              groupIdsToDelete.push(...originalContainedGroupIds);
            }
            
            // グループを削除
            const filteredGroups = mergedGroups.filter(group => !groupIdsToDelete.includes(group.id));
            mergedGroups.splice(0, mergedGroups.length, ...filteredGroups);
          }
        }
      }
      
      // 5. 統合APIで更新
      const actionType = interaction.isCloning ? HistoryActionType.CLONE_GROUP : HistoryActionType.MOVE_GROUP;
      
      // 移動元シートIDを取得（draggedGroupDataRefから直接取得）
      const sourceSheetId = draggedGroupDataRef?.current?.sourceSheetId;
      
      onUpdateActiveSheet(finalPouches, mergedGroups, true, true, undefined, actionType, undefined, sourceSheetId);
      
      // 6. グローバルドラッグ状態をクリア
      if (globalInteractionRef) {
        globalInteractionRef.current = null;
      }
      
      // 7. 元シートからグループを削除
      
      if (interaction.draggedFromOtherSheet && sourceSheetGroupRef?.current && onRemoveGroupFromSourceSheet && draggedGroupDataRef?.current) {
        const sourceGroupId = sourceSheetGroupRef.current.id;
        const sourceSheetId = draggedGroupDataRef.current.sourceSheetId;
        
        // 自分自身のシートから削除しようとしている場合はスキップ
        if (sourceSheetId && sourceSheetId !== activeSheetId) {
          onRemoveGroupFromSourceSheet(sourceGroupId, sourceSheetId);
        } else {
        }
      }
    }
    // グループリサイズ終了の場合
    else if (interaction.type === 'GROUP_RESIZE' && interaction.targetId) {
      
      
      // 更新ロックをかける
      lockUpdates();
      
      // 最新の状態を取得して保存
      const currentPouches = pouchesRef.current;
      const currentGroups = groupsRef.current;
      
      // グループマージ処理：同位置・同サイズの別グループがあればマージ
      const resizedGroupId = interaction.targetId;
      const resizedGroup = currentGroups.find(g => g.id === resizedGroupId);
      let mergedGroups = currentGroups;
      let finalPouches = currentPouches;
      let targetGroupForMerge: Group | null = null;
      
      if (resizedGroup) {
        const resizedBounds = getPathBounds(resizedGroup.path);
        const POSITION_TOLERANCE = 30; // 位置の許容誤差（30px以内なら同じ位置とみなす）
        
        if (resizedBounds) {
          const resizedWidth = resizedBounds.right - resizedBounds.left;
          const resizedHeight = resizedBounds.bottom - resizedBounds.top;
          
          // 同位置・同サイズの別グループを探す
          for (const otherGroup of currentGroups) {
            if (otherGroup.id === resizedGroupId) continue;
            if (otherGroup._isDeleting) continue;
            
            const otherBounds = getPathBounds(otherGroup.path);
            if (!otherBounds) continue;
            
            const otherWidth = otherBounds.right - otherBounds.left;
            const otherHeight = otherBounds.bottom - otherBounds.top;
            
            // 位置とサイズが一致するかチェック
            const isSamePosition = 
              Math.abs(resizedBounds.left - otherBounds.left) <= POSITION_TOLERANCE &&
              Math.abs(resizedBounds.top - otherBounds.top) <= POSITION_TOLERANCE;
            const isSameSize = 
              Math.abs(resizedWidth - otherWidth) <= POSITION_TOLERANCE &&
              Math.abs(resizedHeight - otherHeight) <= POSITION_TOLERANCE;
            
            if (isSamePosition && isSameSize) {
              targetGroupForMerge = otherGroup;
              break;
            }
          }
        }
        
        // マージ対象が見つかった場合
        if (targetGroupForMerge) {
          // リサイズしたグループのパウチを、マージ先グループに移動
          const resizedGroupPouches = resizedGroup.pouches || [];
          const targetGroupPouches = targetGroupForMerge.pouches || [];
          const mergedPouchIds = [...targetGroupPouches, ...resizedGroupPouches.filter(pId => !targetGroupPouches.includes(pId))];
          
          // グループを更新：マージ先にパウチを追加し、リサイズしたグループを削除
          mergedGroups = currentGroups
            .filter(g => g.id !== resizedGroupId) // リサイズしたグループを削除
            .map(g => {
              if (g.id === targetGroupForMerge!.id) {
                return { ...g, pouches: mergedPouchIds };
              }
              return g;
            });
          
          // グループマージが発生した場合、リサイズしたグループを参照しているGTPのreferencedGroupIdを更新
          finalPouches = currentPouches.map(pouch => {
            if (pouch.isGroupTotalPouch && pouch.referencedGroupId === resizedGroupId) {
              return { ...pouch, referencedGroupId: targetGroupForMerge!.id };
            }
            return pouch;
          });
        }
      }
      
      // useEffectでの古いcurrentSheetによる上書きを防ぐ
      skipCurrentSheetUpdateRef.current = true;
      
      // 最終結果をDB保存（非クリティカル更新・DB保存あり）
      onUpdateActiveSheet(finalPouches, mergedGroups, false, true, undefined, HistoryActionType.RESIZE_GROUP);
      
      operationCompleted = true;
    }
    // クリック判定（移動距離がしきい値以下）（PC用のしきい値を使用）
    else if (distance <= CLICK_DISTANCE_THRESHOLD) {
      const currentTime = Date.now();
      const timeSinceLastClick = currentTime - interaction.lastClickTime;
      
      // クリックターゲットの要素を取得
      const targetElement = document.elementFromPoint(e.clientX, e.clientY);
      
      // ボタン要素またはその親要素にボタンが含まれるかを判定（disabled状態も含む）
      const isButtonOrInButton = targetElement ? 
        targetElement.closest('button') !== null || 
        targetElement.closest('.group-buttons') !== null || 
        targetElement.closest('.pouch-color-button, .pouch-delete-button, .pouch-split-button, .pouch-toggle-sign-button') !== null ||
        targetElement.closest('.group-title-input') !== null : 
        false;
      
      // ダブルクリック判定
      if (timeSinceLastClick < DOUBLE_CLICK_THRESHOLD) {
        interaction.type = 'DOUBLE_CLICK';
        
        interaction.lastClickTime = 0; // ダブルクリック後にリセット
        
        // 計算エリア・グループ内へのダブルクリックでパウチ作成（タッチデバイス以外、ボタン/ボタン領域クリック以外）
        if ((!interaction.targetId || interaction.targetType === 'group') && areaRef.current && !interaction.isTouchEvent && !isButtonOrInButton) {
          // 更新ロックをかける
          lockUpdates();
          
          const rect = areaRef.current.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          
          const newPouch = createNewPouch(x, y);
          
          // マウス操作の場合、作成後に値フィールドにフォーカス
          setTimeout(() => {
            const valueDisplay = document.querySelector(
              `[data-pouch-id="${newPouch.id}"] .value-display`
            ) as HTMLElement;
            if (valueDisplay) {
              keyboardNewPouchFirstValueEditPouchIdRef.current = newPouch.id;
              // mouseupイベントをディスパッチして編集モードを開始
              valueDisplay.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
            }
          }, 350);
          
          // 注意: createNewPouch関数が統合APIを呼び出すようになったため、
          // ここで再度onUpdateActiveSheetを呼び出す必要はありません
          operationCompleted = true;
        }
      } else {
        interaction.type = 'CLICK';
        
        interaction.lastClickTime = currentTime;
        
        
        
        // パウチクリック時にそのパウチを最前面に
        if (interaction.targetType === 'pouch' && interaction.targetId) {
          if (interaction.isCloning) {
            
            // 複製フラグとドラッグフラグを持つパウチをフィルタリングして削除
            const updatedPouches = pouchesRef.current.filter(pouch => 
              !pouch._isDragging
            );

            // 統合APIで更新（グループは変更なし）
            onUpdateActiveSheet(updatedPouches, groupsRef.current, false, false);

            // 参照も更新
            pouchesRef.current = updatedPouches;
          } else {
            // 更新ロックをかける
            lockUpdates();
            
            // 最前面に持ってくるが、Z-indexの振り直しはしない
            // 選択されたパウチだけを最前面にする
            const updatedPouches = pouchesRef.current.map(pouch => {
              if (pouch.id === interaction.targetId) {
                return {
                  ...pouch,
                  zIndex: maxPouchZIndex + 1
                };
              }
              return pouch;
            });
            
            // 統合APIで直接更新（現在のグループ状態と一緒に）
            const currentGroups = groupsRef.current;
            
            onUpdateActiveSheet(updatedPouches, currentGroups, false, false);
            
            // maxPouchZIndexを更新
            setMaxPouchZIndex(prev => prev + 1);
          }
          
          operationCompleted = true;
        }
        
        // クローン取り消し（計算エリアまたはグループへのシングルクリック時）
        if ((!interaction.targetId || interaction.targetType === 'group') && 
            areaRef.current && 
            !interaction.isTouchEvent &&
            !isButtonOrInButton &&
            interaction.isCloning) {
            // 複製フラグとドラッグフラグを持つアイテムをフィルタリングして削除
            const updatedPouches = pouchesRef.current.filter(pouch => 
              !pouch._isDragging
            );
            
            // _isCloningがtrueのグループとその記録された内包グループを除外
            const updatedGroups = groupsRef.current.filter(group => {
              if (group._isCloning) return false;
              const isContainedByCloning = interactionRef.current.containedGroups?.some(
                containedGroup => containedGroup.id === group.id
              );
              return !isContainedByCloning;
            });
          
            // 統合APIで更新
            onUpdateActiveSheet(updatedPouches, updatedGroups, false, false);
          
            // 参照も更新
            pouchesRef.current = updatedPouches;
            groupsRef.current = updatedGroups;
        }
      }
    }
    
    // 複製フラグとグループの一時参照をリセット
    interaction.isCloning = false;    
    interaction.clonedGroup = undefined;
    
    
    resetInteraction();
    
      operationCompleted = true;

    // 全リセット（無条件で実行）
    // 全てのイベントリスナーを削除
    if (eventListenersRef.current.mousemove) {
      document.removeEventListener('mousemove', handleMouseMove);
      eventListenersRef.current.mousemove = false;
    }
    if (eventListenersRef.current.mouseup) {
      document.removeEventListener('mouseup', handleMouseUp);
      eventListenersRef.current.mouseup = false;
    }
    if (eventListenersRef.current.touchmove) {
      document.removeEventListener('touchmove', handleTouchMove);
      eventListenersRef.current.touchmove = false;
    }
    if (eventListenersRef.current.touchend) {
      document.removeEventListener('touchend', handleTouchEnd);
      eventListenersRef.current.touchend = false;
    }
    
    // シート間ドラッグ状態をリセット
    if (onResetSheetDragState) {
      onResetSheetDragState();
    }

    // 全てのパウチのドラッグ状態、複製元状態、複製状態をリセット（安全対策）
    const allPouches = [...pouchesRef.current];
    const resetFlagPouches = allPouches.map(p => ({
      ...p,
      _isDragging: false,
      _isCloneSource: false,
      _isCloning: false
    }));
    
    // 全てのグループのドラッグ状態と複製状態をリセット（安全対策）
    const allGroups = [...groupsRef.current];
    const draggingGroupsBefore = allGroups.filter(g => (g as any)._isDragging);
    const resetFlagGroups = allGroups.map(g => ({
      ...g,
      _isDragging: false,
      _isCloning: false
    }));
    
    
    
    // DOM上の見た目も更新
    document.querySelectorAll('.group-dragging').forEach(el => {
      el.classList.remove('group-dragging');
    });
    
    
    // パウチ状態を更新
    setPouches(resetFlagPouches);
    pouchesRef.current = resetFlagPouches;
    
    // グループ状態を更新
    setGroups(resetFlagGroups);
    groupsRef.current = resetFlagGroups;

    // ロックがかかっていなかった場合（操作が完了していない場合）はここでリセット
    if (!operationCompleted && isUpdatingRef.current) {
      setTimeout(unlockUpdates, 10);
    }
    
    // console.log('[DRAG_DEBUG] handleMouseUp終了:', {
    //   activeSheetId,
    //   resetPouchesCount: resetFlagPouches.length,
    //   resetGroupsCount: resetFlagGroups.length,
    //   interactionType: interactionRef.current.type,
    //   draggingPouches: pouchesRef.current.filter(p => p._isDragging).map(p => p.id),
    //   draggingGroups: groupsRef.current.filter(g => (g as any)._isDragging).map(g => g.id)
    // });
    } finally {
      resetScrollEdgeBufferWithTransition();
    }
  }, [resetInteraction, createNewPouch, onUpdateActiveSheet, isDrawingGroup, groupStartPoint, pouches, groups, maxPouchZIndex, maxGroupZIndex, lockUpdates, unlockUpdates, currentSheet, resetScrollEdgeBufferWithTransition]);
  
    
  // タッチ終了後のフラグリセットを遅延させる
  const delayedTouchReset = useCallback(() => {
    // 複製フラグとグループの一時参照をリセット
    interactionRef.current.isCloning = false;
    interactionRef.current.clonedGroup = undefined;
    resetInteraction();
  }, [resetInteraction]);
  
  // バックグラウンドから復帰した時にイベントリスナーとドラッグ状態を強制クリーンアップ
  const forceCleanupOnActivation = useCallback(() => {
    // イベントリスナーの実際の状態とeventListenersRefの状態が不整合になっている可能性があるため、
    // 状態フラグを強制的にリセットする
    // これにより、次のmousedown/touchstart時に新しいイベントリスナーが確実に登録される
    
    // イベントリスナーの状態フラグを全てリセット
    // （実際のイベントリスナーが残っていても、次の操作で新しく登録されるため問題ない）
    eventListenersRef.current.mouseup = false;
    eventListenersRef.current.mousemove = false;
    eventListenersRef.current.touchend = false;
    eventListenersRef.current.touchmove = false;
    
    // ドラッグ状態をリセット
    if (interactionRef.current.type !== null) {
      resetInteraction();
      
      // 全てのパウチのドラッグ状態をリセット
      const resetPouches = pouchesRef.current.map(p => ({
        ...p,
        _isDragging: false,
        _isCloneSource: false,
        _isCloning: false
      }));
      setPouches(resetPouches);
      pouchesRef.current = resetPouches;
      
      // 全てのグループのドラッグ状態をリセット
      const resetGroups = groupsRef.current.map(g => ({
        ...g,
        _isDragging: false,
        _isCloning: false
      }));
      setGroups(resetGroups);
      groupsRef.current = resetGroups;
      
      // DOM上の見た目も更新
      document.querySelectorAll('.group-dragging, .pouch-dragging').forEach(el => {
        el.classList.remove('group-dragging', 'pouch-dragging');
      });
    }
  }, [resetInteraction]);
  
  // 非アクティブ状態検知とクリーンアップ処理
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // バックグラウンドからフォアグラウンドに復帰した
        wasRecentlyInactiveRef.current = true;
        
        // イベントリスナーとドラッグ状態を強制クリーンアップ
        forceCleanupOnActivation();
        
        // 500ms後にフラグをリセット
        if (inactiveTimeoutRef.current) {
          clearTimeout(inactiveTimeoutRef.current);
        }
        inactiveTimeoutRef.current = window.setTimeout(() => {
          wasRecentlyInactiveRef.current = false;
        }, 500);
      }
    };
    
    const handleFocus = () => {
      // ウィンドウがフォーカスを取得した
      wasRecentlyInactiveRef.current = true;
      
      // イベントリスナーとドラッグ状態を強制クリーンアップ
      forceCleanupOnActivation();
      
      // 500ms後にフラグをリセット
      if (inactiveTimeoutRef.current) {
        clearTimeout(inactiveTimeoutRef.current);
      }
      inactiveTimeoutRef.current = window.setTimeout(() => {
        wasRecentlyInactiveRef.current = false;
      }, 500);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (inactiveTimeoutRef.current) {
        clearTimeout(inactiveTimeoutRef.current);
      }
    };
  }, [forceCleanupOnActivation]);

  // シート切り替え時にドラッグ状態とイベントリスナーをクリーンアップ
  useEffect(() => {
    // console.log('[DRAG_DEBUG] シート切り替え検出:', activeSheetId);
    // console.log('[DRAG_DEBUG] 現在のドラッグ状態 - pouches:', pouchesRef.current.filter(p => p._isDragging).map(p => p.id));
    // console.log('[DRAG_DEBUG] 現在のドラッグ状態 - groups:', groupsRef.current.filter(g => (g as any)._isDragging).map(g => g.id));
    // console.log('[DRAG_DEBUG] interactionRef.type:', interactionRef.current.type);
    
    // すべてのシートの削除フラグをクリア
    Object.keys(window).forEach(key => {
      if (key.startsWith('destroying_')) {
        // console.log('[DRAG_DEBUG] 削除フラグをクリア:', key);
        delete (window as any)[key];
      }
    });
    
    // シート切り替え時はドラッグ状態を強制リセット
    forceCleanupOnActivation();
    
    // 古いイベントリスナーを確実に削除
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
    document.removeEventListener('touchcancel', handleTouchEnd);
    
    // イベントリスナーの状態フラグをリセット
    eventListenersRef.current = {
      mousemove: false,
      mouseup: false,
      touchmove: false,
      touchend: false
    };
    
    // interactionRefもリセット
    interactionRef.current = {
      ...interactionRef.current,
      type: null,
      targetId: null,
      targetType: null,
      isLongPressDetected: false,
      isTouchEvent: false,
      dragOffset: { x: 0, y: 0 },
      isDrawingGroup: false,
      groupStartPoint: null,
      currentGroupPath: '',
      isCloning: false,
      groupPouches: [],
      containedGroups: [],
      draggedFromOtherSheet: false,
      clonedGroup: undefined
    };
    
    // console.log('[DRAG_DEBUG] シート切り替えクリーンアップ完了');
  }, [activeSheetId]);

  // グローバルなフォールバック: マウスが離されたときに確実にドラッグ状態を解除
  useEffect(() => {
    const handleGlobalMouseUp = (e: Event) => {
      // ドラッグ中のフラグがある場合のみ処理
      const hasDraggingPouch = pouchesRef.current.some(p => p._isDragging);
      const hasDraggingGroup = groupsRef.current.some(g => (g as any)._isDragging);
      const interactionType = interactionRef.current.type;
      
      // 削除フラグをすべてクリア（マウスアップ時に常にクリア）
      Object.keys(window).forEach(key => {
        if (key.startsWith('destroying_')) {
          delete (window as any)[key];
        }
      });
      
      // console.log('[DRAG_DEBUG] グローバルmouseup検出:', {
      //   eventType: e.type,
      //   activeSheetId,
      //   hasDraggingPouch,
      //   hasDraggingGroup,
      //   interactionType,
      //   draggingPouches: pouchesRef.current.filter(p => p._isDragging).map(p => p.id),
      //   draggingGroups: groupsRef.current.filter(g => (g as any)._isDragging).map(g => g.id)
      // });
      
      if (hasDraggingPouch || hasDraggingGroup || interactionType !== null) {
        // 少し遅延させてから確認（通常のmouseupハンドラーが先に処理されるようにする）
        setTimeout(() => {
          // まだドラッグ状態が残っている場合は強制リセット
          const stillDraggingPouch = pouchesRef.current.some(p => p._isDragging);
          const stillDraggingGroup = groupsRef.current.some(g => (g as any)._isDragging);
          
          // console.log('[DRAG_DEBUG] 50ms後のチェック:', {
          //   stillDraggingPouch,
          //   stillDraggingGroup,
          //   draggingPouches: pouchesRef.current.filter(p => p._isDragging).map(p => p.id),
          //   draggingGroups: groupsRef.current.filter(g => (g as any)._isDragging).map(g => g.id),
          //   interactionType: interactionRef.current.type
          // });
          
          if (stillDraggingPouch || stillDraggingGroup) {
            // console.log('[DRAG_DEBUG] ドラッグ状態が残っている！強制リセット実行');
            
            // パウチのドラッグ状態をリセット
            const resetPouches = pouchesRef.current.map(p => ({
              ...p,
              _isDragging: false,
              _isCloneSource: false,
              _isCloning: false
            }));
            setPouches(resetPouches);
            pouchesRef.current = resetPouches;
            
            // グループのドラッグ状態をリセット
            const resetGroups = groupsRef.current.map(g => ({
              ...g,
              _isDragging: false,
              _isCloning: false
            }));
            setGroups(resetGroups);
            groupsRef.current = resetGroups;
            
            // DOM上の見た目も更新
            document.querySelectorAll('.group-dragging, .pouch-dragging, .dragging-flag').forEach(el => {
              el.classList.remove('group-dragging', 'pouch-dragging', 'dragging-flag');
            });
            
            // interactionRefもリセット
            if (interactionRef.current.longPressTimer) {
              window.clearTimeout(interactionRef.current.longPressTimer);
            }
            interactionRef.current = {
              ...interactionRef.current,
              type: null,
              targetId: null,
              targetType: null,
              isLongPressDetected: false,
              isTouchEvent: false,
              dragOffset: { x: 0, y: 0 },
              isDrawingGroup: false,
              groupStartPoint: null,
              currentGroupPath: '',
              isCloning: false,
              groupPouches: [],
              containedGroups: []
            };
            
            // グローバル状態もクリア
            if (globalInteractionRef) {
              globalInteractionRef.current = null;
            }
            
            // 描画中のグループをクリア
            setIsDrawingGroup(false);
            setGroupStartPoint(null);
            setCurrentGroupPath('');
            setPendingGroup('');
            
            // console.log('[DRAG_DEBUG] 強制リセット完了');
          } else {
            // console.log('[DRAG_DEBUG] ドラッグ状態は正常にクリア済み');
          }
        }, 50);
      }
    };
    
    // windowレベルでイベントをキャプチャ（bubbling phaseではなくcapture phaseで）
    window.addEventListener('mouseup', handleGlobalMouseUp, true);
    window.addEventListener('touchend', handleGlobalMouseUp, true);
    window.addEventListener('touchcancel', handleGlobalMouseUp, true);
    
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp, true);
      window.removeEventListener('touchend', handleGlobalMouseUp, true);
      window.removeEventListener('touchcancel', handleGlobalMouseUp, true);
    };
  }, [globalInteractionRef]);

  // タッチスタートハンドラー
  const handleTouchStart = useCallback((e: React.TouchEvent, targetId?: string, targetType?: 'pouch' | 'group' | 'resize-handle' | 'pending-pouch' | null) => {
    // ドラッグ開始時の元シートIDを記録
    dragStartSheetIdRef.current = currentSheet?.id || activeSheetId;
    
    // イベントリスナーを最初に登録（早期リターンに関係なく確実に登録）
    if (!eventListenersRef.current.touchmove) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      eventListenersRef.current.touchmove = true;
    }
    
    if (!eventListenersRef.current.touchend) {
      document.addEventListener('touchend', handleTouchEnd, { passive: false });
      document.addEventListener('touchcancel', handleTouchEnd, { passive: false });
      eventListenersRef.current.touchend = true;
    }
    
    // 前回のdelayedTouchResetタイマーをキャンセル
    if (delayedTouchResetTimerRef.current) {
      clearTimeout(delayedTouchResetTimerRef.current);
      delayedTouchResetTimerRef.current = null;
    }
    
    // タッチ操作が検出されたらタッチデバイスフラグをオンに
    if (!isTouchDeviceRef.current) {
      isTouchDeviceRef.current = true;
      setIsCurrentTouchDevice(true);
      onUpdateDeviceType(true); // HTMLクラスを更新
    }
    
    // タッチデバイスでなければ処理しない
    if (!isTouchDeviceRef.current) {
      return;
    }

    // データ更新中は新しい操作を受け付けない
    if (isUpdatingRef.current) {
      
      return;
    }

    // サブトータルクリックかどうかを判定
    const targetElement = e.target as HTMLElement;
    const isSubtotalClick = targetElement.closest('.group-subtotal.clickable-subtotal') !== null;

    // サブトータルクリックの場合は処理しない（ただし、ドラッグ処理は開始する）
    if (isSubtotalClick) {
      // ドラッグ処理を開始するため、早期リターンしない
      // return;
    }

    // タッチした要素以外のすべてのフォーカス可能な要素からフォーカスを外す
    if (document.activeElement instanceof HTMLElement && e.currentTarget !== document.activeElement) {
      document.activeElement.blur();
    }
  
    // 非アクティブからのタッチを検知
    if (wasRecentlyInactiveRef.current) {
      
      
      // touchendイベントを自動的に発火（50ms遅延）
      setTimeout(() => {
        handleTouchEnd(e as unknown as TouchEvent);
      }, 50);
    }
    
    
    const isButtonOrInButton = e.targetTouches[0] ? 
      (e.targetTouches[0].target as HTMLElement).closest('button') !== null || 
      (e.targetTouches[0].target as HTMLElement).closest('.group-buttons') !== null || 
      (e.targetTouches[0].target as HTMLElement).closest('.pouch-color-button, .pouch-delete-button, .pouch-split-button, .pouch-toggle-sign-button') !== null ||
      (e.targetTouches[0].target as HTMLElement).closest('.group-title-input') !== null ||
      (e.targetTouches[0].target as HTMLElement).closest('.legends') !== null :
      false;

    // イベント伝播を止める
    e.stopPropagation();

    // 2本指: ページスクロール専用（1本指はグループ描画・ドラッグ用）
    if (e.touches.length >= 2) {
      const prev = interactionRef.current;
      if (prev.longPressTimer) {
        window.clearTimeout(prev.longPressTimer);
      }
      if (prev.phaseTimers) {
        prev.phaseTimers.forEach(timerId => window.clearTimeout(timerId));
      }
      if (prev.isDrawingGroup || isDrawingGroup) {
        setIsDrawingGroup(false);
        setGroupStartPoint(null);
        setCurrentGroupPath('');
        setPendingGroup('');
      }
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const midX = (t0.clientX + t1.clientX) / 2;
      const midY = (t0.clientY + t1.clientY) / 2;
      twoFingerMidRef.current = { x: midX, y: midY };
      interactionRef.current = {
        type: null,
        targetId: null,
        targetType: null,
        startPosition: { x: midX, y: midY },
        currentPosition: { x: midX, y: midY },
        startTime: Date.now(),
        lastClickTime: prev.lastClickTime ?? 0,
        isLongPressDetected: false,
        longPressTimer: null,
        isTouchEvent: true,
        dragOffset: { x: 0, y: 0 },
        isDrawingGroup: false,
        groupStartPoint: null,
        currentGroupPath: '',
        isShiftPressed: false,
        isCloning: false,
        groupPouches: [],
        containedGroups: [],
        isTwoFingerPan: true,
      };
      document.body.classList.remove('is-element-dragging');
      return;
    }

    document.body.classList.add('is-calculator-touching');

    // タッチイベントのフラグをセット
    interactionRef.current = {
      ...interactionRef.current,
      targetId: targetId || null,
      targetType: targetType || null,
      startPosition: { 
        x: e.touches[0].clientX, 
        y: e.touches[0].clientY 
      },
      currentPosition: { 
        x: e.touches[0].clientX, 
        y: e.touches[0].clientY 
      },
      startTime: Date.now(),
      isLongPressDetected: false,
      type: null, // 初期値はnullのまま
      // 修飾キーの状態を保存（長押しホールド時の段階変更で使用）
      modifierKeysOnHold: {
        ctrlOption: (() => {
          const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
          return isMac ? (window as any).__altKeyPressed : (window as any).__ctrlKeyPressed;
        })(),
        shift: (window as any).__shiftKeyPressed || false
      },
      longPressTimer: window.setTimeout(() => {
        // 長押し判定
        const interaction = interactionRef.current;
        const distance = calculateDistance(
          interaction.startPosition,
          interaction.currentPosition
        );
        
        // 長押し中に大きく動いていない場合（モバイル用のしきい値を使用）
        if (distance <= TAP_DISTANCE_THRESHOLD && !isButtonOrInButton) {
          interaction.isLongPressDetected = true;
          interaction.type = 'LONG_PRESS';
          interaction.longPressStartTime = Date.now();
          
        // パウチの長押し時に即座に複製処理を実行
        if (interaction.targetType === 'pouch' && interaction.targetId && areaRef.current && !interaction.isCloning) {
          interaction.isCloning = true;
          const rect = areaRef.current.getBoundingClientRect();
          const x = interaction.currentPosition.x - rect.left;
          const y = interaction.currentPosition.y - rect.top;
          // 複製元のパウチの元の位置を保存
          const sourceElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`) as HTMLElement;
          const originalX = sourceElement ? parseFloat(sourceElement.style.left) : x;
          const originalY = sourceElement ? parseFloat(sourceElement.style.top) : y;
          // 最新のパウチ配列から複製元のパウチを探す
          const currentPouches = pouchesRef.current;
          const sourcePouch = currentPouches.find(p => p.id === interaction.targetId);
          if (sourcePouch) {
            // 複製元のパウチIDを保存（複製処理前に保存）
            interaction.originalPouchId = interaction.targetId;
            
            // 元のマルチ値を保存
            interaction.originalMultiplier = sourcePouch.multiplier;
            
            const newPouch: PouchWithDrag = {
              ...sourcePouch,
              id: uuidv4(),
              x: originalX,
              y: originalY,
              _isDragging: true,
              _isCloneSource: false,
              _isCloning: true
            };
            // 複製元のパウチに複製元フラグを設定
            const updatedPouches = currentPouches.map(p => {
              if (p.id === interaction.targetId) {
                return {
                  ...p,
                  _isCloneSource: true
                } as PouchWithDrag;
              }
              return p;
            });
            updatedPouches.push(newPouch);
            setPouches(updatedPouches);
            pouchesRef.current = updatedPouches;
            interaction.targetId = newPouch.id;
            
            // エラー条件を先に判定して実行フェーズを決定
            const currentPouchesAfterClone = pouchesRef.current;
            const sourcePouchAfterClone = currentPouchesAfterClone.find(p => p.id === interaction.originalPouchId);
            const clonedPouchAfterClone = currentPouchesAfterClone.find(p => p.id === interaction.targetId);
            
            if (sourcePouchAfterClone && clonedPouchAfterClone) {
              // 複製パウチの要素にchanging-clone-modeクラスを追加（DOM更新後に実行）
              setTimeout(() => {
                const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
                if (clonedPouchElement) {
                  clonedPouchElement.classList.add('changing-clone-mode');
                } else {
                  // もう一度試してみる（Reactの再レンダリングを待つ）
                  setTimeout(() => {
                    const retryElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
                    if (retryElement) {
                      retryElement.classList.add('changing-clone-mode');
                    }
                  }, 100);
                }
              }, 0);
              
              // 元の値を保存
              const originalValue = sourcePouchAfterClone.value;
              
              // フェーズ1（ちぎる）のエラー条件をチェック
              const originalResult = sourcePouchAfterClone.multiplier >= 0 ? 
                sourcePouchAfterClone.value * sourcePouchAfterClone.multiplier : 
                sourcePouchAfterClone.value / Math.abs(sourcePouchAfterClone.multiplier);
              const diffResult = originalResult - sourcePouchAfterClone.value;
              const canTear = diffResult !== 0;
              
              // フェーズ2（はんぶんこ）のエラー条件をチェック
              const canSplit = canHalfSplitPouchByMultiplier(sourcePouchAfterClone, currentSheet);
              
              // ×1かどうかを判定
              const equivalentPositiveValue = sourcePouchAfterClone.multiplier > 0 ? 
                sourcePouchAfterClone.multiplier : 
                1 / Math.abs(sourcePouchAfterClone.multiplier);
              const isMultiplierOne = equivalentPositiveValue === 1;
              
              // APパウチの場合はちぎる・はんぶんこをスキップ（複製のみ許可）
              const isBalancingPouchSource = currentSheet?.balancingPouchId === interaction.originalPouchId;

              // 修飾キー+ドラッグの場合は段階マルチ変更を無効化
              if (interaction.disablePhaseChange) {
                return;
              }
              
              // 修飾キー+長押しホールドの場合は1段階目で修飾キー相当のマルチ値に変更
              if (interaction.modifierKeysOnHold && (interaction.modifierKeysOnHold.shift || interaction.modifierKeysOnHold.ctrlOption)) {
                const { ctrlOption, shift } = interaction.modifierKeysOnHold;
                
                // 修飾キーが押されている場合のみ処理を実行
                  // エラー条件をチェック
                  let hasError = false;
                  if (isBalancingPouchSource && (shift)) {
                    // APパウチでShiftが押されている場合はちぎる・はんぶんこ不可（複製に強制）
                    hasError = false;
                  } else if (shift && !ctrlOption) {
                    // Shiftキーの場合：×1で|value|<2の場合はエラー
                    if (isMultiplierOne) {
                      hasError = Math.abs(sourcePouchAfterClone.value) < 2;
                    } else {
                      hasError = false; // ×1以外は常にOK
                    }
                  } else if (shift && ctrlOption) {
                    // Shift+Ctrl/Optionキーの場合
                    if (isMultiplierOne) {
                      // ×1の場合：|value|<2の場合はエラー
                      hasError = Math.abs(sourcePouchAfterClone.value) < 2;
                    } else {
                      // ×1以外の場合：小数桁数が5桁以上の場合はエラー
                      const originalMultiplier = sourcePouchAfterClone.multiplier;
                      const originalSign = originalMultiplier < 0;
                      const pureMultiplier = originalSign ? 
                        1 / Math.abs(originalMultiplier) : 
                        originalMultiplier;
                      const halfPureMultiplier = pureMultiplier / 2;
                      const decimalPlaces = (halfPureMultiplier.toString().split('.')[1] || '').length;
                      hasError = decimalPlaces >= 5;
                    }
                  }
                  
                  if (hasError) {
                    // エラーの場合はブルブル表示
                    showPouchError(sourcePouchAfterClone.id);
                  } else {
                    // 1段階目で修飾キー相当のマルチ値に変更
                    const timerId = window.setTimeout(() => {
                      // ドラッグが開始されていたらフェーズ変更を停止
                      if (interactionRef.current.type === 'DRAG' || interactionRef.current.type === 'LONG_PRESS_DRAG') {
                        return;
                      }
                      
                      const currentPouches = pouchesRef.current;
                      const sourcePouch = currentPouches.find(p => p.id === interaction.originalPouchId);
                      const clonedPouch = currentPouches.find(p => p.id === interaction.targetId);
                      
                      if (!sourcePouch || !clonedPouch) return;
                      
                      let result: { success: boolean; sourcePouch?: Pouch; clonedPouch?: Pouch } | null = null;
                      
                      if (isBalancingPouchSource) {
                        // APパウチの場合：常に通常の複製
                        result = { success: true, sourcePouch, clonedPouch };
                      } else if (shift && !ctrlOption) {
                        // Shiftキーの場合
                        if (isMultiplierOne) {
                          // ×1の場合：値ちぎる
                          result = performValueTearOperation(sourcePouch, clonedPouch);
                        } else {
                          // ×1以外の場合：マルチ分割複製
                          result = performTearOperation(sourcePouch, clonedPouch);
                        }
                      } else if (shift && ctrlOption) {
                        // Shift+Ctrl/Optionキーの場合
                        if (isMultiplierOne) {
                          // ×1の場合：値はんぶんこ
                          result = performValueSplitOperation(sourcePouch, clonedPouch);
                        } else {
                          // ×1以外の場合：マルチ2分割複製
                          result = performSplitOperation(sourcePouch, clonedPouch, interaction.originalMultiplier);
                        }
                      } else if (ctrlOption && !shift) {
                        // Ctrl/Optionキーの場合：通常の複製（何もしない）
                        result = { success: true, sourcePouch, clonedPouch };
                      }
                      
                      if (result && result.success && result.sourcePouch && result.clonedPouch) {
                        const updatedPouches = currentPouches.map(p => {
                          if (p.id === result!.sourcePouch!.id) return result!.sourcePouch!;
                          if (p.id === result!.clonedPouch!.id) return result!.clonedPouch!;
                          return p;
                        });
                        setPouches(updatedPouches);
                        pouchesRef.current = updatedPouches;
                      }
                    }, 0); // 即座に実行
                    
                    // タイマーIDを配列に保存
                    const phaseTimers: number[] = [timerId];
                    interaction.phaseTimers = phaseTimers;
                  }
                return; // 修飾キー+長押しの場合は通常の段階変更処理をスキップ
              }
              
              // 実行するフェーズの配列を作成
              const phases: number[] = [];
              
              // ×1の場合と×1以外の場合でフェーズを分岐
              if (isBalancingPouchSource) {
                // APパウチの場合：複製のみ
                phases.push(3);
              } else if (isMultiplierOne) {
                // ×1の場合: 複製→値はんぶんこ→値ちぎる
                phases.push(3); // 純粋な複製（最初）
                if (Math.abs(originalValue) >= 2) {
                  phases.push(4); // 値はんぶんこ
                  phases.push(5); // 値ちぎる
                }
              } else {
                // ×1以外の場合: ちぎる→はんぶんこ→複製（従来通り）
                if (canTear) phases.push(1); // ちぎる
                if (canSplit) phases.push(2); // はんぶんこ
                phases.push(3); // 純粋な複製（常に実行）
              }
              
              // フェーズを順次実行
              const phaseTimers: number[] = [];
              phases.forEach((phase, index) => {
                const delay = index * 1000; // 0秒、1秒、2秒...
                
                const timerId = window.setTimeout(() => {
                  // ドラッグが開始されていたらフェーズ変更を停止
                  if (interactionRef.current.type === 'DRAG' || interactionRef.current.type === 'LONG_PRESS_DRAG') {
                    // ドラッグ開始時もクラスを削除
                    const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
                    if (clonedPouchElement) {
                      clonedPouchElement.classList.remove('changing-clone-mode');
                    }
                    return;
                  }
                  
                  const currentPouches = pouchesRef.current;
                  const sourcePouch = currentPouches.find(p => p.id === interaction.originalPouchId);
                  const clonedPouch = currentPouches.find(p => p.id === interaction.targetId);
                  
                  if (!sourcePouch || !clonedPouch) return;
                  
                  let result: { success: boolean; sourcePouch?: Pouch; clonedPouch?: Pouch } | null = null;
                  
                  switch (phase) {
                    case 1: // マルチちぎる
                      result = performTearOperation({ ...sourcePouch }, { ...clonedPouch });
                      break;
                    case 2: // マルチはんぶんこ
                      // 元のマルチ値から計算するため、元のマルチ値でパウチを復元
                      const sourcePouchForSplit = { ...sourcePouch, multiplier: interaction.originalMultiplier!, value: originalValue };
                      const clonedPouchForSplit = { ...clonedPouch, multiplier: interaction.originalMultiplier!, value: originalValue };
                      result = performSplitOperation(sourcePouchForSplit, clonedPouchForSplit, originalValue);
                      break;
                    case 3: // 純粋な複製
                      // 元のマルチ値と元の値に戻す
                      result = { 
                        success: true, 
                        sourcePouch: { ...sourcePouch, multiplier: interaction.originalMultiplier!, value: originalValue }, 
                        clonedPouch: { ...clonedPouch, multiplier: interaction.originalMultiplier!, value: originalValue } 
                      };
                      break;
                    case 4: // 値はんぶんこ（×1専用）
                      const sourcePouchForValueSplit = { ...sourcePouch, multiplier: 1, value: originalValue };
                      const clonedPouchForValueSplit = { ...clonedPouch, multiplier: 1, value: originalValue };
                      result = performValueSplitOperation(sourcePouchForValueSplit, clonedPouchForValueSplit);
                      break;
                    case 5: // 値ちぎる（×1専用）
                      const sourcePouchForValueTear = { ...sourcePouch, multiplier: 1, value: originalValue };
                      const clonedPouchForValueTear = { ...clonedPouch, multiplier: 1, value: originalValue };
                      result = performValueTearOperation(sourcePouchForValueTear, clonedPouchForValueTear);
                      break;
                  }
                  
                  if (result && result.success && result.sourcePouch && result.clonedPouch) {
                    const phaseSource = result.sourcePouch;
                    const phaseCloned = result.clonedPouch;
                    const updatedPouches = currentPouches.map(p => {
                      if (p.id === interaction.originalPouchId) {
                        return mergeClonePhasePouchScalars(p, phaseSource);
                      }
                      if (p.id === interaction.targetId) {
                        return mergeClonePhasePouchScalars(p, phaseCloned);
                      }
                      return p;
                    });
                    setPouches(updatedPouches);
                    pouchesRef.current = updatedPouches;
                  }
                  
                  // 最後のフェーズ（純粋な複製）が完了したらクラスを削除
                  // if (phase === 3) {
                  //   const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
                  //   if (clonedPouchElement) {
                  //     clonedPouchElement.classList.remove('changing-clone-mode');
                  //   }
                  // }
                }, delay);
                
                // タイマーIDを配列に保存
                phaseTimers.push(timerId);
              });
              
              // タイマーID配列をinteractionに保存
              interaction.phaseTimers = phaseTimers;
            }            
          }
          }
          // グループの長押し時に即座に複製処理を実行
          else if (interaction.targetType === 'group' && interaction.targetId && areaRef.current) {
            
            
            // 複製フラグを立てる
            interaction.isCloning = true;
            
            // 最新のグループ情報とパウチ情報を取得
            const currentGroups = groupsRef.current;
            const currentPouches = pouchesRef.current;
            
            // 複製元グループを見つける
            const sourceGroup = currentGroups.find(g => g.id === interaction.targetId);
            
            if (sourceGroup) {
              // 1. グループの複製
              const clonedGroup = cloneGroup(sourceGroup, 0, 0, maxGroupZIndex + 1);
              
              // _isCloning属性を追加
              clonedGroup._isCloning = true;
              
              // Shiftキーが押されている場合は、グループ自体のみを複製（中のパウチや内包グループは複製しない）
              if (interaction.modifierKeysOnHold?.shift) {
                // 空のグループとして複製
                clonedGroup.pouches = [];
                
                // 複製したグループのみを追加し、複製元グループの_isDraggingフラグを即座に解除
                const originalGroupIds = [sourceGroup.id, ...interaction.containedGroups.map(cg => cg.id)];
                const updatedGroups = currentGroups.map(g => {
                  if (originalGroupIds.includes(g.id)) {
                    return { ...g, _isDragging: false };
                  }
                  return g;
                });
                updatedGroups.push({ ...clonedGroup, _isDragging: true }); // 複製されたグループにドラッグフラグを設定
                
                // 複製元グループの所属パウチの_isDraggingフラグも即座に解除
                const originalGroupPouchIds = sourceGroup.pouches || [];
                const updatedPouches = currentPouches.map(p => {
                  if (originalGroupPouchIds.includes(p.id)) {
                    return { ...p, _isDragging: false };
                  }
                  return p;
                });
                
                setPouches(updatedPouches);
                setGroups(updatedGroups);
                // 複製元グループの_isDragging解除をgroupsRefにも即座に反映
                groupsRef.current = updatedGroups;
                pouchesRef.current = updatedPouches;
                setMaxGroupZIndex(prev => prev + 1);
                
                // 複製したグループをドラッグ対象に設定
                interaction.targetId = clonedGroup.id;
                interaction.clonedGroup = clonedGroup;
                interaction.groupPouches = [];
                interaction.containedGroups = [];
                
                return;
              }
              
              // 2. 複製元グループに含まれるパウチを複製（shiftキーが押されていない場合のみ）
              const clonedPouches: Pouch[] = [];
              const pouchIdMap: {[oldId: string]: string} = {}; // 古いID → 新しいIDのマッピング
              
              if (sourceGroup.pouches && sourceGroup.pouches.length > 0) {
                sourceGroup.pouches.forEach(pouchId => {
                  const sourcePouch = currentPouches.find(p => p.id === pouchId);
                  if (sourcePouch) {
                    // パウチを複製（位置はそのまま）
                    const clonedPouch = clonePouch(sourcePouch, sourcePouch.x, sourcePouch.y, maxPouchZIndex + clonedPouches.length + 1);
                    clonedPouches.push(clonedPouch);
                    
                    // ID変換マップを更新
                    pouchIdMap[pouchId] = clonedPouch.id;
                  }
                });
                
                // 複製したグループのパウチIDリストを更新
                clonedGroup.pouches = sourceGroup.pouches.map(id => pouchIdMap[id] || id);
              }
              
              // 3. 内包グループも複製
              const clonedContainedGroups: Group[] = [];
              const groupIdMap: {[oldId: string]: string} = {}; // 古いグループID → 新しいグループIDのマッピング
              groupIdMap[sourceGroup.id] = clonedGroup.id; // 親グループのマッピングを追加
              
              interaction.containedGroups.forEach(({ id, path }) => {
                const containedGroup = currentGroups.find(g => g.id === id);
                if (containedGroup) {
                  // グループを複製
                  const clonedContainedGroup = cloneGroup(containedGroup, 0, 0, maxGroupZIndex + 2 + clonedContainedGroups.length);
                  clonedContainedGroups.push(clonedContainedGroup);
                  
                  // ID変換マップを更新
                  groupIdMap[containedGroup.id] = clonedContainedGroup.id;
                  
                  // 内包グループに所属するパウチも複製
                  if (containedGroup.pouches && containedGroup.pouches.length > 0) {
                    containedGroup.pouches.forEach(pouchId => {
                      // すでに複製済みでなければ複製
                      if (!pouchIdMap[pouchId]) {
                        const sourcePouch = currentPouches.find(p => p.id === pouchId);
                        if (sourcePouch) {
                          const clonedPouch = clonePouch(sourcePouch, sourcePouch.x, sourcePouch.y, maxPouchZIndex + clonedPouches.length + 1);
                          clonedPouches.push(clonedPouch);
                          pouchIdMap[pouchId] = clonedPouch.id;
                        }
                      }
                    });
                    
                    // 複製した内包グループのパウチIDリストを更新
                    clonedContainedGroup.pouches = containedGroup.pouches.map(id => pouchIdMap[id] || id);
                  }
                }
              });
              
              // 4. すべての複製パウチとグループを配列に追加し、複製されたグループにドラッグフラグを設定
              const updatedPouches = [...currentPouches, ...clonedPouches];
              const updatedGroupsWithDragFlags = [...currentGroups, 
                { ...clonedGroup, _isDragging: true }, // 複製されたメイングループにドラッグフラグを設定
                ...clonedContainedGroups.map(g => ({ ...g, _isDragging: true })) // 複製された内包グループにもドラッグフラグを設定
              ];
              const updatedGroups = updatedGroupsWithDragFlags;
              
              // 5. GTPの参照を新しいグループに付け替え
              const clonedPouchIds = clonedPouches.map(p => p.id);
              const pouchesWithUpdatedGTPReferences = updateGTPReferencesAfterClone(updatedPouches, groupIdMap, clonedPouchIds);
              
              // 6. 複製されたパウチにドラッグフラグを設定
              const finalPouches = pouchesWithUpdatedGTPReferences.map(pouch => {
                if (clonedPouches.some(cp => cp.id === pouch.id)) {
                  // 新しく複製されたパウチの場合
                  return {
                    ...pouch,
                    _isDragging: true
                  };
                }
                return pouch;
              });
              // 複製元グループとその内包グループの_isDraggingフラグを即座に解除
              const originalGroupIds = [sourceGroup.id, ...interaction.containedGroups.map(cg => cg.id)];
              const updatedGroupsWithSourceReset = updatedGroups.map(g => {
                if (originalGroupIds.includes(g.id)) {
                  return { ...g, _isDragging: false };
                }
                return g;
              });
              
              // 複製元グループの所属パウチの_isDraggingフラグも即座に解除
              const originalGroupPouchIds = sourceGroup.pouches || [];
              const finalPouchesWithSourceReset = finalPouches.map(p => {
                if (originalGroupPouchIds.includes(p.id)) {
                  return { ...p, _isDragging: false };
                }
                return p;
              });
              
              setPouches(finalPouchesWithSourceReset);
              setGroups(updatedGroupsWithSourceReset);
              // 複製元グループの_isDragging解除をgroupsRefにも即座に反映
              groupsRef.current = updatedGroupsWithSourceReset;
              // 6. 統合APIで更新（DB保存なし）
              // onUpdateActiveSheet(finalPouches, updatedGroups, true, false);
              
              // 7. maxPouchZIndexとmaxGroupZIndexを更新
              setMaxPouchZIndex(prev => prev + clonedPouches.length);
              setMaxGroupZIndex(prev => prev + 1 + clonedContainedGroups.length);
              
              // 8. 制御をクローンされたグループに移す
              interaction.targetId = clonedGroup.id;
              interaction.clonedGroup = clonedGroup;
              
              // 9. 内包グループと所属パウチの情報も更新
              const clonedGroupPouches = clonedPouches
                .filter(p => clonedGroup.pouches?.includes(p.id))
                .map(p => ({ id: p.id, x: p.x, y: p.y }));
              
              interaction.groupPouches = clonedGroupPouches;
              
              interaction.containedGroups = clonedContainedGroups.map(g => ({ 
                id: g.id, 
                path: g.path 
              }));
            }
          }
        }
      }, LONG_PRESS_THRESHOLD),
      isTouchEvent: true,
      dragOffset: { x: 0, y: 0 },
      isCloning: false,
      isShiftPressed: false,
      groupPouches: [],
      containedGroups: [],
      clonedGroup: undefined
    };
    
    // パウチのドラッグ開始処理
    if (targetType === 'pouch' && targetId) {
      
      // パウチドラッグの共通処理を使用
      startPouchDrag(e, targetId);
    }
    // グループのドラッグ開始処理
    else if (targetType === 'group' && targetId) {
      // グループを見つける
      const group = groupsRef.current.find(g => g.id === targetId);
      if (group && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        
        // グループの現在の境界を取得
        const bounds = getPathBounds(group.path);
        if (bounds) {
          // タッチ位置とグループ左上コーナーとのオフセットを計算
          const touchX = e.touches[0].clientX - rect.left;
          const touchY = e.touches[0].clientY - rect.top;
          
          
          // オフセットを保存（グループ左上とタッチ位置の差）
          interactionRef.current.dragOffset = {
            x: touchX - bounds.left,
            y: touchY - bounds.top
          };
          
          // このグループと内包子グループに属するパウチの初期位置を記録
          const subtreePouchIds = collectPouchIdsForGroupSubtree(
            targetId,
            groupsRef.current,
          );
          const groupPouches = pouchesRef.current
            .filter(p => subtreePouchIds.includes(p.id))
            .map(p => ({ id: p.id, x: p.x, y: p.y }));
          
          interactionRef.current.groupPouches = groupPouches;
          
          // このグループに完全に内包されているグループを検出
          const containedGroups = groupsRef.current
            .filter(g => g.id !== targetId && isGroupPhysicallyContained(g.id, targetId, groupsRef.current))
            .map(g => ({ id: g.id, path: g.path }));
          
          interactionRef.current.containedGroups = containedGroups;
        }
      }
    }
    // リサイズハンドルのタッチ開始処理
    else if (targetType === 'resize-handle' && targetId) {
      // リサイズ対象のグループを見つける
      const group = groupsRef.current.find(g => g.id === targetId);
      if (group && areaRef.current) {
        // グループの現在の境界を保存
        const bounds = getPathBounds(group.path);
        if (bounds) {
          interactionRef.current.groupOriginalPath = group.path;
          interactionRef.current.groupInitialBounds = bounds;
          
          // タッチ位置とグループ右下コーナーとのオフセットを計算
          const rect = areaRef.current.getBoundingClientRect();
          const cornerX = bounds.right;
          const cornerY = bounds.bottom;
          const touchX = e.touches[0].clientX - rect.left;
          const touchY = e.touches[0].clientY - rect.top;
          
          // オフセットを保存（ハンドル内のどの位置をタッチしたかを記録）
          interactionRef.current.dragOffset = {
            x: touchX - cornerX,
            y: touchY - cornerY
          };
          
          // GTPの境界判定と相対位置を保存
          const gtpBoundaryChecks: { [gtpId: string]: { nearRight: boolean; nearBottom: boolean } } = {};
          const gtpRelativePositions: { [gtpId: string]: { relativeToRight?: number; relativeToBottom?: number } } = {};
          const referencingGtps = pouchesRef.current.filter(pouch => 
            pouch.isGroupTotalPouch && pouch.referencedGroupId === targetId
          );
          
          referencingGtps.forEach(gtp => {
            const boundaryCheck = isGtpNearGroupBoundary(gtp, group);
            gtpBoundaryChecks[gtp.id] = boundaryCheck;
            
            // 相対位置を保存
            const relativePositions: { relativeToRight?: number; relativeToBottom?: number } = {};
            if (boundaryCheck.nearRight) {
              relativePositions.relativeToRight = gtp.x - bounds.right;
            }
            if (boundaryCheck.nearBottom) {
              relativePositions.relativeToBottom = gtp.y - bounds.bottom;
            }
            gtpRelativePositions[gtp.id] = relativePositions;
          });
          
          interactionRef.current.gtpBoundaryChecks = gtpBoundaryChecks;
          interactionRef.current.gtpRelativePositions = gtpRelativePositions;
        }
      }
    }
    
  }, [calculateDistance, groups, isGroupPhysicallyContained, pouchesRef, pouches, startPouchDrag, isTouchDevice, isGtpNearGroupBoundary, isDrawingGroup]);
  
  // タッチ移動ハンドラー
  const handleTouchMove = useCallback((e: TouchEvent) => {
    // タッチデバイスでなければ処理しない
    if (!isTouchDeviceRef.current) {
      return;
    }
    
    // グローバルなドラッグ状態がある場合のみ、アクティブシートチェックを行う
    if (globalInteractionRef?.current) {
      // 現在のシートがアクティブでない場合は処理しない
      if (globalInteractionRef.current.activeSheetId !== activeSheetId) {
        // 非アクティブシートのイベントリスナーを削除
        if (eventListenersRef.current.touchmove) {
          document.removeEventListener('touchmove', handleTouchMove);
          eventListenersRef.current.touchmove = false;
        }
        if (eventListenersRef.current.touchend) {
          document.removeEventListener('touchend', handleTouchEnd);
          eventListenersRef.current.touchend = false;
        }
        return;
      }
    }
    
    const interaction = interactionRef.current;
    
    // タッチダウンされていない場合は何もしない
    if (!interaction.startTime || !interaction.isTouchEvent) {
      
      return;
    }

    // 2本指パン: 中点の移動でウィンドウをスクロール（1本指はグループ描画のためブロックされている）
    if (e.touches.length >= 2) {
      e.preventDefault();
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const midX = (t0.clientX + t1.clientX) / 2;
      const midY = (t0.clientY + t1.clientY) / 2;
      const prevMid = twoFingerMidRef.current;
      if (prevMid) {
        window.scrollBy(prevMid.x - midX, prevMid.y - midY);
        requestAnimationFrame(() => {
          bumpScrollEdgeBufferIfNearViewportEdge();
        });
      }
      twoFingerMidRef.current = { x: midX, y: midY };
      interaction.isTwoFingerPan = true;
      interaction.currentPosition = { x: midX, y: midY };
      return;
    }

    if (interaction.isTwoFingerPan) {
      twoFingerMidRef.current = null;
      e.preventDefault();
      return;
    }

    // デフォルトのスクロール動作を防止
    e.preventDefault();
    
    // 現在位置を更新
    if (e.touches.length > 0) {
      interaction.currentPosition = { 
        x: e.touches[0].clientX, 
        y: e.touches[0].clientY 
      };
      if (shouldApplyDragEdgeAutoScroll(interaction)) {
        applyDragEdgeAutoScroll(
          e.touches[0].clientX,
          e.touches[0].clientY,
          dragEdgeScrollLayoutRef.current,
          () => {
            requestAnimationFrame(() => {
              bumpScrollEdgeBufferIfNearViewportEdge();
            });
          },
        );
      }
    }
    
    // 開始位置からの距離を計算
    const distance = calculateDistance(interaction.startPosition, interaction.currentPosition);
    
    // ドラッグ判定のしきい値を超えた場合（モバイル用のしきい値を使用）
    if (distance > TAP_DISTANCE_THRESHOLD && interaction.type === null || interaction.type === 'LONG_PRESS') {
      // ターゲットの種類によって異なるドラッグタイプを設定
      if (interaction.targetType === 'group') {
        // タッチ版では常にグループドラッグ（修飾キーは使用しない）
        interaction.type = 'GROUP_DRAG';
        interaction.isShiftPressed = (window as any).__shiftKeyPressed || false;
        
        // グループドラッグ開始時に_isDraggingフラグを設定（グループ、所属パウチ、内包グループ全て）
        const currentGroups = [...groupsRef.current];
        const targetGroup = currentGroups.find(g => g.id === interaction.targetId);
        
        // まず全フラグをクリア（前回の残留フラグを確実に削除）
        const clearedGroups = currentGroups.map(g => ({
          ...g,
          _isDragging: false,
          _isCloning: false
        }));
        const clearedPouches = pouchesRef.current.map(p => ({
          ...p,
          _isDragging: false,
          _isCloneSource: false,
          _isCloning: false
        }));
        
        // 内包グループを検出（複製後の場合はinteraction.containedGroupsを使用）
        let containedGroupIds: string[];
        if (interaction.isCloning && interaction.containedGroups && interaction.containedGroups.length > 0) {
          // 複製後の場合は、既に記録されている内包グループIDを使用
          containedGroupIds = interaction.containedGroups.map(cg => cg.id);
        } else {
          // 通常のドラッグ開始の場合は物理的な検出を行う
          containedGroupIds = currentGroups
            .filter(g => g.id !== interaction.targetId && isGroupPhysicallyContained(g.id, interaction.targetId!, currentGroups))
            .map(g => g.id);
        }
        
        
        // クリア後、必要なフラグのみを設定
        const updatedGroups = clearedGroups.map(g => {
          if (g.id === interaction.targetId || containedGroupIds.includes(g.id)) {
            return {
              ...g,
              _isDragging: true
            };
          }
          return g;
        });
        
        // 所属パウチにも_isDraggingフラグを設定（Shiftキーが押されていない場合のみ）
        const updatedPouches = clearedPouches.map(p => {
          if (!interaction.isShiftPressed && targetGroup?.pouches?.includes(p.id)) {
            return {
              ...p,
              _isDragging: true
            };
          }
          return p;
        });
        
        groupsRef.current = updatedGroups;
        pouchesRef.current = updatedPouches;
        setGroups(updatedGroups);
        setPouches(updatedPouches);
        
        // DOM要素の見た目を更新
        const groupElement = document.querySelector(`[data-group-id="${interaction.targetId}"]`);
        if (groupElement) {
          (groupElement as HTMLElement).classList.add('group-dragging');
        }
      } 
      else if (interaction.targetType === 'resize-handle') {
        interaction.type = 'GROUP_RESIZE';
        
        // リサイズ中もグループタイトルのイベントを無効化するためクラスを追加
        const resizeGroupElement = document.querySelector(`[data-group-id="${interaction.targetId}"]`);
        if (resizeGroupElement) {
          (resizeGroupElement as HTMLElement).classList.add('group-dragging');
        }
      }
      else if (interaction.isLongPressDetected) {
        interaction.type = 'LONG_PRESS_DRAG';
        
        // 段階変更のタイマーをクリア
        if (interaction.phaseTimers) {
          interaction.phaseTimers.forEach(timerId => {
            clearTimeout(timerId);
          });
          interaction.phaseTimers = [];
        }
        
        // ドラッグ開始時にchanging-clone-modeクラスを削除
        if (interaction.targetId) {
          const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
          if (clonedPouchElement) {
            clonedPouchElement.classList.remove('changing-clone-mode');
          }
        }
      }
      else {
        interaction.type = 'DRAG';
        
        // 段階変更のタイマーをクリア
        if (interaction.phaseTimers) {
          interaction.phaseTimers.forEach(timerId => {
            clearTimeout(timerId);
          });
          interaction.phaseTimers = [];
        }
        
        // ドラッグ開始時にchanging-clone-modeクラスを削除
        if (interaction.targetId) {
          const clonedPouchElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`);
          if (clonedPouchElement) {
            clonedPouchElement.classList.remove('changing-clone-mode');
          }
        }
        
        // ドラッグ開始時に_isDraggingフラグを設定
        if (interaction.targetType === 'pouch' && interaction.targetId) {
          
          // ドラッグ判定時にパウチの値を元に戻す
          const pouchRef = pouchRefs.current.get(interaction.targetId);
          if (pouchRef?.current?.revertToOriginalValues) {
            pouchRef.current.revertToOriginalValues();
          }
          
          // 最新のパウチ配列を取得
          const currentPouches = [...pouchesRef.current];
          
          // _isDraggingフラグを設定
          const updatedPouches = currentPouches.map(p => {
            if (p.id === interaction.targetId) {
              return {
                ...p,
                _isDragging: true
              };
            }
            return p;
          });
          setPouches(updatedPouches);
          // パウチ配列を更新（ステート更新は行わない - 直接refを更新）
          pouchesRef.current = updatedPouches;
        }
      }
      
      // 長押しタイマーがあればクリア
      if (interaction.longPressTimer) {
        window.clearTimeout(interaction.longPressTimer);
        interaction.longPressTimer = null;
      }
      
      // 計算エリアでのドラッグ開始でグループ描画を開始（グループやリサイズハンドルではなく、パウチでもない場合）
      if (!interaction.targetId && !interaction.targetType && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const startX = interaction.startPosition.x - rect.left;
        const startY = interaction.startPosition.y - rect.top;
        
        // グループ描画開始
        const start = startGroupDrawing(startX, startY);
        
        // インタラクション状態に保存
        interaction.isDrawingGroup = true;
        interaction.groupStartPoint = start;
        
        // UIステートも更新
        setIsDrawingGroup(true);
        setGroupStartPoint(start);
      }
    }
    
    // ドラッグ中は body にクラスを追加してツールチップのちらつきを防止
    if (interaction.type === 'DRAG' || interaction.type === 'GROUP_DRAG' || interaction.type === 'LONG_CLICK_DRAG' || interaction.type === 'LONG_PRESS_DRAG') {
      if (!document.body.classList.contains('is-element-dragging')) {
        document.body.classList.add('is-element-dragging');
      }
    }

    // ドラッグ中の処理
    if (interaction.type === 'DRAG') {
      
      // パウチドラッグの処理
      
      
      
      
      if (interaction.targetType === 'pouch' && interaction.targetId && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const x = e.touches[0].clientX - rect.left;
        const y = e.touches[0].clientY - rect.top;
        
        // Option/Altキーが押されている場合（モバイルでは該当なし）を考慮してマウスと同様の処理
        // ポインタ位置＋ドラッグオフセットでパウチ位置を更新
        const newX = x + interaction.dragOffset.x;
        const newY = y + interaction.dragOffset.y;
        
        // パウチの位置更新
        updatePouchPosition(interaction.targetId, newX, newY);
      }
      // グループ描画中の処理（マウスと同様の処理でタッチでもグループを描画できるようにする）
      else if (interaction.isDrawingGroup && interaction.groupStartPoint && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const currentX = e.touches[0].clientX - rect.left;
        const currentY = e.touches[0].clientY - rect.top;
        
        // グループパスを更新
        const path = continueGroupDrawing(interaction.groupStartPoint, currentX, currentY);
        interaction.currentGroupPath = path;
        
        // pendingGroupを更新
        setPendingGroup(path);
      }
    }
    // 長押しドラッグ中の処理
    else if (interaction.type === 'LONG_PRESS_DRAG') {
      
      // パウチの長押しドラッグの処理（複製機能）
      // 長押し時点で既に複製されている場合はこのステップをスキップ
      if (interaction.targetType === 'pouch' && interaction.targetId && areaRef.current && !interaction.isCloning) {
        interaction.isCloning = true;
        const rect = areaRef.current.getBoundingClientRect();
        const x = e.touches[0].clientX - rect.left;
        const y = e.touches[0].clientY - rect.top;
        // 複製元のパウチの元の位置を保存
        const sourceElement = document.querySelector(`[data-pouch-id="${interaction.targetId}"]`) as HTMLElement;
        const originalX = sourceElement ? parseFloat(sourceElement.style.left) : x;
        const originalY = sourceElement ? parseFloat(sourceElement.style.top) : y;
        // 最新のパウチ配列から複製元のパウチを探す
        const currentPouches = pouchesRef.current;
        const sourcePouch = currentPouches.find(p => p.id === interaction.targetId);
        if (sourcePouch) {
          const newPouch: PouchWithDrag = {
            ...sourcePouch,
            id: uuidv4(),
            x: originalX,
            y: originalY,
            _isDragging: true,
            _isCloneSource: false,
            _isCloning: true
          };
          // 複製元のパウチに複製元フラグを設定
          const updatedPouches = currentPouches.map(p => {
            if (p.id === interaction.targetId) {
              return {
                ...p,
                _isCloneSource: true
              } as PouchWithDrag;
            }
            return p;
          });
          updatedPouches.push(newPouch);
          setPouches(updatedPouches);
          pouchesRef.current = updatedPouches;
          interaction.targetId = newPouch.id;
        }
      }
      
      // 複製されたパウチまたはドラッグ中のパウチの位置を更新
      if (interaction.targetType === 'pouch' && interaction.targetId && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const x = e.touches[0].clientX - rect.left;
        const y = e.touches[0].clientY - rect.top;
        
        // ポインタ位置＋ドラッグオフセットでパウチ位置を更新
        const newX = x + interaction.dragOffset.x;
        const newY = y + interaction.dragOffset.y;
        
        // パウチの位置更新
        updatePouchPosition(interaction.targetId, newX, newY);
      }
      // グループの長押しドラッグ処理
      else if (interaction.targetType === 'group' && interaction.targetId && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const currentX = e.touches[0].clientX - rect.left;
        const currentY = e.touches[0].clientY - rect.top;
        
        // グループの新しい位置を計算（ドラッグオフセットを考慮、グリッドにスナップ）
        const newGroupX = snapToGrid(currentX - interaction.dragOffset.x);
        const newGroupY = snapToGrid(currentY - interaction.dragOffset.y);
        
        // 移動対象のグループを特定
        const bounds = interaction.clonedGroup 
          ? getPathBounds(interaction.clonedGroup.path)
          : (() => {
              const group = groupsRef.current.find(g => g.id === interaction.targetId);
              return group ? getPathBounds(group.path) : null;
            })();
        
        if (bounds) {
          // 移動距離を計算
          const deltaX = newGroupX - bounds.left;
          const deltaY = newGroupY - bounds.top;
          
          // 画面端に到達したかチェック
          const containerWidth = rect.width;
          const containerHeight = rect.height;
          
          // 画面内に収まるように調整
          const adjustedDeltaX = Math.max(0 - bounds.left, deltaX);
          const adjustedDeltaY = Math.max(0 - bounds.top, deltaY);
          
          // パフォーマンス向上のために、DOM操作だけで位置を更新
          // グループ要素全体を移動（CSSトランスフォームで）
          const groupWrapper = document.querySelector(`div.group-wrapper[data-group-id="${interaction.targetId}"]`);
          if (groupWrapper) {
            (groupWrapper as HTMLElement).style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
          }
          
          // グループに含まれるパウチを移動
          if (interaction.groupPouches && interaction.groupPouches.length > 0) {
            interaction.groupPouches.forEach(pouch => {
              const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
              if (pouchElement) {
                const newX = pouch.x + adjustedDeltaX;
                const newY = pouch.y + adjustedDeltaY;
                (pouchElement as HTMLElement).style.left = `${newX}px`;
                (pouchElement as HTMLElement).style.top = `${newY}px`;
              }
            });
          }
          
          // 内包されているグループも移動
          if (interaction.containedGroups && interaction.containedGroups.length > 0) {
            interaction.containedGroups.forEach(containedGroup => {
              const containedWrapper = document.querySelector(`div.group-wrapper[data-group-id="${containedGroup.id}"]`);
              if (containedWrapper) {
                (containedWrapper as HTMLElement).style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
              }
            });
          }
        }
      }
      
      // グループ描画中の処理
      else if (interaction.isDrawingGroup && interaction.groupStartPoint && areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        const currentX = e.touches[0].clientX - rect.left;
        const currentY = e.touches[0].clientY - rect.top;
        
        // グループパスを更新
        const path = continueGroupDrawing(interaction.groupStartPoint, currentX, currentY);
        interaction.currentGroupPath = path;
        
        // pendingGroupを更新
        setPendingGroup(path);
      }
    }
    // グループドラッグ中の処理
    else if (interaction.type === 'GROUP_DRAG' && interaction.targetId && areaRef.current) {
      const rect = areaRef.current.getBoundingClientRect();
      const currentX = e.touches[0].clientX - rect.left;
      const currentY = e.touches[0].clientY - rect.top;
      
      // グループの新しい位置を計算（ドラッグオフセットを考慮、グリッドにスナップ）
      let newGroupX = snapToGrid(currentX - interaction.dragOffset.x);
      let newGroupY = snapToGrid(currentY - interaction.dragOffset.y);
      
      // 複製かつシート間移動の場合、calc-areaの境界で制限（左と上だけ）
      if (interaction.draggedFromOtherSheet && interaction.isCloning) {
        const calculatorArea = document.querySelector('.calculator-area') as HTMLElement;
        if (calculatorArea) {
          const calcRect = calculatorArea.getBoundingClientRect();
          const areaRect = areaRef.current.getBoundingClientRect();
          
          // areaRef基準でのcalc-areaの相対位置
          const calcLeft = calcRect.left - areaRect.left;
          const calcTop = calcRect.top - areaRect.top;
          
          // calc-areaの左端・上端で制限
          newGroupX = Math.max(calcLeft, newGroupX);
          newGroupY = Math.max(calcTop, newGroupY);
        }
      }
      
      // 移動対象のグループを特定
      const bounds = interaction.clonedGroup 
        ? getPathBounds(interaction.clonedGroup.path)
        : (() => {
            const group = groupsRef.current.find(g => g.id === interaction.targetId);
            return group ? getPathBounds(group.path) : null;
          })();
      
      if (bounds) {
        // 移動距離を計算
        const deltaX = newGroupX - bounds.left;
        const deltaY = newGroupY - bounds.top;
        
        // 画面端に到達したかチェック
        const containerWidth = rect.width;
        const containerHeight = rect.height;
        const groupWidth = bounds.right - bounds.left;
        const groupHeight = bounds.bottom - bounds.top;
        
        // 画面内に収まるように調整（累積移動量を考慮）
        let adjustedDeltaX: number, adjustedDeltaY: number;
        
        // 複製かつシート間移動の場合、calc-areaの境界を使用
        if (interaction.draggedFromOtherSheet && interaction.isCloning) {
          const calculatorArea = document.querySelector('.calculator-area') as HTMLElement;
          if (calculatorArea) {
            const calcRect = calculatorArea.getBoundingClientRect();
            const areaRect = areaRef.current.getBoundingClientRect();
            
            // areaRef基準でのcalc-areaの相対位置
            const calcLeft = calcRect.left - areaRect.left;
            const calcTop = calcRect.top - areaRect.top;
            
            // calc-area境界での制限（左と上だけ）
            adjustedDeltaX = Math.max(calcLeft - bounds.left, deltaX);
            adjustedDeltaY = Math.max(calcTop - bounds.top, deltaY);
          } else {
            // calc-areaが見つからない場合は従来通り
            adjustedDeltaX = Math.max(0 - bounds.left, deltaX);
            adjustedDeltaY = Math.max(0 - bounds.top, deltaY);
          }
        } else {
          // 通常の場合は従来通り
          adjustedDeltaX = Math.max(0 - bounds.left, Math.min(deltaX, containerWidth - bounds.right));
          adjustedDeltaY = Math.max(0 - bounds.top, Math.min(deltaY, containerHeight - bounds.bottom));
        }
        
        // パフォーマンス向上のために、DOM操作だけで位置を更新
        // グループ要素全体を移動（CSSトランスフォームで）
        const groupWrapper = document.querySelector(`div.group-wrapper[data-group-id="${interaction.targetId}"]`);
        if (groupWrapper) {
          (groupWrapper as HTMLElement).style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
        }
        
        // グループに含まれるパウチを移動
        if (interaction.groupPouches && interaction.groupPouches.length > 0) {
          interaction.groupPouches.forEach(pouch => {
            const pouchElement = document.querySelector(`[data-pouch-id="${pouch.id}"]`);
            if (pouchElement) {
              const newX = pouch.x + adjustedDeltaX;
              const newY = pouch.y + adjustedDeltaY;
              (pouchElement as HTMLElement).style.left = `${newX}px`;
              (pouchElement as HTMLElement).style.top = `${newY}px`;
            }
          });
        }
        
        // 内包されているグループも移動
        if (interaction.containedGroups && interaction.containedGroups.length > 0) {
          interaction.containedGroups.forEach(containedGroup => {
            const containedWrapper = document.querySelector(`div.group-wrapper[data-group-id="${containedGroup.id}"]`);
            if (containedWrapper) {
              (containedWrapper as HTMLElement).style.transform = `translate(${adjustedDeltaX}px, ${adjustedDeltaY}px)`;
            }
          });
        }
      }
    }
    // グループリサイズ中の処理を追加
    else if (interaction.type === 'GROUP_RESIZE' && interaction.targetId && areaRef.current) {
      const rect = areaRef.current.getBoundingClientRect();
      const currentX = e.touches[0].clientX - rect.left;
      const currentY = e.touches[0].clientY - rect.top;
      
      // リサイズ処理を実行（groupInitialBoundsがundefinedの場合はnullとして扱う）
      resizeGroup(
        interaction.targetId,
        interaction.groupInitialBounds || null,
        currentX,
        currentY,
        interaction.dragOffset.x,
        interaction.dragOffset.y
      );
    }
  }, [updatePouchPosition, calculateDistance, resizeGroup, pouches, groups, maxPouchZIndex, maxGroupZIndex, onUpdateActiveSheet, isTouchDevice]);
  
  // タッチ終了ハンドラー
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    // タッチデバイスでなければ処理しない
    if (!isTouchDeviceRef.current) {
      return;
    }
    // e.preventDefault();
    
    const interaction = interactionRef.current;

    // 2本指パン終了（グループ確定などは行わない）
    if (interaction.isTwoFingerPan) {
      twoFingerMidRef.current = null;
      interaction.isTwoFingerPan = false;
      if (eventListenersRef.current.touchend) {
        document.removeEventListener('touchend', handleTouchEnd);
        eventListenersRef.current.touchend = false;
      }
      if (eventListenersRef.current.touchmove) {
        document.removeEventListener('touchmove', handleTouchMove);
        eventListenersRef.current.touchmove = false;
      }
      interaction.startTime = 0;
      interaction.isTouchEvent = false;
      document.body.classList.remove('is-element-dragging');
      resetScrollEdgeBufferWithTransition();
      return;
    }
    
    // touchend と touchmove イベントリスナーを削除
    if (eventListenersRef.current.touchend) {
      document.removeEventListener('touchend', handleTouchEnd);
      eventListenersRef.current.touchend = false;
    }
    if (eventListenersRef.current.touchmove) {
      document.removeEventListener('touchmove', handleTouchMove);
      eventListenersRef.current.touchmove = false;
    }
    
    // グローバルなドラッグ状態がある場合のみ、アクティブシートチェックを行う
    if (globalInteractionRef?.current) {
      // 現在のシートがアクティブでない場合は処理しない
      if (globalInteractionRef.current.activeSheetId !== activeSheetId) {
        return;
      }
      // グローバル状態もクリア
      globalInteractionRef.current = null;
    }
    
    // タッチスタートされていない場合や、タッチイベントでない場合は何もしない
    if (!interaction.startTime || !interaction.isTouchEvent) {
      return;
    }

    try {
    
    // 段階変更のタイマーをクリア
    if (interaction.phaseTimers) {
      interaction.phaseTimers.forEach(timerId => {
        clearTimeout(timerId);
      });
      interaction.phaseTimers = [];
    }
    
    // 旧タイマーもクリア（互換性のため）
    if (interaction.multiplierChangeTimer1) {
      clearTimeout(interaction.multiplierChangeTimer1);
      interaction.multiplierChangeTimer1 = undefined;
    }
    if (interaction.multiplierChangeTimer2) {
      clearTimeout(interaction.multiplierChangeTimer2);
      interaction.multiplierChangeTimer2 = undefined;
    }
    
    // 最終タッチ位置
    let finalX = interaction.currentPosition.x;
    let finalY = interaction.currentPosition.y;
    
    if (e.changedTouches && e.changedTouches.length > 0) {
      finalX = e.changedTouches[0].clientX;
      finalY = e.changedTouches[0].clientY;
      
      interaction.currentPosition = { x: finalX, y: finalY };
    }
    
    const distance = calculateDistance(interaction.startPosition, interaction.currentPosition);
    
    // 操作完了フラグ - 実際にグループやパウチが更新されたかを追跡
    let operationCompleted = false;
    
    
    // 各種操作タイプの判別と処理
    if (interaction.type === 'DRAG' || interaction.type === 'LONG_PRESS_DRAG') {
      // パウチのドラッグ終了時
      if (interaction.targetType === 'pouch' && interaction.targetId) {
        // パウチの再補正処理を実行
        const pouchRef = pouchRefs.current.get(interaction.targetId);
        if (pouchRef?.current?.handleMouseUp) {
          pouchRef.current.handleMouseUp();
        }
        
        // 更新ロックをかける
        lockUpdates();
        
        // 最新のパウチデータを取得
        const currentPouches = [...pouchesRef.current];
        
        // ドラッグされたパウチの位置を更新
        let updatedPouches = currentPouches.map(p => {
          if (p.id === interaction.targetId) {
            const finalX = tempPouchPositionRef.current?.x || p.x;
            const finalY = tempPouchPositionRef.current?.y || p.y;
            return {
              ...p,
              x: finalX,
              y: finalY,
              _isDragging: false,
              _isCloneSource: false,
              _isCloning: false,
              _isMoving: true, // 位置移動フラグをオン
              zIndex: maxPouchZIndex + 1
            };
          }
          return p;
        });
        
        // GTPの位置が一時的に保存されている場合は、それも更新に含める
        if (tempPouchPositionRef.current && tempPouchPositionRef.current.id !== interaction.targetId) {
          const tempPosition = tempPouchPositionRef.current;
          updatedPouches = updatedPouches.map(p => {
            if (p.id === tempPosition.id) {
              // 実際のパウチデータと異なる場合のみ更新
              if (p.x !== tempPosition.x || p.y !== tempPosition.y) {
                return {
                  ...p,
                  x: tempPosition.x,
                  y: tempPosition.y
                };
              }
            }
            return p;
          });
        }
        
        // マージ処理をチェック（複製中でも相手がcloneSourceでなければマージ可能）
        let isMerged = false; // マージが実行されたかどうかのフラグ
        const draggedPouch = updatedPouches.find(p => p.id === interaction.targetId);
        if (draggedPouch) {
          // processPouchMerge関数を使用してマージ処理を実行
          const mergeResult = processPouchMerge(draggedPouch, updatedPouches);
          if (mergeResult.isMerged) {
            updatedPouches = mergeResult.pouches;
            isMerged = true;
          }
        }
        
        // 一時的な位置情報をクリア
        tempPouchPositionRef.current = null;
        
        // 移動フラグがついたパウチ数をログに出力
        const movingPouches = updatedPouches.filter(p => p._isMoving);
        
        // maxPouchZIndexを更新
        const newMaxZIndex = maxPouchZIndex + 1;
        setMaxPouchZIndex(newMaxZIndex);
        
        // 統合更新APIを使用（パウチだけ更新）
        
        const currentGroups = groupsRef.current;
        // 複製フラグとマージフラグに基づいて適切なアクションタイプを設定
        let actionType: HistoryActionType;
        if (interaction.isCloning) {
          actionType = HistoryActionType.CLONE_POUCH;
        } else if (isMerged) {
          actionType = HistoryActionType.MERGE_POUCH;
        } else {
          actionType = HistoryActionType.MOVE_POUCH;
        }
        onUpdateActiveSheet(updatedPouches, currentGroups, true, true, undefined, actionType);
        
        // 複製フラグをリセット
        interaction.isCloning = false;
        
        operationCompleted = true;
      }
      // 通常ドラッグでのグループ作成処理（長押しではなく通常ドラッグでも作成できるようにする）
      else if (!interaction.targetId && interaction.isDrawingGroup && interaction.groupStartPoint && areaRef.current) {
        // 更新ロックをかける
        lockUpdates();
        
        const rect = areaRef.current.getBoundingClientRect();
        const endX = finalX - rect.left;
        const endY = finalY - rect.top;
        
        // 最小サイズチェック
        const isTooSmall = (() => {
          if (!interaction.currentGroupPath) return true;
          const bounds = getPathBounds(interaction.currentGroupPath);
          return bounds ? 
            (Math.min(bounds.right - bounds.left, bounds.bottom - bounds.top) < 100 || Math.max(bounds.right - bounds.left, bounds.bottom - bounds.top) < 150) : 
            true;
        })();
        
        // 最小サイズ以下の場合はグループを作成しない
        const newGroup = isTooSmall ? null : finishGroupDrawing(
          interaction.groupStartPoint,
          endX,
          endY,
          pouches,
          maxGroupZIndex,
          lastGroupColor,
          undefined,
          false,
          groupsRef.current.length
        );
        
        if (newGroup) {
          // 最大Z-Indexを更新（z-indexはDOM操作で管理するため、固定値をインクリメント）
          const newMaxZIndex = maxGroupZIndex + 1;
          setMaxGroupZIndex(newMaxZIndex);
          
            // 最新のグループ配列を取得し、新しいグループを追加（追加アニメーション用フラグを設定）
            const newGroupWithAnimation = { ...newGroup, _isAdding: true };
            const updatedGroups = [...groupsRef.current, newGroupWithAnimation];
          
          // 最新のパウチデータを取得
          const currentPouches = pouchesRef.current;
          
          // 統合更新APIで直接更新
          
          onUpdateActiveSheet(currentPouches, updatedGroups, true, true, undefined, HistoryActionType.ADD_GROUP);
          
          // アニメーション後にフラグをリセット
          setTimeout(() => {
              const groupsWithoutAddingFlag = updatedGroups.map(g =>
                g.id === newGroup.id ? { ...g, _isAdding: false } : g
              );
              onUpdateActiveSheet(currentPouches, groupsWithoutAddingFlag, false, false);
          }, 500);
          
          operationCompleted = true;
        }
        
        // グループ描画状態をリセット
        interaction.isDrawingGroup = false;
        interaction.groupStartPoint = null;
        interaction.currentGroupPath = '';
        
        setIsDrawingGroup(false);
        setGroupStartPoint(null);
        setCurrentGroupPath('');
        setPendingGroup('');
      }
    }
    
    
    if (interaction.type === 'GROUP_DRAG') {
      
      
      // 更新ロックをかける
      lockUpdates();
      
      // グループの最上位ラッパー要素からトランスフォームを取得
      const groupWrapper = document.querySelector(`div.group-wrapper[data-group-id="${interaction.targetId}"]`);
      let deltaX = 0;
      let deltaY = 0;
      
      if (groupWrapper) {
        const transform = groupWrapper.getAttribute('style');
        if (transform) {
          const match = transform.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
          if (match) {
            deltaX = parseFloat(match[1]);
            deltaY = parseFloat(match[2]);
          }
        }
        
        // 変換をリセット
        (groupWrapper as HTMLElement).style.transform = '';
      }
      
      // 内包されているグループのtransformをリセット
      if (interaction.containedGroups && interaction.containedGroups.length > 0) {
        interaction.containedGroups.forEach(containedGroup => {
          const containedWrapper = document.querySelector(`div.group-wrapper[data-group-id="${containedGroup.id}"]`);
          if (containedWrapper) {
            (containedWrapper as HTMLElement).style.transform = '';
          }
        });
      }
      
      if (deltaX === 0 && deltaY === 0) {
        // 移動がなければ処理終了
        setTimeout(unlockUpdates, 10);
        return;
      }
      
      // グループとパウチを直接更新
      
      
      // 最新の状態を取得
      const currentPouches = pouchesRef.current;
      let currentGroups = groupsRef.current;
      
      // グループパスを新しい位置に移動し、ドラッグフラグを確実に解除
      const updatedGroups = currentGroups.map(g => {
        if (g.id === interaction.targetId) {
          const translatedPath = translatePath(g.path, deltaX, deltaY);
          return { 
            ...g, 
            path: translatedPath, 
            zIndex: maxGroupZIndex + 1,
            _isDragging: false,  // ドラッグ完了時にフラグをオフに
            _isCloning: false    // 複製・移動完了時にフラグをオフに
          };
        }
        
        // 内包グループも移動（タッチ版では常に移動）
        if (interaction.containedGroups.some(cg => cg.id === g.id)) {
          const translatedPath = translatePath(g.path, deltaX, deltaY);
          return { 
            ...g, 
            path: translatedPath,
            _isDragging: false,  // ドラッグ完了時にフラグをオフに
            _isCloning: false    // 内包グループのフラグもオフに
          };
        }
        
        // その他のグループもドラッグフラグをクリア（安全対策）
        if ((g as any)._isDragging) {
          return {
            ...g,
            _isDragging: false,
            _isCloning: false
          };
        }
        
        return g;
      });

      // グループマージ処理：同位置・同サイズの別グループがあればマージ
      const draggedGroupId = interaction.targetId;
      const draggedGroup = updatedGroups.find(g => g.id === draggedGroupId);
      let mergedGroups = updatedGroups;
      let targetGroupForMerge: Group | null = null;
      
      if (draggedGroup) {
        const draggedBounds = getPathBounds(draggedGroup.path);
        const POSITION_TOLERANCE = 30; // 位置の許容誤差（30px以内なら同じ位置とみなす）
        
        if (draggedBounds) {
          const draggedWidth = draggedBounds.right - draggedBounds.left;
          const draggedHeight = draggedBounds.bottom - draggedBounds.top;
          
          // 同位置・同サイズの別グループを探す
          for (const otherGroup of updatedGroups) {
            if (otherGroup.id === draggedGroupId) continue;
            if (otherGroup._isDeleting) continue;
            
            const otherBounds = getPathBounds(otherGroup.path);
            if (!otherBounds) continue;
            
            const otherWidth = otherBounds.right - otherBounds.left;
            const otherHeight = otherBounds.bottom - otherBounds.top;
            
            // 位置とサイズが一致するかチェック
            const isSamePosition = 
              Math.abs(draggedBounds.left - otherBounds.left) <= POSITION_TOLERANCE &&
              Math.abs(draggedBounds.top - otherBounds.top) <= POSITION_TOLERANCE;
            const isSameSize = 
              Math.abs(draggedWidth - otherWidth) <= POSITION_TOLERANCE &&
              Math.abs(draggedHeight - otherHeight) <= POSITION_TOLERANCE;
            
            if (isSamePosition && isSameSize) {
              targetGroupForMerge = otherGroup;
              break;
            }
          }
        }
        
        // マージ対象が見つかった場合
        if (targetGroupForMerge) {
          // ドラッグしたグループのパウチを、マージ先グループに移動
          const draggedGroupPouches = draggedGroup.pouches || [];
          const targetGroupPouches = targetGroupForMerge.pouches || [];
          const mergedPouches = [...targetGroupPouches, ...draggedGroupPouches.filter(pId => !targetGroupPouches.includes(pId))];
          
          // グループを更新：マージ先にパウチを追加し、ドラッグしたグループを削除
          mergedGroups = updatedGroups
            .filter(g => g.id !== draggedGroupId) // ドラッグしたグループを削除
            .map(g => {
              if (g.id === targetGroupForMerge!.id) {
                return { ...g, pouches: mergedPouches };
              }
              return g;
            });
        }
      }

      setGroups(mergedGroups);
      // パウチ位置を移動し、ドラッグフラグを確実に解除
      // また、グループマージが発生した場合はGTPのreferencedGroupIdを更新
      const updatedPouches = currentPouches.map(pouch => {
        const groupPouch = interaction.groupPouches?.find(p => p.id === pouch.id);
        let updatedPouch = pouch;
        
        if (groupPouch) {
          const newX = groupPouch.x + deltaX;
          const newY = groupPouch.y + deltaY;
          updatedPouch = { ...pouch, x: newX, y: newY, _isDragging: false, _isCloneSource: false, _isCloning: false };
        } else if ((pouch as any)._isDragging) {
          // その他のパウチもドラッグフラグをクリア（安全対策）
          updatedPouch = {
            ...pouch,
            _isDragging: false,
            _isCloneSource: false,
            _isCloning: false
          };
        }
        
        // グループマージが発生した場合、ドラッグしたグループを参照しているGTPのreferencedGroupIdを更新
        if (targetGroupForMerge && updatedPouch.isGroupTotalPouch && updatedPouch.referencedGroupId === draggedGroupId) {
          updatedPouch = { ...updatedPouch, referencedGroupId: targetGroupForMerge.id };
        }
        
        return updatedPouch;
      });
      
      // Z-indexも更新
      setMaxGroupZIndex(prev => prev + 1);
      
      // 移動したパウチのマージ処理
      let finalPouches = updatedPouches;
      {
        // 移動したパウチのリストを取得
        const movedPouches = updatedPouches.filter(pouch => {
          const groupPouch = interaction.groupPouches?.find(p => p.id === pouch.id);
          return groupPouch;
        });
        
        // 各移動パウチに対してマージ処理を実行
        let hasMerges = true;
        let processedPouches = finalPouches;
        
        while (hasMerges) {
          hasMerges = false;
          const currentMovedPouches = processedPouches.filter(pouch => {
            const groupPouch = interaction.groupPouches?.find(p => p.id === pouch.id);
            return groupPouch;
          });
          
          for (const movedPouch of currentMovedPouches) {
            const mergeResult = processPouchMerge(movedPouch, processedPouches);
            if (mergeResult.isMerged) {
              processedPouches = mergeResult.pouches;
              hasMerges = true;
              break; // マージが発生したら、リストを更新して再チェック
            }
          }
        }
        
        finalPouches = processedPouches;
        
        // 移動したグループに属していたパウチがすべてマージされたかチェック
        const movedGroupId = interaction.targetId;
        const originalMovedPouches = interaction.groupPouches || [];
        
        // 元々パウチが存在していた場合のみチェック（空のグループは削除しない）
        if (originalMovedPouches.length > 0) {
          // 移動したグループに属していたパウチのIDリスト
          const originalPouchIds = originalMovedPouches.map(p => p.id);
          
          // 現在のパウチリストで、元々移動したグループに属していたパウチが存在するかチェック
          const remainingOriginalPouches = finalPouches.filter(pouch => 
            originalPouchIds.includes(pouch.id)
          );
          
          // 元々移動したグループに属していたパウチがすべて削除された場合、そのグループを削除
          if (remainingOriginalPouches.length === 0) {
            // 削除対象のグループIDリスト（移動したグループ + 元々属していた内包グループ）
            const groupIdsToDelete = [movedGroupId];
            
            // 移動したグループに元々属していた内包グループも削除対象に追加
            if (interaction.containedGroups && interaction.containedGroups.length > 0) {
              const originalContainedGroupIds = interaction.containedGroups.map(cg => cg.id);
              groupIdsToDelete.push(...originalContainedGroupIds);
            }
            
            // グループを削除
            const filteredGroups = mergedGroups.filter(group => !groupIdsToDelete.includes(group.id));
            mergedGroups.splice(0, mergedGroups.length, ...filteredGroups);
          }
        }
      }
      
      // 統合APIで更新
      // 複製フラグに基づいて適切なアクションタイプを設定
      const actionType = interaction.isCloning ? HistoryActionType.CLONE_GROUP : HistoryActionType.MOVE_GROUP;
      // 移動元シートIDを取得（draggedGroupDataRefから直接取得）
      const sourceSheetId = draggedGroupDataRef?.current?.sourceSheetId;
      
      onUpdateActiveSheet(finalPouches, mergedGroups, true, true, undefined, actionType, undefined, sourceSheetId);
      
      // 複製フラグをリセット
      interaction.isCloning = false;      
      operationCompleted = true;
    }
    else if (interaction.type === 'GROUP_RESIZE') {
      // 更新ロックをかける
      lockUpdates();
      
      // 最新の状態を取得して保存
      const currentPouches = pouchesRef.current;
      const currentGroups = groupsRef.current;
      
      // グループマージ処理：同位置・同サイズの別グループがあればマージ
      const resizedGroupId = interaction.targetId;
      const resizedGroup = currentGroups.find(g => g.id === resizedGroupId);
      let mergedGroups = currentGroups;
      let finalPouches = currentPouches;
      let targetGroupForMerge: Group | null = null;
      
      if (resizedGroup) {
        const resizedBounds = getPathBounds(resizedGroup.path);
        const POSITION_TOLERANCE = 30; // 位置の許容誤差（30px以内なら同じ位置とみなす）
        
        if (resizedBounds) {
          const resizedWidth = resizedBounds.right - resizedBounds.left;
          const resizedHeight = resizedBounds.bottom - resizedBounds.top;
          
          // 同位置・同サイズの別グループを探す
          for (const otherGroup of currentGroups) {
            if (otherGroup.id === resizedGroupId) continue;
            if (otherGroup._isDeleting) continue;
            
            const otherBounds = getPathBounds(otherGroup.path);
            if (!otherBounds) continue;
            
            const otherWidth = otherBounds.right - otherBounds.left;
            const otherHeight = otherBounds.bottom - otherBounds.top;
            
            // 位置とサイズが一致するかチェック
            const isSamePosition = 
              Math.abs(resizedBounds.left - otherBounds.left) <= POSITION_TOLERANCE &&
              Math.abs(resizedBounds.top - otherBounds.top) <= POSITION_TOLERANCE;
            const isSameSize = 
              Math.abs(resizedWidth - otherWidth) <= POSITION_TOLERANCE &&
              Math.abs(resizedHeight - otherHeight) <= POSITION_TOLERANCE;
            
            if (isSamePosition && isSameSize) {
              targetGroupForMerge = otherGroup;
              break;
            }
          }
        }
        
        // マージ対象が見つかった場合
        if (targetGroupForMerge) {
          // リサイズしたグループのパウチを、マージ先グループに移動
          const resizedGroupPouches = resizedGroup.pouches || [];
          const targetGroupPouches = targetGroupForMerge.pouches || [];
          const mergedPouchIds = [...targetGroupPouches, ...resizedGroupPouches.filter(pId => !targetGroupPouches.includes(pId))];
          
          // グループを更新：マージ先にパウチを追加し、リサイズしたグループを削除
          mergedGroups = currentGroups
            .filter(g => g.id !== resizedGroupId) // リサイズしたグループを削除
            .map(g => {
              if (g.id === targetGroupForMerge!.id) {
                return { ...g, pouches: mergedPouchIds };
              }
              return g;
            });
          
          // グループマージが発生した場合、リサイズしたグループを参照しているGTPのreferencedGroupIdを更新
          finalPouches = currentPouches.map(pouch => {
            if (pouch.isGroupTotalPouch && pouch.referencedGroupId === resizedGroupId) {
              return { ...pouch, referencedGroupId: targetGroupForMerge!.id };
            }
            return pouch;
          });
        }
      }
      
      // useEffectでの古いcurrentSheetによる上書きを防ぐ
      skipCurrentSheetUpdateRef.current = true;
      
      // 最終結果をDB保存（非クリティカル更新・DB保存あり）
      onUpdateActiveSheet(finalPouches, mergedGroups, false, true, undefined, HistoryActionType.RESIZE_GROUP);
      
      operationCompleted = true;
    }
    // タップ判定（移動距離がしきい値以下）
    else if (distance <= TAP_DISTANCE_THRESHOLD) {
      
      const currentTime = Date.now();
      const timeSinceLastClick = currentTime - interaction.lastClickTime;      

      interaction.type = 'CLICK';
      
      interaction.lastClickTime = currentTime;
      const isButtonOrInButton = e.target ? 
        (e.target as HTMLElement).closest('button') !== null || 
        (e.target as HTMLElement).closest('.group-buttons') !== null || 
        (e.target as HTMLElement).closest('.pouch-color-button, .pouch-delete-button, .pouch-split-button, .pouch-toggle-sign-button') !== null ||
        (e.target as HTMLElement).closest('.group-title-input') !== null ||
        (e.target as HTMLElement).closest('.group-subtotal') !== null : 
        false;

      
      const pendingPouch = interaction.targetType === 'pending-pouch';
      // pendingPouchがあればタップして確定
      if (pendingPouch && areaRef.current) {
          
        const rect = areaRef.current.getBoundingClientRect();
        const x = finalX - rect.left;
        const y = finalY - rect.top;      
        // 既存のタイマーがあればクリア
        if (pendingTimerRef.current !== null) {
          window.clearTimeout(pendingTimerRef.current);
          pendingTimerRef.current = null;
        }        
        lockUpdates();
        const newPouch = createNewPouch(x, y);
        setPendingPouch(null);
        openValueEditWhenPouchMounted(newPouch.id);
        operationCompleted = true;
      }
      // 通常のエリアタップ（パウチ新規作成）
      else if ((!interaction.targetId && !interaction.targetType || interaction.targetType === 'group') && areaRef.current && !isButtonOrInButton) {

        
        const rect = areaRef.current.getBoundingClientRect();
        const x = finalX - rect.left;
        const y = finalY - rect.top;
        
        createPendingPouch(x, y);
        
        operationCompleted = true;
      }
    }
    else if (interaction.type === 'LONG_PRESS') {
      
      // 長押し完了処理がある場合はここに実装
    }
    
    // 操作終了共通処理
    // 長押しタイマーのクリア
    if (interaction.longPressTimer) {
      window.clearTimeout(interaction.longPressTimer);
      interaction.longPressTimer = null;
    }
    
    if(interaction.isCloning && !operationCompleted) {
      // 複製フラグとドラッグフラグを持つアイテムをフィルタリングして削除（複製元は残す）
      const updatedPouches = pouchesRef.current.filter(pouch => 
        !pouch._isDragging || pouch._isCloneSource
      );
      
      // _isCloningがtrueのグループとその記録された内包グループを除外
      const updatedGroups = groupsRef.current.filter(group => {
        // クローニング中のグループを除外
        if (group._isCloning) return false;
        
        // クローニング中のグループの内包グループとして記録されているものを除外
        const isContainedByCloning = interactionRef.current.containedGroups?.some(
          containedGroup => containedGroup.id === group.id
        );
        return !isContainedByCloning;
      });
    
      // 統合APIで更新
      onUpdateActiveSheet(updatedPouches, updatedGroups, false, false);
      
      // 参照も更新
      pouchesRef.current = updatedPouches;
      groupsRef.current = updatedGroups;
    }
    // 全てのパウチのドラッグ状態、複製元状態、複製状態をリセット（安全対策）
    const allPouches = [...pouchesRef.current];
    const resetFlagPouches = allPouches.map(p => ({
      ...p,
      _isDragging: false,
      _isCloneSource: false,
      _isCloning: false
    }));
    
    
    // パウチ状態を更新
    setPouches(resetFlagPouches);
    pouchesRef.current = resetFlagPouches;
    
    // 全てのグループのドラッグ状態と複製状態をリセット（安全対策）
    // ただしGROUP_RESIZEの場合はすでに正しい値が設定されているのでスキップ（ちらつき防止）
    if (interaction.type !== 'GROUP_RESIZE') {
      const allGroups = [...groupsRef.current];
      const resetFlagGroups = allGroups.map(g => {
        return {
          ...g,
          _isDragging: false,
          _isCloning: false
        };
      });
      
      // グループ状態を更新
      setGroups(resetFlagGroups);
      groupsRef.current = resetFlagGroups;
    }
    
    // DOM上の見た目も更新
    document.querySelectorAll('.group-dragging').forEach(el => {
      el.classList.remove('group-dragging');
    });
    
    // スキップフラグを遅延リセット（useEffectの古いcurrentSheetによる上書きを完全に防ぐ）
    // 連続操作時の競合を防ぐため、十分な時間を確保
    if (skipCurrentSheetUpdateRef.current) {
      setTimeout(() => {
        skipCurrentSheetUpdateRef.current = false;
      }, 500);
    }
    
    // タッチ操作リセット処理（delayedTouchReset関数で遅延実行）
    delayedTouchResetTimerRef.current = setTimeout(delayedTouchReset, 10);
    
    // ロックがかかっていなかった場合はここでリセット
    if (!operationCompleted && isUpdatingRef.current) {
      setTimeout(unlockUpdates, 10);
    }
    
    // 全リセット（無条件で実行）
    
    // イベントリスナーの削除（状態管理付き）
    if (eventListenersRef.current.touchmove) {
      document.removeEventListener('touchmove', handleTouchMove);
      eventListenersRef.current.touchmove = false;
    }
    if (eventListenersRef.current.touchend) {
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
      eventListenersRef.current.touchend = false;
    }
    
    // シート間ドラッグ状態をリセット
    if (onResetSheetDragState) {
      onResetSheetDragState();
    }
    } finally {
      resetScrollEdgeBufferWithTransition();
    }
  }, [delayedTouchReset, resetInteraction, createNewPouch, createPendingPouch, openValueEditWhenPouchMounted, lockUpdates, isTouchDevice, onResetSheetDragState, bumpScrollEdgeBufferIfNearViewportEdge, resetScrollEdgeBufferWithTransition]);

  
  // ダブルタップにズームを防止する関数
  const preventZoom = useCallback((e: TouchEvent) => {
    // マルチタッチは処理しない（ピンチ操作を許可）
    if (e.touches.length > 1) return;
    
    // ダブルタップでのズームを防止
    e.preventDefault();
  }, []);

  // ダブルタップズーム防止のイベントリスナー設定
  useEffect(() => {
    // ズーム防止用イベントリスナーを追加
    document.addEventListener('touchend', preventZoom, { passive: false });
    
    return () => {
      // クリーンアップ
      document.removeEventListener('touchend', preventZoom);
    };
  }, [preventZoom]);

  // 統一されたイベントリスナー登録関数（グローバル追跡付き）
  const addEventListeners = useCallback(() => {
    // グローバルなリスナー配列を初期化（存在しない場合）
    if (!(document as any)._mouseMoveListeners) (document as any)._mouseMoveListeners = [];
    if (!(document as any)._mouseUpListeners) (document as any)._mouseUpListeners = [];
    if (!(document as any)._touchMoveListeners) (document as any)._touchMoveListeners = [];
    if (!(document as any)._touchEndListeners) (document as any)._touchEndListeners = [];
    
    if (!eventListenersRef.current.mousemove) {
      document.addEventListener('mousemove', handleMouseMove);
      (document as any)._mouseMoveListeners.push(handleMouseMove);
      eventListenersRef.current.mousemove = true;
    }
    
    if (!eventListenersRef.current.mouseup) {
      document.addEventListener('mouseup', handleMouseUp);
      (document as any)._mouseUpListeners.push(handleMouseUp);
      eventListenersRef.current.mouseup = true;
    }
    
    if (!eventListenersRef.current.touchmove) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      (document as any)._touchMoveListeners.push(handleTouchMove);
      eventListenersRef.current.touchmove = true;
    }
    
    if (!eventListenersRef.current.touchend) {
      document.addEventListener('touchend', handleTouchEnd);
      (document as any)._touchEndListeners.push(handleTouchEnd);
      eventListenersRef.current.touchend = true;
    }
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd, activeSheetId]);

  // 統一されたイベントリスナー削除関数
  const removeEventListeners = useCallback(() => {
    if (eventListenersRef.current.mousemove) {
      document.removeEventListener('mousemove', handleMouseMove);
      eventListenersRef.current.mousemove = false;
    }
    
    if (eventListenersRef.current.mouseup) {
      document.removeEventListener('mouseup', handleMouseUp);
      eventListenersRef.current.mouseup = false;
    }
    
    if (eventListenersRef.current.touchmove) {
      document.removeEventListener('touchmove', handleTouchMove);
      eventListenersRef.current.touchmove = false;
    }
    
    if (eventListenersRef.current.touchend) {
      document.removeEventListener('touchend', handleTouchEnd);
      eventListenersRef.current.touchend = false;
    }
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);
  
  // ドラッグ状態を復元する関数
  const restoreDragState = useCallback((preservedState: {
    interaction: any;
    draggedFromOtherSheet: boolean;
    draggedGroupId?: string;
    tempPouchPosition?: { x: number; y: number };
  }) => {
    // interactionRefに保存された状態を復元
    interactionRef.current = preservedState.interaction;
    
    // 一時的なパウチ位置を復元
    if (preservedState.tempPouchPosition) {
      tempPouchPositionRef.current = {
        id: preservedState.interaction.targetId || '',
        x: preservedState.tempPouchPosition.x,
        y: preservedState.tempPouchPosition.y
      };
    }
    
    // ドラッグ状態復元後、イベントハンドラーを再設定
    if (preservedState.interaction.type === 'DRAG' || preservedState.interaction.type === 'GROUP_DRAG') {
      // マウス/タッチイベントリスナーを追加
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);
  
  // 右クリックメニューの抑制
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      // デフォルトの右クリックメニューを抑制
      e.preventDefault();
    };
    
    areaRef.current?.addEventListener('contextmenu', handleContextMenu);
    
    return () => {
      areaRef.current?.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  
  // シート切り替え時に状態を更新
  useEffect(() => {
        // activeSheetIdが変わったときだけ初期値で状態をリセット
        if (activeSheetId !== prevActiveSheetIdRef.current) {
      
      
      // シート切替フラグを設定
      isSheetSwitchingRef.current = prevActiveSheetIdRef.current !== null;


      // グループサブトータルにもアニメーションフラグを追加し、ドラッグフラグをクリア
      const initialGroupsWithAnimation = initialGroups?.map(group => {
        return {
          ...group,
          _animateFromZero: isInitialLoading || isSheetSwitchingRef.current, // シート切替時のアニメーションフラグ
          _isDragging: false, // シート切替時にドラッグフラグをクリア
          _isCloning: false   // 複製フラグもクリア
        };
      }) || [];

      // パウチにもアニメーションフラグを追加し、ドラッグフラグをクリア
      const initialPouchesWithAnimation = (initialPouches || []).map(pouch => ({
        ...pouch,
        _animateFromZero: isInitialLoading || isSheetSwitchingRef.current, // シート切替時のアニメーションフラグ
        _animateMultiplier: false,
        _isDragging: false,    // シート切替時にドラッグフラグをクリア
        _isCloning: false,     // 複製フラグもクリア
        _isCloneSource: false  // 複製元フラグもクリア
      }));

      // パウチとグループのアニメーション状態を設定してローカル状態を更新
      setPouches(initialPouchesWithAnimation);
      setGroups(initialGroupsWithAnimation);
      
      // アニメーション開始後にApp側の状態も更新（DB保存なし）
      // クリティカル更新（アニメーション用）・DB保存なし
      onUpdateActiveSheet(initialPouchesWithAnimation, initialGroupsWithAnimation, true, false);
      
      prevActiveSheetIdRef.current = activeSheetId;

      // シート切替フラグをリセット（少し遅延させる）
      setTimeout(() => {
        isSheetSwitchingRef.current = false;
      }, 100);
    }
    
    // maxPouchZIndexの初期化
    if (initialPouches && initialPouches.length > 0) {
      const currentMaxZIndex = Math.max(
        ...initialPouches.map(p => p.zIndex || MIN_POUCH_Z_INDEX),
        MIN_POUCH_Z_INDEX
      );
      setMaxPouchZIndex(currentMaxZIndex);
    } else {
      setMaxPouchZIndex(MIN_POUCH_Z_INDEX);
    }
  }, [activeSheetId]);
  
  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (pendingTimerRef.current !== null) {
        window.clearTimeout(pendingTimerRef.current);
      }
    };
  }, []);
  
  // pendingポウチのタッチスタート処理（バブリング防止のみ）
  const handlePendingPouchTouchStart = useCallback((e: React.TouchEvent<Element>) => {
    e.stopPropagation();
    handleTouchStart(e, pendingPouch?.id, 'pending-pouch');
  }, [handleTouchStart, pendingPouch]);
  
  // 乗数更新ハンドラー
  const handleUpdateMultiplier = useCallback((pouchId: string, multiplier: number, forceNewEntry?: boolean, roundUnitOverride?: number | null) => {
    const currentPouch = pouchesRef.current.find(p => p.id === pouchId);
    if (!currentPouch) return;

    const lid = currentSheet ? resolveActiveLayerId(currentSheet) : undefined;
    const useLayerValues = !!(currentSheet && pouchUsesLayerValues(currentSheet, currentPouch));
    const currentRoundUnit =
      useLayerValues && lid
        ? getPouchLayerRoundUnit(currentPouch, lid)
        : currentPouch.roundUnit;
    const valueForClamp =
      useLayerValues && lid && !currentPouch.isGroupTotalPouch
        ? getLayerValue(currentPouch, lid)
        : currentPouch.value;

    const isTimeLayer = !!(lid && getPouchLayerIsTime(currentPouch, lid));
    const currentMult =
      isTimeLayer && lid
        ? getPouchLayerMultiplier(currentPouch, lid)
        : currentPouch.multiplier;

    const multChanged = currentMult !== multiplier;
    const roundOnly = roundUnitOverride !== undefined && !multChanged;
    const multForRoundCap = multChanged ? multiplier : currentMult;

    // 多層時間: top-level multiplier（数量 GTP 按分）を layerValues に書かない
    if (
      multChanged &&
      isTimeLayer &&
      useLayerValues &&
      multiplier === currentPouch.multiplier &&
      multiplier !== currentMult
    ) {
      if (currentPouch._animateMultiplier) {
        const cleared = pouchesRef.current.map(p =>
          p.id === pouchId ? { ...p, _animateMultiplier: false } : p,
        );
        setPouches(cleared);
        pouchesRef.current = cleared;
      }
      return;
    }

    const scaledCapForRound = getGroupTotalPouchRoundUnitScaledCap(
      currentPouch,
      groupsRef.current,
      pouchesRef.current,
      multForRoundCap,
      currentPouch.isGroupTotalPouch ? undefined : valueForClamp,
    );

    const nextRoundUnit = clampRoundUnitToAbsoluteCap(
      roundUnitOverride === null
        ? undefined
        : roundUnitOverride !== undefined
          ? roundUnitOverride
          : currentRoundUnit,
      scaledCapForRound,
    );

    const roundChanged = nextRoundUnit !== currentRoundUnit;
    if (!multChanged && !roundChanged) return;

    const updatedPouches = pouchesRef.current.map(pouch => {
      if (pouch.id === pouchId) {
        if (useLayerValues && lid) {
          if (pouch.isGroupTotalPouch) {
            let next: Pouch = pouch;
            if (roundChanged || roundOnly) {
              next = applyPouchRoundUnitForLayer(
                next,
                lid,
                nextRoundUnit,
                multChanged ? { clearExpression: true } : undefined,
                currentSheet,
              );
            }
            if (multChanged) {
              const exprClear = {
                lastExpression: '',
                valueWhenLastExpression: undefined,
                multiplyWhenLastExpression: undefined,
                roundUnitWhenLastExpression: undefined,
              };
              next = { ...next, multiplier, ...exprClear, _animateMultiplier: false };
            } else if (roundChanged || roundOnly) {
              next = { ...next, _animateMultiplier: false };
            }
            return next;
          }
          const exprClear = multChanged
            ? {
                lastExpression: '',
                valueWhenLastExpression: undefined,
                multiplyWhenLastExpression: undefined,
              }
            : {};
          if (isTimeLayer) {
            const base = { ...pouch, ...exprClear, _animateMultiplier: false };
            return setPouchLayerValue(
              base,
              lid,
              getLayerValue(pouch, lid),
              {
                ...(multChanged ? { multiplier } : {}),
                roundUnit: nextRoundUnit,
                ...(multChanged || roundUnitOverride !== undefined
                  ? { roundUnitWhenLastExpression: undefined }
                  : {}),
              },
              currentSheet,
            );
          }
          let next = {
            ...pouch,
            ...(multChanged ? { multiplier } : {}),
            ...exprClear,
            _animateMultiplier: false,
          };
          return applyPouchRoundUnitForLayer(
            next,
            lid,
            nextRoundUnit,
            multChanged || roundUnitOverride !== undefined
              ? { clearExpression: multChanged }
              : undefined,
            currentSheet,
          );
        }
        const exprClear = roundOnly
          ? {}
          : {
              lastExpression: '',
              valueWhenLastExpression: undefined,
              multiplyWhenLastExpression: undefined,
              roundUnitWhenLastExpression: undefined,
            };
        return {
          ...pouch,
          ...(multChanged ? { multiplier } : {}),
          roundUnit: nextRoundUnit,
          ...exprClear,
          _animateMultiplier: false,
        };
      }
      return pouch;
    });
    
    setPouches(updatedPouches);
    pouchesRef.current = updatedPouches;

    // 統合APIで更新
    const currentGroups = groupsRef.current;
    onUpdateActiveSheet(updatedPouches, currentGroups, true, true, undefined, HistoryActionType.EDIT_POUCH_VALUE, forceNewEntry);
  }, [onUpdateActiveSheet, currentSheet, setPouches]);

  // 値とmultiplierを同時更新するハンドラー
  const handleUpdateValueAndMultiplier = useCallback((pouchId: string, value: number, multiplier: number, historyActionType?: HistoryActionType, forceNewEntry?: boolean, lastExpression?: string, valueWhenLastExpression?: number, multiplyWhenLastExpression?: number, isTime?: boolean, oppositeToTotal?: boolean, roundUnit?: number | null, roundUnitWhenLastExpression?: number) => {
    const currentPouch = pouchesRef.current.find(p => p.id === pouchId);
    if (!currentPouch) return;

    const lid = currentSheet ? resolveActiveLayerId(currentSheet) : undefined;
    const useLayerValues = !!(currentSheet && pouchUsesLayerValues(currentSheet, currentPouch));
    const currentRoundUnit =
      useLayerValues && lid
        ? getPouchLayerRoundUnit(currentPouch, lid)
        : currentPouch.roundUnit;

    const nextOppositeToTotal =
      oppositeToTotal !== undefined && currentPouch.isGroupTotalPouch ? oppositeToTotal : currentPouch.oppositeToTotal;

    const valueForRoundCap =
      useLayerValues && lid && !currentPouch.isGroupTotalPouch
        ? value
        : currentPouch.isGroupTotalPouch
          ? undefined
          : value;

    const scaledCapForRound = getGroupTotalPouchRoundUnitScaledCap(
      currentPouch,
      groupsRef.current,
      pouchesRef.current,
      multiplier,
      valueForRoundCap,
    );

    const nextRoundUnit = clampRoundUnitToAbsoluteCap(
      roundUnit === null
        ? undefined
        : roundUnit !== undefined
          ? roundUnit
          : currentRoundUnit,
      scaledCapForRound,
    );

    const nextRuWhenLast =
      roundUnitWhenLastExpression !== undefined
        ? roundUnitWhenLastExpression
        : roundUnit !== undefined
          ? nextRoundUnit
          : useLayerValues && lid
            ? getPouchLayerRoundUnitWhenLastExpression(currentPouch, lid)
            : currentPouch.roundUnitWhenLastExpression;

    const isTimeLayer = !!(lid && getPouchLayerIsTime(currentPouch, lid));
    const currentMult =
      isTimeLayer && lid
        ? getPouchLayerMultiplier(currentPouch, lid)
        : currentPouch.multiplier;
    const currentValue =
      currentPouch.isGroupTotalPouch
        ? currentPouch.value
        : useLayerValues && lid
          ? getLayerValue(currentPouch, lid)
          : currentPouch.value;
    const currentIsTime =
      useLayerValues && lid
        ? getPouchLayerIsTime(currentPouch, lid)
        : !!currentPouch.isTime;

    if (
      currentValue === value &&
      currentMult === multiplier &&
      currentIsTime === (isTime !== undefined ? isTime : currentIsTime) &&
      currentPouch.oppositeToTotal === nextOppositeToTotal &&
      nextRoundUnit === currentRoundUnit
    ) {
      return;
    }

    const updatedPouches = pouchesRef.current.map(pouch => {
      if (pouch.id === pouchId) {
        const newLastExpression = lastExpression !== undefined ? lastExpression : pouch.lastExpression;
        const layerId = currentSheet ? resolveActiveLayerId(currentSheet) : undefined;
        const layerIsTime = !!(layerId && getPouchLayerIsTime(pouch, layerId));
        const timeFlag = isTime !== undefined ? isTime : layerIsTime;
        const storeMultInTimeLayer = !!timeFlag;
        const effNegative = storeMultInTimeLayer ? value * multiplier < 0 : value < 0;
        const mergedOpposite =
          oppositeToTotal !== undefined && pouch.isGroupTotalPouch ? oppositeToTotal : pouch.oppositeToTotal;
        const base = {
          ...pouch,
          value,
          ...(storeMultInTimeLayer ? {} : { multiplier }),
          roundUnit: nextRoundUnit,
          isNegative: effNegative,
          isTime: timeFlag,
          oppositeToTotal: mergedOpposite,
          lastExpression: newLastExpression,
          valueWhenLastExpression: valueWhenLastExpression !== undefined ? valueWhenLastExpression : pouch.valueWhenLastExpression,
          multiplyWhenLastExpression: multiplyWhenLastExpression !== undefined ? multiplyWhenLastExpression : pouch.multiplyWhenLastExpression,
          roundUnitWhenLastExpression: nextRuWhenLast,
          _animateMultiplier: historyActionType !== HistoryActionType.EDIT_POUCH_VALUE && forceNewEntry
        };
        if (pouch.isGroupTotalPouch && currentSheet?.layers?.length && roundUnit !== undefined) {
          const layerId = resolveActiveLayerId(currentSheet);
          let next = applyPouchRoundUnitForLayer(
            pouch,
            layerId,
            nextRoundUnit,
            { roundUnitWhenLastExpression: nextRuWhenLast },
            currentSheet,
          );
          return {
            ...next,
            value,
            multiplier,
            isNegative: effNegative,
            isTime: timeFlag,
            oppositeToTotal: mergedOpposite,
            lastExpression: newLastExpression,
            valueWhenLastExpression:
              valueWhenLastExpression !== undefined
                ? valueWhenLastExpression
                : pouch.valueWhenLastExpression,
            multiplyWhenLastExpression:
              multiplyWhenLastExpression !== undefined
                ? multiplyWhenLastExpression
                : pouch.multiplyWhenLastExpression,
            _animateMultiplier:
              historyActionType !== HistoryActionType.EDIT_POUCH_VALUE && forceNewEntry,
          };
        }
        if (!pouch.isGroupTotalPouch && currentSheet?.layers?.length) {
          const layerId = resolveActiveLayerId(currentSheet);
          return setPouchLayerValue(
            base,
            layerId,
            value,
            {
              lastExpression: newLastExpression,
              valueWhenLastExpression: base.valueWhenLastExpression,
              roundUnit: nextRoundUnit,
              roundUnitWhenLastExpression: nextRuWhenLast,
              ...(storeMultInTimeLayer ? { multiplier } : {}),
              ...(isTime !== undefined ? { isTime } : {}),
            },
            currentSheet
          );
        }
        return { ...base, multiplier };
      }
      return pouch;
    });

    const currentGroups = groupsRef.current;
    onUpdateActiveSheet(
      updatedPouches,
      currentGroups,
      true,
      true,
      undefined,
      historyActionType,
      forceNewEntry
    );
  }, [onUpdateActiveSheet, currentSheet]);

  // パウチの削除ハンドラー
  const handleDeletePouch = useCallback((pouchId: string) => {
    const currentPouches = pouchesRef.current;
    const currentGroups = groupsRef.current;
    
    // 最終的な状態を計算（削除後の状態）
    const finalPouches = currentPouches.filter(p => p.id !== pouchId);
    
    // applyAnimationFlagsを使用してアニメーション用フラグを設定
    const animationResult = HistoryLogic.applyAnimationFlags(
      currentPouches,
      finalPouches,
      currentGroups,
      currentGroups
    );
    
    // 状態を更新してアニメーションを開始
    setPouches(animationResult.pouches);
    
    // 300ms後に実際の削除処理を実行
    setTimeout(() => {
      // アニメーション用フラグを除去して最終状態に更新
      const cleanFinalPouches = finalPouches.map(p => {
        const cleanPouch = { ...p };
        const pouchAny = cleanPouch as any;
        delete pouchAny._isDeleting;
        delete pouchAny._animateFromZero;
        return cleanPouch;
      });
      
      // 統合APIを呼び出し（削除済みの状態を渡す）
      onUpdateActiveSheet(cleanFinalPouches, currentGroups, true, true, undefined, HistoryActionType.DELETE_POUCH);
    }, 300);
  }, [setPouches, onUpdateActiveSheet]);

  /** パウチ分解（マージの逆）— 純マルチ >1 を N 個に分割し配置エンジンで座標付与 */
  const handleSplitPouch = useCallback((pouchId: string) => {
    const splitDebug = (step: string, detail?: Record<string, unknown>) => {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[POUCHES split]', step, detail ?? {});
      }
    };

    const abortSplit = (reason: string, detail?: Record<string, unknown>) => {
      splitDebug('abort', { reason, ...detail });
      showPouchErrorRef.current(pouchId);
    };

    splitDebug('handleSplitPouch', { pouchId });

    try {
    const currentPouches = pouchesRef.current;
    const currentGroups = groupsRef.current;
    const source = currentPouches.find(p => p.id === pouchId);
    if (!source || !onUpdateActiveSheet) {
      splitDebug('abort', {
        reason: !source
          ? 'pouch not found'
          : 'onUpdateActiveSheet missing',
      });
      if (source) {
        showPouchErrorRef.current(pouchId);
      }
      return;
    }
    if (source.isGroupTotalPouch) {
      abortSplit('group total pouch');
      return;
    }

    const canSplit = canSplitPouchByMultiplier(source, currentSheet);
    splitDebug('canSplitPouchByMultiplier', { canSplit, pouchId });
    if (!canSplit) {
      abortSplit('canSplitPouchByMultiplier false');
      return;
    }

    const scalarResult = splitPouchScalars(source, currentSheet);
    splitDebug('splitPouchScalars', scalarResult);
    if (!scalarResult.ok) {
      abortSplit('splitPouchScalars failed', { error: scalarResult.error });
      return;
    }

    bumpScrollEdgeBufferIfNearViewportEdge();

    const sourceGroup = currentGroups.find(g => g.pouches?.includes(pouchId));
    const splitGroupTitle = sourceGroup?.title?.trim() || source.title?.trim() || undefined;
    const searchInsets = computeSplitGroupInsets(false);

    const placementViewport = expandPlacementViewportForSplit(
      computePlacementViewport(
        currentPouches,
        currentGroups,
        scrollEdgeBufferRef.current
      ),
      source,
      scalarResult.scalars.length,
      searchInsets
    );
    const searchViewport = expandViewportSearchExtents(
      placementViewport,
      source,
      scalarResult.scalars.length,
      searchInsets
    );
    const siblingCount =
      sourceGroup?.pouches?.filter(id => id !== pouchId).length ?? 0;
    const isSoleMemberInGroup =
      !!sourceGroup && (sourceGroup.pouches?.length ?? 0) === 1;
    const expandedSearchViewport =
      siblingCount > 0 || isSoleMemberInGroup
        ? expandViewportForSplitRelocate(searchViewport)
        : searchViewport;
    splitDebug('viewport', {
      placementViewport,
      searchViewport: expandedSearchViewport,
      edgeBuffer: scrollEdgeBufferRef.current,
      inGroupSiblings: siblingCount,
    });

    const plan = planSplitPlacement(
      source,
      scalarResult.scalars.length,
      currentPouches,
      currentGroups,
      placementViewport,
      expandedSearchViewport
    );
    if (!plan) {
      abortSplit('planSplitPlacement returned null');
      return;
    }

    splitDebug('planSplitPlacement', {
      positionsCount: plan.positions.length,
      splitGroupBounds: plan.splitGroupBounds,
      createNewGroup: plan.createNewGroup,
      parentGroupId: plan.parentGroupId ?? null,
    });

    const newPouches = buildSplitPouches(
      source,
      scalarResult.scalars,
      plan.positions,
      maxPouchZIndex,
      currentSheet
    );

    let finalGroups: Group[];
    if (plan.createNewGroup) {
      const splitGroup = buildSplitGroup(
        source,
        newPouches.map(p => p.id),
        plan.splitGroupBounds,
        maxGroupZIndex,
        currentGroups.length,
        currentSheet,
        splitGroupTitle
      );
      splitDebug('buildSplitGroup', {
        groupId: splitGroup.id,
        memberCount: splitGroup.pouches?.length ?? 0,
      });
      finalGroups = [...plan.groups, splitGroup];
    } else {
      splitDebug('split in parent (no new group)', {
        parentGroupId: plan.parentGroupId,
      });
      finalGroups = inheritPouchColorToParentGroup(
        inheritPouchTitleToParentGroupIfEmpty(
          plan.groups,
          plan.parentGroupId,
          splitGroupTitle,
        ),
        plan.parentGroupId,
        source.color,
      );
    }

    const unchangedPouches = plan.pouches.filter(p => p.id !== pouchId);
    const finalPouches = [...unchangedPouches, ...newPouches];

    const animationResult = HistoryLogic.applyAnimationFlags(
      currentPouches,
      finalPouches,
      currentGroups,
      finalGroups
    );

    const needsTransition =
      plan.createNewGroup ||
      finalGroups.some(g => {
        const prev = currentGroups.find(c => c.id === g.id);
        return prev != null && (prev.path !== g.path || prev.color !== g.color);
      }) ||
      plan.pouches.some(p => {
        const prev = currentPouches.find(c => c.id === p.id);
        return prev != null && (prev.x !== p.x || prev.y !== p.y);
      });

    if (needsTransition) {
      document.documentElement.classList.remove('undo', 'slideshow');
      document.documentElement.classList.add('undoredo', 'redo');
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPouches(animationResult.pouches);
        setGroups(animationResult.groups);
        groupsRef.current = animationResult.groups;
      });
    });

    setMaxPouchZIndex(prev => prev + scalarResult.scalars.length);
    if (plan.createNewGroup) {
      setMaxGroupZIndex(prev => prev + 1);
    }

    splitDebug('apply local state', {
      newPouchIds: newPouches.map(p => p.id),
      removedPouchId: pouchId,
      createNewGroup: plan.createNewGroup,
    });

    window.setTimeout(() => {
      if (needsTransition) {
        document.documentElement.classList.remove('undoredo', 'redo');
      }

      const cleanFinalPouches = finalPouches.map(p => {
        const cleanPouch = { ...p };
        const pouchAny = cleanPouch as any;
        delete pouchAny._isDeleting;
        delete pouchAny._animateFromZero;
        return cleanPouch;
      });

      const cleanFinalGroups = finalGroups.map(g => {
        const cleanGroup = { ...g };
        const groupAny = cleanGroup as any;
        delete groupAny._isAdding;
        return cleanGroup;
      });

      setPouches(cleanFinalPouches);
      setGroups(cleanFinalGroups);
      groupsRef.current = cleanFinalGroups;

      splitDebug('onUpdateActiveSheet', {
        pouchCount: cleanFinalPouches.length,
        groupCount: cleanFinalGroups.length,
      });

      onUpdateActiveSheet(
        cleanFinalPouches,
        cleanFinalGroups,
        true,
        true,
        undefined,
        HistoryActionType.SPLIT_POUCH,
        true
      );

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const area = areaRef.current;
          if (!area) return;
          applyScrollToRevealContentBounds(
            area,
            plan.splitGroupBounds,
            dragEdgeScrollLayoutRef.current,
            () => bumpScrollEdgeBufferIfNearViewportEdge(),
          );
        });
      });
    }, 300);
    } catch (err) {
      abortSplit('handleSplitPouch exception', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, [currentSheet, maxPouchZIndex, maxGroupZIndex, onUpdateActiveSheet, bumpScrollEdgeBufferIfNearViewportEdge]);

  /** キーボード新規キャンセル等: 削除アニメは通常削除と同じ、履歴エントリのみ付けない */
  const handleDeletePouchWithoutHistory = useCallback((pouchId: string) => {
    const currentPouches = pouchesRef.current;
    const currentGroups = groupsRef.current;
    const finalPouches = currentPouches.filter(p => p.id !== pouchId);
    const animationResult = HistoryLogic.applyAnimationFlags(
      currentPouches,
      finalPouches,
      currentGroups,
      currentGroups
    );
    setPouches(animationResult.pouches);
    setTimeout(() => {
      const cleanFinalPouches = finalPouches.map(p => {
        const cleanPouch = { ...p };
        const pouchAny = cleanPouch as any;
        delete pouchAny._isDeleting;
        delete pouchAny._animateFromZero;
        return cleanPouch;
      });
      onUpdateActiveSheet(
        cleanFinalPouches,
        currentGroups,
        true,
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        pouchId
      );
    }, 300);
  }, [setPouches, onUpdateActiveSheet]);

  handleDeletePouchForKeyboardRef.current = handleDeletePouchWithoutHistory;

  // パウチエラー表示の共通関数
  const showPouchError = useCallback((pouchId: string) => {
    // 前回のタイマーをキャンセル（溜まった削除処理がアニメーションを中断するのを防ぐ）
    const existingTimer = pouchErrorTimersRef.current.get(pouchId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    
    // DOM操作でエラー表示 - 複数の要素を全て取得
    const pouchContainers = document.querySelectorAll(`[data-pouch-id="${pouchId}"]`);
    const pouchRows = document.querySelectorAll(`tr[data-pouch-id="${pouchId}"]`);
    
    // 全てのpouchContainerにクラスを追加（アニメーションをリセットするため一度削除してから追加）
    pouchContainers.forEach(container => {
      const el = container as HTMLElement;
      const focused = el.querySelector(':focus');
      if (focused instanceof HTMLElement) {
        focused.blur();
      }
      el.classList.remove('value-error');
      // reflowを強制してアニメーションをリセット
      void el.offsetWidth;
      el.classList.add('value-error');
      const circle = el.querySelector('.pouch-main-circle') as HTMLElement | null;
      if (circle) {
        circle.style.animation = 'none';
        void circle.offsetWidth;
        circle.style.animation = 'shake-error 0.6s ease-in-out';
      }
    });
    
    // 全てのpouchRowの子td要素に直接animationを設定（CSSクラスではなくJSで直接制御）
    pouchRows.forEach(row => {
      // 編集中（フォーカスがある）の行はスキップ
      if (row.querySelector(':focus')) {
        return;
      }
      const tdElements = row.querySelectorAll('td');
      tdElements.forEach(td => {
        const tdEl = td as HTMLElement;
        // 一度animationを完全に削除
        tdEl.style.animation = 'none';
        // reflowを強制
        void tdEl.offsetWidth;
        // 直接animationを設定（CSSの定義を参照せず）
        tdEl.style.animation = 'shake-error 0.6s ease-in-out';
      });
    });
    
    // 1.5秒後にエラー表示を削除（新しいタイマーを設定）
    const timerId = window.setTimeout(() => {
      // パウチコンテナのクラスを削除
      pouchContainers.forEach(container => {
        container.classList.remove('value-error');
        const circle = container.querySelector('.pouch-main-circle') as HTMLElement | null;
        if (circle) {
          circle.style.animation = '';
        }
      });
      // テーブル行のtd要素のanimationをクリア（再度DOMを取得）
      const currentPouchRows = document.querySelectorAll(`tr[data-pouch-id="${pouchId}"]`);
      currentPouchRows.forEach(row => {
        const tdElements = row.querySelectorAll('td');
        tdElements.forEach(td => {
          (td as HTMLElement).style.animation = '';
        });
      });
      // タイマーをMapから削除
      pouchErrorTimersRef.current.delete(pouchId);
    }, 1500);
    
    // タイマーIDを保存
    pouchErrorTimersRef.current.set(pouchId, timerId);
  }, []);
  showPouchErrorRef.current = showPouchError;

  // パウチエラー状態設定ハンドラー（互換性のため残す）
  const handleSetPouchError = useCallback((pouchId: string, hasError: boolean) => {
    if (hasError) {
      showPouchError(pouchId);
    }
  }, [showPouchError]);

  // PC・パウチホバー中: ⌃/Alt+←→ で roundUnit 調整（マルチ矢印と同型の長押し連打）
  useEffect(() => {
    if (isCurrentTouchDevice) {
      return;
    }
    const endRoundUnitAdjust = () => {
      const pid = pouchArrowAdjustActivePouchIdRef.current;
      if (!pid) {
        return;
      }
      pouchArrowAdjustActivePouchIdRef.current = null;
      pouchRefs.current.get(pid)?.current?.keyboardSimulatePressEnd();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!hoveredPouchIdRef.current) {
        return;
      }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return;
      }
      if (!getAltControlModifierKey(e) || e.metaKey) {
        return;
      }
      if (isDrawingGroup || document.body.classList.contains('is-element-dragging')) {
        return;
      }
      if ((window as any).isIMEComposing || isEditableElement(document.activeElement)) {
        return;
      }
      if (document.activeElement?.closest('.table-component')) {
        return;
      }
      if (e.repeat) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }
      const pid = hoveredPouchIdRef.current;
      const pref = pouchRefs.current.get(pid);
      if (!pref?.current) {
        return;
      }
      const direction = e.key === 'ArrowRight' ? 'increase' : 'decrease';
      const ok = pref.current.keyboardSimulatePressStart('roundUnit', direction, e.shiftKey);
      if (!ok) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      pouchArrowAdjustActivePouchIdRef.current = pid;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return;
      }
      endRoundUnitAdjust();
    };
    const onWinBlur = () => {
      endRoundUnitAdjust();
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onWinBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onWinBlur);
      endRoundUnitAdjust();
    };
  }, [isCurrentTouchDevice, isDrawingGroup, getAltControlModifierKey]);

  // パウチのカラー変更ハンドラー
  const handleChangeColor = useCallback((pouchId: string, isShiftPressed: boolean = false) => {
    
    // 色を変更するパウチを見つける
    const targetPouch = pouchesRef.current.find(p => p.id === pouchId);
    if (!targetPouch) return;
    
    // 現在の色のインデックスを取得（idで比較）
    const currentColorIndex = POUCH_COLORS.findIndex(color => color.id === targetPouch.color);
    
    let nextColorId: string;
    
    if (isShiftPressed) {
      // Shift+クリック: 逆ローテーション
      if (currentColorIndex === 0) {
        // 最初の色から逆方向に行く場合は、最後の色
        nextColorId = POUCH_COLORS[POUCH_COLORS.length - 1].id;
      } else {
        // その他の場合は前の色
        const prevColorIndex = (currentColorIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
        nextColorId = POUCH_COLORS[prevColorIndex].id;
      }
    } else {
      // 通常クリック: 通常のローテーション
      const nextColorIndex = (currentColorIndex + 1) % POUCH_COLORS.length;
      // 見つからない場合は最初の色を使用
      nextColorId = currentColorIndex === -1 
        ? POUCH_COLORS[0].id 
        : POUCH_COLORS[nextColorIndex].id;
    }
    
    // パウチの色を更新
    const updatedPouches = pouchesRef.current.map(pouch => {
      if (pouch.id === pouchId) {
        return {
          ...pouch,
          color: nextColorId
        };
      }
      return pouch;
    });
    
    // 統合APIで更新
    const currentGroups = groupsRef.current;
    onUpdateActiveSheet(updatedPouches, currentGroups, true, true, undefined, HistoryActionType.CHANGE_POUCH_COLOR);
  }, [onUpdateActiveSheet]);

  const handleSetPouchColor = useCallback((pouchId: string, colorId: string) => {
    if (!POUCH_COLORS.some(c => c.id === colorId)) {
      return;
    }
    const targetPouch = pouchesRef.current.find(p => p.id === pouchId);
    if (!targetPouch || targetPouch.color === colorId) {
      return;
    }
    const updatedPouches = pouchesRef.current.map(pouch =>
      pouch.id === pouchId ? { ...pouch, color: colorId } : pouch
    );
    const currentGroups = groupsRef.current;
    onUpdateActiveSheet(updatedPouches, currentGroups, true, true, undefined, HistoryActionType.CHANGE_POUCH_COLOR);
  }, [onUpdateActiveSheet]);
  
  // パウチの値更新ハンドラー
  const handleUpdateValue = useCallback((pouchId: string, value: number, historyActionType?: HistoryActionType, forceNewEntry?: boolean, isTime?: boolean) => {
    
    // GTPの最大値チェック用フラグ
    let gtpExceededMaxValue = false;
    
    const updatedPouches = pouchesRef.current.map(pouch => {
      if (pouch.id === pouchId) {
        // GTPの場合は値とisNegativeを更新
        if (pouch.isGroupTotalPouch) {
          // GTPの最大値チェック（9999999999）
          const MAX_VALUE = 9999999999;
          let clampedValue = value;
          if (Math.abs(value) > MAX_VALUE) {
            clampedValue = value > 0 ? MAX_VALUE : -MAX_VALUE;
            gtpExceededMaxValue = true;
          }
          
          // 正負転換ボタンが押された場合（historyActionTypeがある場合）は oppositeToTotal を切り替える。
          // ÷モードでは value / |multiplier| が実寄与になるため、value も同時に反転して符号を即時反映する。
          if (historyActionType === HistoryActionType.EDIT_POUCH_VALUE) {
            const toggledValue = -clampedValue;
            const toggled = {
              ...pouch,
              value: toggledValue,
              isNegative: toggledValue < 0,
              isTime: isTime !== undefined ? isTime : pouch.isTime,
              oppositeToTotal: !pouch.oppositeToTotal
            };
          return {
            ...toggled,
            roundUnit: clampRoundUnitToAbsoluteCap(
              toggled.roundUnit,
              getGroupTotalPouchRoundUnitScaledCap(toggled, groupsRef.current, pouchesRef.current),
            ),
          };
          }
          const gtpUpdated = {
            ...pouch,
            value: clampedValue,
            isNegative: clampedValue < 0,
            isTime: isTime !== undefined ? isTime : pouch.isTime
          };
          return {
            ...gtpUpdated,
            roundUnit: clampRoundUnitToAbsoluteCap(
              gtpUpdated.roundUnit,
              getGroupTotalPouchRoundUnitScaledCap(gtpUpdated, groupsRef.current, pouchesRef.current),
            ),
          };
        }
        
        const lid = currentSheet ? resolveActiveLayerId(currentSheet) : undefined;
        const useLayerValues = !!(currentSheet && pouchUsesLayerValues(currentSheet, pouch));
        const currentRu = useLayerValues && lid ? getPouchLayerRoundUnit(pouch, lid) : pouch.roundUnit;
        const base = {
          ...pouch,
          value,
          isNegative: value < 0,
          isTime: isTime !== undefined ? isTime : pouch.isTime,
        };
        const nextRu = clampRoundUnitToScaledCap(currentRu, base, value, pouch.multiplier);
        const withRound = { ...base, roundUnit: nextRu };
        if (currentSheet?.layers?.length) {
          const layerId = resolveActiveLayerId(currentSheet);
          return setPouchLayerValue(
            withRound,
            layerId,
            value,
            {
              ...(isTime !== undefined ? { isTime } : {}),
              roundUnit: nextRu,
            },
            currentSheet
          );
        }
        return withRound;
      }
      return pouch;
    });
    
    // 統合APIで更新
    const currentGroups = groupsRef.current;
    onUpdateActiveSheet(
      updatedPouches,
      currentGroups,
      true,
      true,
      undefined,
      historyActionType,
      forceNewEntry
    );
    
    // GTPが最大値を超えた場合は震えるアニメーションを発生させる
    // onUpdateActiveSheetの後にReactが再レンダリングされるので、少し遅延させてDOMが更新された後に実行
    if (gtpExceededMaxValue) {
      requestAnimationFrame(() => {
        showPouchError(pouchId);
      });
    }
  }, [onUpdateActiveSheet, showPouchError, currentSheet]);

  useEffect(() => {
    keyboardEscToggleSignRef.current = () => {
      if (isSheetTitleEditing) {
        return;
      }
      const hoverPid = hoveredPouchIdRef.current;
      const hoverGid = hoveredGroupIdRef.current;
      if (hoverPid) {
        const pouch = pouchesRef.current.find(p => p.id === hoverPid);
        if (!pouch || pouch._isDragging) {
          return;
        }
        if (currentSheet?.balancingPouchId === hoverPid) {
          onToggleBalancingPouch?.(hoverPid);
          return;
        }
        if (pouch.isGroupTotalPouch) {
          handleUpdateValue(hoverPid, pouch.value, HistoryActionType.EDIT_POUCH_VALUE);
          handleEditConfirm();
        } else if (pouch.isTime) {
          // 時間は内部値±60×分率。Esc は値の符号を反転（実効の正負反転）
          if (Math.abs(pouch.multiplier) > 9999999999) {
            return;
          }
          handleUpdateValueAndMultiplier(
            hoverPid,
            -pouch.value,
            pouch.multiplier,
            HistoryActionType.EDIT_POUCH_VALUE,
            undefined,
            undefined,
            undefined,
            undefined,
            true
          );
          handleEditConfirm();
        } else {
          const newValue = -pouch.value;
          if (Math.abs(newValue) > 9999999999) {
            return;
          }
          handleUpdateValue(hoverPid, newValue, HistoryActionType.EDIT_POUCH_VALUE);
          handleEditConfirm();
        }
      } else if (hoverGid) {
        const g = groupsRef.current.find(x => x.id === hoverGid);
        if (!g?.pouches?.length) {
          return;
        }
        onToggleGroupSign(hoverGid, pouchesRef.current, groupsRef.current);
      }
    };
  }, [
    handleUpdateValue,
    handleUpdateValueAndMultiplier,
    handleEditConfirm,
    onToggleGroupSign,
    onToggleBalancingPouch,
    currentSheet?.balancingPouchId,
    isSheetTitleEditing,
  ]);
  
  // パウチのタイトル更新ハンドラー
  const handleUpdateTitle = useCallback((pouchId: string, title: string) => {
    const updatedPouches = pouchesRef.current.map(pouch => {
      if (pouch.id === pouchId) {
        return {
          ...pouch,
          title
        };
      }
      return pouch;
    });
    
    // 統合APIで更新
    const currentGroups = groupsRef.current;
    onUpdateActiveSheet(updatedPouches, currentGroups, true, true, undefined, HistoryActionType.EDIT_POUCH_TITLE);
  }, [onUpdateActiveSheet]);
  
  // グループのタイトル更新ハンドラー
  const handleUpdateGroupTitle = useCallback((groupId: string, title: string) => {
    // 最新のグループ情報を使用
    const updatedGroups = groupsRef.current.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          title
        };
      }
      return group;
    });
    
    // 最新のパウチ状態を取得
    const currentPouches = pouchesRef.current;
    
    // 統合更新APIを使用
    onUpdateActiveSheet(currentPouches, updatedGroups, true, true, undefined, HistoryActionType.EDIT_GROUP_TITLE);
  }, [onUpdateActiveSheet]);

  // グループトータルパウチ作成ハンドラー
  const handleCreateGroupTotalPouch = useCallback((groupId: string, clientX: number, clientY: number) => {
    const referencedGroup = groupsRef.current.find(g => g.id === groupId);
    if (!referencedGroup) return;
    

    // 自分自身を参照するGTPを除外したパウチの合計を計算（最新のパウチ情報を使用）
    const groupPouches = pouchesRef.current.filter(p => 
      referencedGroup.pouches?.includes(p.id) && 
      !p._isDeleting && 
      !(p.isGroupTotalPouch && p.referencedGroupId === groupId) && // 自分自身を参照するGTPを除外
      !groupsRef.current.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
    );
    const groupSubtotal = calculateGroupTotal(groupId, groupPouches, groupsRef.current);

    // 初期位置を計算
    let initialX = (() => {
      // 普通のパウチの位置から150px右
      if (areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        return clientX - rect.left + 150;
      }
      return clientX + 150;
    })();
    let initialY = (() => {
      // 普通のパウチの位置から20px上
      if (areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        return clientY - rect.top - 20;
      }
      return clientY - 20;
    })();

    // スナップを適用
    let finalX = snapToGrid(initialX);
    let finalY = snapToGrid(initialY);

    // 既存のGTPを取得
    const existingGTPs = pouchesRef.current.filter(p => p.isGroupTotalPouch && !p._isDeleting);

    // 同じ位置にGTPがあるかチェックし、あれば右にずらす
    const GTP_OFFSET = 100; // ずらす距離
    const POSITION_TOLERANCE = 10; // 位置の許容誤差（この範囲内なら同じ位置とみなす）
    let maxIterations = 100; // 無限ループ防止
    
    while (maxIterations > 0) {
      const hasOverlap = existingGTPs.some(gtp => 
        Math.abs(gtp.x - finalX) < POSITION_TOLERANCE && 
        Math.abs(gtp.y - finalY) < POSITION_TOLERANCE
      );
      
      if (!hasOverlap) break;
      
      // 右にずらす
      finalX = snapToGrid(finalX + GTP_OFFSET);
      maxIterations--;
    }

    // グループトータルパウチを作成
    const groupTotalPouch: Pouch = {
      id: uuidv4(),
      value: Math.abs(groupSubtotal),
      title: '',
      multiplier: 0.1, // 初期マルチは0.1(×10%)
      x: finalX,
      y: finalY,
      isNegative: (groupSubtotal * (false ? -1 : 1)) < 0, // oppositeToTotal: falseなので元の正負
      color: 'gray', // GTPのデフォルトカラーはグレー
      isGroupTotalPouch: true,
      referencedGroupId: groupId,
      oppositeToTotal: false, // 初期値はfalse（グループトータルと同じ正負）
      zIndex: maxPouchZIndex + 1
    };

    // パウチを追加（最新のパウチ情報とグループ情報を使用）
    const updatedPouches = [...pouchesRef.current, groupTotalPouch];
    onUpdateActiveSheet(updatedPouches, groupsRef.current, true, true, undefined, HistoryActionType.ADD_POUCH);
  }, [groups, onUpdateActiveSheet, maxPouchZIndex, getMatchingPouchColor, getPathBounds, calculateGroupTotal]);
  
  // パウチのrefを取得または作成する関数
  const getPouchRef = useCallback((pouchId: string) => {
    if (!pouchRefs.current.has(pouchId)) {
      pouchRefs.current.set(pouchId, React.createRef());
    }
    return pouchRefs.current.get(pouchId)!;
  }, []);

  const groupDisplayTotals = useMemo(
    () => computeAllGroupDisplayTotals(groups, pouches, groups, currentSheet),
    [groups, pouches, currentSheet]
  );

  /** GTP 小計・allPouches 参照用: アクティブレイヤー mirror（top-level multiplier は数量共用） */
  const pouchesMirroredForDisplay = useMemo(() => {
    if (!currentSheet?.layers?.length) return pouches;
    const lid = resolveActiveLayerId(currentSheet);
    return mirrorPouchesForLayer(pouches, lid, currentSheet, groups);
  }, [pouches, groups, currentSheet]);

  // シート全体で個々のパウチ表示に影響する「値以外」の構造/モードシグネチャ。
  // 値編集では変化しないため、PouchComponent の React.memo がスキップ判定に使う。
  const pouchRenderSignature = useMemo(() => {
    const lid = currentSheet ? resolveActiveLayerId(currentSheet) : '';
    const bp = currentSheet?.balancingPouchId || '';
    let ps = '';
    for (const p of pouches) {
      ps += p.id + (p.isTime ? 'T' : '') + (p.isGroupTotalPouch ? 'G' : '') + (p.referencedGroupId ? 'r' + p.referencedGroupId : '') + ';';
    }
    let gs = '';
    for (const g of groups) {
      gs += g.id + ':' + (g.color || '') + ':' + ((g.pouches || []).join('-')) + '|';
    }
    return bp + '#' + lid + '#' + ps + '#' + gs;
  }, [pouches, groups, currentSheet]);

  // パウチレンダリング関数
  const renderPouches = useCallback(() => {
    return pouches.map(pouch => (
      <div
        key={pouch.id}
        className="pouch-wrapper"
        onMouseDown={e => handleMouseDown(e, pouch.id, 'pouch')}
        onTouchStart={e => handleTouchStart(e, pouch.id, 'pouch')}
        // スタイル指定は削除 - 位置はPouchComponentで処理される
      >
        <PouchComponent
          ref={getPouchRef(pouch.id)}
          pouch={pouch}
          lastPouchValue={lastPouchValue}
          lastPouchColor={lastPouchColor}
          onUpdateMultiplier={handleUpdateMultiplier}
          onUpdateValueAndMultiplier={handleUpdateValueAndMultiplier}
          onSetPouchError={handleSetPouchError}
          onDeletePouch={handleDeletePouch}
          onSplitPouch={handleSplitPouch}
          onChangeColor={handleChangeColor}
          onSetPouchColor={handleSetPouchColor}
          isTouchDevice={isCurrentTouchDevice}
          onUpdateValue={handleUpdateValue}
          onUpdateTitle={handleUpdateTitle}
          onEditConfirm={handleEditConfirm}
          isInitialLoading={isInitialLoading}
          isSheetSwitching={isSheetSwitchingRef.current}
          isTableSelected={selectedPouches[pouch.id]}
          allGroups={groups}
          allPouches={pouchesMirroredForDisplay}
          currentSheet={currentSheet}
          onPouchHover={handlePouchHover} // ホバーイベントハンドラーを渡す
          hoveredPouchId={hoveredPouchId} // ホバー状態を渡す
          hoveredPouchIdRef={hoveredPouchIdRef}
          hoveredGroupId={hoveredGroupId} // グループホバー状態を渡す
          onGtpGroupHover={handleGtpGroupHover} // GTPホバー時のグループクラス付け用
          interaction={interactionRef.current} // インタラクション状態を渡す
          onToggleBalancingPouch={onToggleBalancingPouch}
          keyboardValueEditSeedRef={keyboardValueEditSeedRef}
          keyboardOpenExtraCharsRef={keyboardOpenExtraCharsRef}
          keyboardGraceAppendBump={keyboardGraceAppendBump}
          keyboardNewPouchFirstValueEditPouchIdRef={keyboardNewPouchFirstValueEditPouchIdRef}
          onDeletePouchWithoutHistory={handleDeletePouchWithoutHistory}
          pouchRenderSignature={pouchRenderSignature}
        />
      </div>
    ));
  }, [pouches, pouchesMirroredForDisplay, groups, handleMouseDown, handleTouchStart, handleUpdateMultiplier, handleDeletePouch, handleSplitPouch,
      handleChangeColor, handleSetPouchColor, handleUpdateValue, handleUpdateTitle, isInitialLoading, 
      handleEditConfirm, isSheetSwitchingRef, selectedPouches, currentSheet,
      handlePouchHover, hoveredPouchId, hoveredGroupId, handleGtpGroupHover, isCurrentTouchDevice,
      keyboardGraceAppendBump, handleDeletePouchWithoutHistory, pouchRenderSignature]);

  // 他のシートからドラッグを転送するメソッド（グローバル状態管理により簡素化）
  const transferDragFromOtherSheet = useCallback((interactionState: InteractionState) => {
  }, [activeSheetId]);

  // 全てのイベントリスナーを削除するメソッド
  const removeAllEventListeners = useCallback(() => {
    // 全てのイベントリスナーを削除（状態管理付き）
    if (eventListenersRef.current.mousemove) {
      document.removeEventListener('mousemove', handleMouseMove);
      eventListenersRef.current.mousemove = false;
    }
    if (eventListenersRef.current.mouseup) {
      document.removeEventListener('mouseup', handleMouseUp);
      eventListenersRef.current.mouseup = false;
    }
    if (eventListenersRef.current.touchmove) {
      document.removeEventListener('touchmove', handleTouchMove);
      eventListenersRef.current.touchmove = false;
    }
    if (eventListenersRef.current.touchend) {
      document.removeEventListener('touchend', handleTouchEnd);
      eventListenersRef.current.touchend = false;
    }
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd, activeSheetId]);

  // Escキーでの強制キャンセル機能
  const handleForceDragCancel = useCallback(() => {
    
    // ドラッグ状態をリセット
    const interaction = interactionRef.current;
    if (interaction.startTime) {
      // ドラッグ中の要素をその場に置く（ムーブ判定と同じ処理）
      if (interaction.type === 'GROUP_DRAG' || interaction.type === 'DRAG') {
        // 通常のマウスアップと同じ処理を実行
        handleMouseUp(new MouseEvent('mouseup', { bubbles: true }));
        
        // 500ms後にアンドゥを実行（Control+Undoボタンの動作）
        setTimeout(() => {
          const undoEvent = new CustomEvent('forceUndo');
          document.dispatchEvent(undoEvent);
        }, 10);
      } else {
        // ドラッグ以外の場合は即座にフラグをクリア
        const clearedPouches = pouchesRef.current.map(p => ({
          ...p,
          _isDragging: false,
          _isCloneSource: false,
          _isCloning: false
        }));
        const clearedGroups = groupsRef.current.map(g => ({
          ...g,
          _isDragging: false,
          _isCloning: false
        }));
        
        setPouches(clearedPouches);
        setGroups(clearedGroups);
        pouchesRef.current = clearedPouches;
        groupsRef.current = clearedGroups;
        
        // DOM要素のクラスをクリア
        document.querySelectorAll('.group-dragging, .pouch-dragging').forEach(el => {
          el.classList.remove('group-dragging', 'pouch-dragging');
        });
      }
      
      // 全てのイベントリスナーを削除
      removeAllEventListeners();
      
      // ドラッグ状態はリセットしない（シート切り替え時は保持する）
    }
  }, [activeSheetId, onUpdateActiveSheet, removeAllEventListeners]);

  // Escキーでの強制キャンセルイベントリスナー
  useEffect(() => {
    const handleForceDragCancelEvent = () => {
      handleForceDragCancel();
    };

    document.addEventListener('forceDragCancel', handleForceDragCancelEvent);
    return () => {
      document.removeEventListener('forceDragCancel', handleForceDragCancelEvent);
    };
  }, [handleForceDragCancel]);

  // シート間ドラッグで作成された要素を削除するメソッド
  const removeCrossSheetElements = useCallback(() => {
    // シート間ドラッグで作成されたグループを特定
    const crossSheetGroups = groupsRef.current.filter(g => g._isFromOtherSheet);
    
    // これらのグループに含まれるパウチのIDを収集
    const pouchIdsInCrossSheetGroups = new Set<string>();
    crossSheetGroups.forEach(group => {
      group.pouches.forEach(pouchId => {
        pouchIdsInCrossSheetGroups.add(pouchId);
      });
    });
    
    // シート間ドラッグで作成されたパウチも特定
    const crossSheetPouches = pouchesRef.current.filter(p => p._isFromOtherSheet);
    
    
    // シート間ドラッグで作成されたグループとパウチを削除
    const updatedGroups = groupsRef.current.filter(g => !g._isFromOtherSheet);
    const updatedPouches = pouchesRef.current.filter(p => 
      !p._isFromOtherSheet && !pouchIdsInCrossSheetGroups.has(p.id)
    );
    
    setGroups(updatedGroups);
    groupsRef.current = updatedGroups;
    
    setPouches(updatedPouches);
    pouchesRef.current = updatedPouches;
    
    // 強制的に再レンダリングをトリガーしてDOMを更新
    setTimeout(() => {
      setPouches([...updatedPouches]);
      setGroups([...updatedGroups]);
    }, 0);
    
    // データベースにも反映（永続化）
    if (crossSheetGroups.length > 0) {
      onUpdateActiveSheet(updatedPouches, updatedGroups, true, true, undefined, HistoryActionType.DELETE_GROUP);
    } else if (crossSheetPouches.length > 0) {
      onUpdateActiveSheet(updatedPouches, updatedGroups, true, true, undefined, HistoryActionType.DELETE_POUCH);
    }
  }, [onUpdateActiveSheet]);

  // 中央にパウチを作成するメソッドを公開
  useImperativeHandle(ref, () => ({
    createPouchAtCenter: () => {
      if (areaRef.current) {
        // シート作成時の初期パウチと同じ基準位置（createNewPouch 内でグリッドスナップ）
        createNewPouch(60, 90);
      } else {
      }
    },
    getInteractionState: () => {
      return interactionRef.current;
    },
    setPouches: (pouches: Pouch[]) => {
      pouchesRef.current = pouches;
      setPouches(pouches);
    },
    setGroups: (groups: Group[]) => {
      groupsRef.current = groups;
      setGroups(groups);
    },
    forceUpdate: () => {
      // ここで強制的にコンポーネントを再レンダリングするための関数を実装
      // 例: ここで何かを変更して再レンダリングする
      // 内部状態を強制的に更新して再レンダリングをトリガー
      // 最新のpouchesとgroupsを取得してステートを更新
      setPouches([...pouchesRef.current]);
      setGroups([...groupsRef.current]);
    },
    setError: (pouchId: string, hasError: boolean) => {
      if (hasError) {
        showPouchError(pouchId);
      }
    },
    restoreDragState,
    getTempPouchPosition,
    createDraggedContent: (data: {
      group: Group;
      pouches: Pouch[];
      containedGroups: Group[];
      finalPosition: { x: number, y: number };
      sourceSheetId: string;
    }) => {
      // globalInteractionRefから保存された情報を取得
      if (!globalInteractionRef?.current?.draggedGroupData) {
        console.error('createDraggedContent: globalInteractionRefに必要なデータがありません');
        return;
      }
      
      const savedData = globalInteractionRef.current.draggedGroupData;
      const mainGroupPos = globalInteractionRef.current.mainGroupPosition;
      const dragOffset = globalInteractionRef.current.dragOffset;
      
      // 現在のシートが元のシートでないことを確認
      if (activeSheetId === savedData.sourceSheetId) {
        return;
      }
      
      // 現在のマウス位置を取得
      let mouseX = 0, mouseY = 0;
      if (interactionRef.current && interactionRef.current.currentPosition) {
        mouseX = interactionRef.current.currentPosition.x;
        mouseY = interactionRef.current.currentPosition.y;
      }
      
      // 新しいシートでのマウス位置をシート座標に変換
      let sheetMouseX = 0, sheetMouseY = 0;
      if (areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        sheetMouseX = mouseX - rect.left;
        sheetMouseY = mouseY - rect.top;
      }
      
      // 本体グループの新しい位置を計算
      let constrainedMainGroupX: number, constrainedMainGroupY: number;
      
      // 複製かつシート間移動の場合のみ、dragOffsetを考慮した制限を適用
      const isCloneOperation = globalInteractionRef?.current?.isCloning || false;
      if (isCloneOperation && dragOffset) {
        // マウス位置 - dragOffset = グループの実際の左上位置
        const actualGroupX = sheetMouseX - dragOffset.x;
        const actualGroupY = sheetMouseY - dragOffset.y;
        
        // calc-area（calculator-area）の境界を取得
        const calculatorArea = document.querySelector('.calculator-area') as HTMLElement;
        let calcLeft = 0, calcTop = 0;
        
        if (calculatorArea && areaRef.current) {
          const calcRect = calculatorArea.getBoundingClientRect();
          const areaRect = areaRef.current.getBoundingClientRect();
          
          // areaRef基準でのcalc-areaの相対位置
          calcLeft = calcRect.left - areaRect.left;
          calcTop = calcRect.top - areaRect.top;
        }
        
        // 実際のグループ位置をcalc-areaの左端・上端で制限（左と上だけ）
        constrainedMainGroupX = Math.max(calcLeft, actualGroupX);
        constrainedMainGroupY = Math.max(calcTop, actualGroupY);
      } else {
        // 通常の場合は従来通り（元の位置をそのまま復元、ただし左端制限）
        constrainedMainGroupX = Math.max(0, mainGroupPos.left);
        constrainedMainGroupY = mainGroupPos.top;
      }
      
      
      // 現在のパウチとグループを取得
      const currentPouches = pouchesRef.current;
      const currentGroups = groupsRef.current;
      
      // 新しいIDを生成
      const pouchIdMap: {[oldId: string]: string} = {};
      const groupIdMap: {[oldId: string]: string} = {};
      
      // 1. 本体グループを作成
      const newMainGroupId = uuidv4();
      const newMainGroupPath = `M ${constrainedMainGroupX} ${constrainedMainGroupY} L ${constrainedMainGroupX + mainGroupPos.width} ${constrainedMainGroupY} L ${constrainedMainGroupX + mainGroupPos.width} ${constrainedMainGroupY + mainGroupPos.height} L ${constrainedMainGroupX} ${constrainedMainGroupY + mainGroupPos.height} Z`;
      
      const newMainGroup: Group = {
        ...savedData.group,
        id: newMainGroupId,
        path: newMainGroupPath,
        pouches: [], // 後で更新
        zIndex: maxGroupZIndex + 1,
        _isDragging: true
      } as any;
      
      groupIdMap[savedData.group.id] = newMainGroup.id;
      
      // 2. 所属パウチを作成（本体グループからの相対位置を維持）
      const newPouches: Pouch[] = [];
      savedData.pouches.forEach((pouchData: any) => {
        const newPouch = {
          ...pouchData,
          id: uuidv4(),
          x: constrainedMainGroupX + pouchData.relativeX,
          y: constrainedMainGroupY + pouchData.relativeY,
          zIndex: maxPouchZIndex + newPouches.length + 1,
          _isDragging: true
        };
        
        // GTPの参照ID処理
        if (newPouch.isGroupTotalPouch && newPouch.referencedGroupId) {
          const isReferencingMovingGroup = newPouch.referencedGroupId === savedData.group.id || 
            savedData.containedGroups.some((cg: any) => cg.id === newPouch.referencedGroupId);
          
          if (!isReferencingMovingGroup) {
            newPouch.isGroupTotalPouch = false;
            newPouch.referencedGroupId = undefined;
          }
        }
        
        newPouches.push(newPouch);
        pouchIdMap[pouchData.id] = newPouch.id;
      });
      
      // 3. 内包グループを作成（本体グループからの相対位置を維持）
      const newContainedGroups: Group[] = [];
      savedData.containedGroups.forEach((containedData: any, index: number) => {
        const containedGroupBounds = getPathBounds(containedData.path);
        if (!containedGroupBounds) return;
        
        const containedWidth = containedGroupBounds.right - containedGroupBounds.left;
        const containedHeight = containedGroupBounds.bottom - containedGroupBounds.top;
        
        const newContainedX = constrainedMainGroupX + containedData.relativeX;
        const newContainedY = constrainedMainGroupY + containedData.relativeY;
        
        const newContainedPath = `M ${newContainedX} ${newContainedY} L ${newContainedX + containedWidth} ${newContainedY} L ${newContainedX + containedWidth} ${newContainedY + containedHeight} L ${newContainedX} ${newContainedY + containedHeight} Z`;
        
        const newContainedGroup = {
          ...containedData,
          id: uuidv4(),
          path: newContainedPath,
          pouches: containedData.pouches ? containedData.pouches.map((id: string) => pouchIdMap[id] || id) : [],
          zIndex: maxGroupZIndex + 2 + index,
          _isDragging: true
        };
        
        newContainedGroups.push(newContainedGroup);
        groupIdMap[containedData.id] = newContainedGroup.id;
        
        // 内包グループ専用のパウチがある場合の処理
        if (containedData.pouches) {
          containedData.pouches.forEach((pouchId: string) => {
            if (!pouchIdMap[pouchId]) {
              // まだ作成されていないパウチがある場合
              const originalPouch = savedData.pouches.find((p: any) => p.id === pouchId);
              if (originalPouch) {
                const newPouch = {
                  ...originalPouch,
                  id: uuidv4(),
                  x: constrainedMainGroupX + originalPouch.relativeX,
                  y: constrainedMainGroupY + originalPouch.relativeY,
                  zIndex: maxPouchZIndex + newPouches.length + 1,
                  _isDragging: true
                };
                newPouches.push(newPouch);
                pouchIdMap[pouchId] = newPouch.id;
              }
            }
          });
        }
      });
      
      // 4. パウチIDを更新
      newMainGroup.pouches = savedData.group.pouches ? savedData.group.pouches.map((id: string) => pouchIdMap[id] || id) : [];
      
      // 5. GTPの参照IDを更新
      const finalPouches = newPouches.map(pouch => {
        if (pouch.isGroupTotalPouch && pouch.referencedGroupId && groupIdMap[pouch.referencedGroupId]) {
          return {
            ...pouch,
            referencedGroupId: groupIdMap[pouch.referencedGroupId]
          };
        }
        return pouch;
      });
      
      // 6. 状態を更新
      const updatedPouches = [...currentPouches, ...finalPouches];
      const updatedGroups = [...currentGroups, ...newContainedGroups, newMainGroup];
      
      setPouches(updatedPouches);
      setGroups(updatedGroups);
      setMaxPouchZIndex(prev => prev + finalPouches.length);
      setMaxGroupZIndex(prev => prev + 1 + newContainedGroups.length);
      
      pouchesRef.current = updatedPouches;
      groupsRef.current = updatedGroups;
      
      // 7. 状態をDBに保存
      if (onUpdateActiveSheet) {
        onUpdateActiveSheet(updatedPouches, updatedGroups, false, false, undefined, HistoryActionType.ADD_GROUP);
      }
      
      // 8. ドラッグ継続のためのinteractionRef設定
      if (interactionRef.current && interactionRef.current.startTime) {
        interactionRef.current.draggedFromOtherSheet = true;
        interactionRef.current.targetId = newMainGroup.id;
        interactionRef.current.targetType = 'group';
        
        // グローバル状態を更新
        if (globalInteractionRef) {
          globalInteractionRef.current = {
            ...globalInteractionRef.current,
            activeSheetId: activeSheetId,
            targetId: newMainGroup.id
          };
        }
        
        // イベントリスナーを設定
        removeAllEventListeners();
        
        if (!eventListenersRef.current.mousemove) {
          document.addEventListener('mousemove', handleMouseMove);
          eventListenersRef.current.mousemove = true;
        }
        if (!eventListenersRef.current.mouseup) {
          document.addEventListener('mouseup', handleMouseUp);
          eventListenersRef.current.mouseup = true;
        }
        if (!eventListenersRef.current.touchmove) {
          document.addEventListener('touchmove', handleTouchMove);
          eventListenersRef.current.touchmove = true;
        }
        if (!eventListenersRef.current.touchend) {
          document.addEventListener('touchend', handleTouchEnd);
          eventListenersRef.current.touchend = true;
        }
        
        // グループ情報を更新
        interactionRef.current.groupPouches = finalPouches.map(p => ({ 
          id: p.id, 
          x: p.x, 
          y: p.y 
        }));
        
        interactionRef.current.containedGroups = newContainedGroups.map(g => ({ 
          id: g.id, 
          path: g.path 
        }));
        
        // 複製時のdragOffset補正
        const isCloneOperation = globalInteractionRef?.current?.isCloning || false;
        if (interactionRef.current && isCloneOperation) {
          // globalInteractionRefから補正されたdragOffsetを取得
          if (globalInteractionRef?.current?.dragOffset) {
            interactionRef.current.dragOffset = {
              x: interactionRef.current.dragOffset.x + globalInteractionRef.current.originalTranslate.x,
              y: interactionRef.current.dragOffset.y + globalInteractionRef.current.originalTranslate.y
            };
          }
        }
        
        // DOM要素にドラッグクラスを追加
        setTimeout(() => {
          const groupElement = document.querySelector(`[data-group-id="${newMainGroup.id}"]`);
          if (groupElement) {
            (groupElement as HTMLElement).classList.add('group-dragging');
          }
        }, 50);
      }
      
    },
    createDraggedPouch: (data: {
      pouch: Pouch;
      finalPosition: { x: number, y: number };
      sourceSheetId: string;
    }) => {
      const { pouch, finalPosition, sourceSheetId } = data;
      
      // 現在のシートが元のシートでないことを確認
      if (activeSheetId === sourceSheetId) {
        return;
      }
      
      // 現在のパウチを取得
      const currentPouches = pouchesRef.current;
      
      // 新しいパウチを作成
      const newPouch: PouchWithDrag = {
        ...pouch,
        id: uuidv4(), // 新しいIDを生成
        _isDragging: true,
        _isFromOtherSheet: true
      };
      
      // GTPの場合は通常のパウチに変換（GTP関連データをクリア）
      if (newPouch.isGroupTotalPouch) {
        newPouch.isGroupTotalPouch = false;
        newPouch.referencedGroupId = undefined;
        // その他のGTP関連プロパティがあればクリア
      }
      
      // 現在のマウス位置を取得（interactionRefから）
      let mouseX = 0, mouseY = 0;
      if (interactionRef.current && interactionRef.current.currentPosition) {
        mouseX = interactionRef.current.currentPosition.x;
        mouseY = interactionRef.current.currentPosition.y;
      }
      
      // 新しいシートでの基準位置を計算（マウス位置をシート座標に変換）
      let baseX = 0, baseY = 0;
      if (areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        baseX = mouseX - rect.left;
        baseY = mouseY - rect.top;
      }
      
      // 移動制限を考慮した位置を計算
      const minX = 0;
      const minY = 0;
      const maxX = areaRef.current ? areaRef.current.clientWidth - 60 : 740; // パウチサイズを考慮
      const maxY = areaRef.current ? areaRef.current.clientHeight - 60 : 500;
      
      // 位置を制限内に収める
      const constrainedX = Math.max(minX, Math.min(baseX, maxX));
      const constrainedY = Math.max(minY, Math.min(baseY, maxY));
      
      // パウチの位置を設定
      newPouch.x = constrainedX;
      newPouch.y = constrainedY;
      newPouch.zIndex = Math.max(...currentPouches.map(p => p.zIndex || 0), 0) + 1;
      
      // パウチを追加
      const updatedPouches = [...currentPouches, newPouch];
      setPouches(updatedPouches);
      pouchesRef.current = updatedPouches;
      
      // state.sheetsも更新（Total反映のため）
      if (onUpdateActiveSheet) {
        onUpdateActiveSheet(updatedPouches, groupsRef.current, false, false, undefined, HistoryActionType.ADD_POUCH);
      }
      
      // 新しいシートでの正しいdragOffsetを計算
      const newDragOffsetX = mouseX - constrainedX;
      const newDragOffsetY = mouseY - constrainedY;
      
      // interactionRefを更新してドラッグ状態を継続
      interactionRef.current = {
        ...interactionRef.current,
        type: 'DRAG',
        targetId: newPouch.id,
        targetType: 'pouch',
        draggedFromOtherSheet: true,
        dragOffset: { x: newDragOffsetX, y: newDragOffsetY }, // 新しいdragOffsetを設定
        startTime: Date.now(),
        startPosition: { x: mouseX, y: mouseY },
        currentPosition: { x: mouseX, y: mouseY }
      };
      
      // グローバルドラッグ状態を設定
      if (globalInteractionRef) {
        globalInteractionRef.current = {
          ...interactionRef.current,
          activeSheetId: activeSheetId
        };
      }
      
      // 全ての既存のイベントリスナーを削除
      removeAllEventListeners();
      
      // 移動先シートのイベントリスナーを追加（状態管理付き）
      if (!eventListenersRef.current.mousemove) {
        document.addEventListener('mousemove', handleMouseMove);
        eventListenersRef.current.mousemove = true;
      }
      if (!eventListenersRef.current.mouseup) {
        document.addEventListener('mouseup', handleMouseUp);
        eventListenersRef.current.mouseup = true;
      }
      if (!eventListenersRef.current.touchmove) {
        document.addEventListener('touchmove', handleTouchMove);
        eventListenersRef.current.touchmove = true;
      }
      if (!eventListenersRef.current.touchend) {
        document.addEventListener('touchend', handleTouchEnd);
        eventListenersRef.current.touchend = true;
      }
    },
    transferDragFromOtherSheet: (interactionState) => {
      transferDragFromOtherSheet(interactionState);
    },
    removeAllEventListeners: () => {
      removeAllEventListeners();
    },
    removeCrossSheetElements: () => {
      removeCrossSheetElements();
    },
    clearCanvasHover: () => {
      clearCanvasHover();
    },
    revealScrollForUndoRedo: (
      prevPouches: Pouch[],
      prevGroups: Group[],
      nextPouches: Pouch[],
      nextGroups: Group[],
    ) => {
      const bounds = computeUndoRedoRevealBounds(
        prevPouches,
        prevGroups,
        nextPouches,
        nextGroups,
      );
      const area = areaRef.current;
      if (!bounds || !area) return;

      const livePouches = nextPouches.filter(p => !(p as { _isDeleting?: boolean })._isDeleting);
      const liveGroups = nextGroups.filter(g => !(g as { _isDeleting?: boolean })._isDeleting);
      pouchesRef.current = livePouches;
      groupsRef.current = liveGroups;

      const runRevealScroll = () => {
        if (!areaRef.current) return;
        updateCalcAreaSize(bounds);
        bumpScrollEdgeBufferIfNearViewportEdge();
        applyScrollToRevealContentBounds(
          areaRef.current,
          bounds,
          dragEdgeScrollLayoutRef.current,
          () => bumpScrollEdgeBufferIfNearViewportEdge(),
        );
      };

      // scrollWidth が届くよう先に計算エリアを広げる（大 N グループの undo/redo）
      updateCalcAreaSize(bounds);

      // 大きなグループの React 描画後に bounds を画面へ
      window.setTimeout(runRevealScroll, Math.min(300, UNDO_REDO_TRANSITION_MS));
    },
  }));

    

  // グループ書き出し処理
  const handleExportGroup = useCallback((groupId: string) => {
    // Appから渡された関数があれば呼び出す
    if (onExportGroup) {
      onExportGroup(groupId);
    }
  }, [onExportGroup]);

  // グループ正負転換処理
  const handleToggleGroupSign = useCallback((groupId: string) => {
    // Appから渡された関数があれば呼び出す（最新のパウチデータとグループデータを渡す）
    if (onToggleGroupSign) {
      onToggleGroupSign(groupId, pouchesRef.current, groupsRef.current);
    }
  }, [onToggleGroupSign]);

  // グループカラー変更処理
  const handleChangeGroupColor = useCallback((groupId: string, isShiftPressed: boolean = false, isCtrlOptionPressed: boolean = false) => {
    
    let updatedGroups: Group[];
    
    if (isShiftPressed) {
      // Shift+クリック: 逆ローテーション
      updatedGroups = changeGroupColor(groupId, groupsRef.current, true); // 逆ローテーション
    } else if (isCtrlOptionPressed) {
      // Ctrl/Option+クリック: ミュートグループに変更
      const targetGroup = groupsRef.current.find(g => g.id === groupId);
      if (!targetGroup) return;
      
      if (targetGroup.color === 'mute') {
        // 現在がミュートの場合は、元の色に戻す
        const originalColor = targetGroup.originalColor !== undefined ? targetGroup.originalColor : 'yellow'; // イエロー
        updatedGroups = groupsRef.current.map(g => 
          g.id === groupId 
            ? { ...g, color: originalColor, originalColor: undefined }
            : g
        );
      } else {
        // 現在がミュートでない場合は、ミュートに変更
        updatedGroups = groupsRef.current.map(g => 
          g.id === groupId 
            ? { ...g, originalColor: g.color, color: 'mute' }
            : g
        );
      }
    } else {
      // 通常クリック: 通常のローテーション
      updatedGroups = changeGroupColor(groupId, groupsRef.current);
    }
    
    // 変更されたグループの色を取得
    const updatedGroup = updatedGroups.find(g => g.id === groupId);
    if (updatedGroup && updatedGroup.color !== undefined) {
      try {
        // 最新のパウチ状態を取得
        const currentPouches = pouchesRef.current;
        
        // 統合APIを使用してグループデータを更新
        // App.tsxのhandleUpdateActiveSheetでグループカラー変更を検出し、lastGroupColorを更新
        
        onUpdateActiveSheet(currentPouches, updatedGroups, true, true, undefined, HistoryActionType.CHANGE_GROUP_COLOR, true);
        } catch (err) {
          // エラーハンドリング
        }
    }
  }, [groups, onUpdateActiveSheet]);

  /** グループホットキー: 単色を直接指定（changeGroupColor のローテーションとは別経路） */
  const handleSetGroupColorById = useCallback((groupId: string, colorId: string) => {
    if (!GROUP_COLORS.some(c => c.id === colorId)) {
      return;
    }
    const updatedGroups = groupsRef.current.map(g => {
      if (g.id !== groupId) return g;
      return { ...g, color: colorId, originalColor: undefined };
    });
    const updatedGroup = updatedGroups.find(g => g.id === groupId);
    if (updatedGroup && updatedGroup.color !== undefined) {
      try {
        onUpdateActiveSheet(pouchesRef.current, updatedGroups, true, true, undefined, HistoryActionType.CHANGE_GROUP_COLOR, true);
      } catch {
        /* ignore */
      }
    }
  }, [onUpdateActiveSheet]);
  
  // グループマージ処理
  // プレビュー用: レンダリング前のパウチスナップショット
  const gtpPreviewSnapshotRef = useRef<Pouch[] | null>(null);

  // マージボタン押下中のプレビュー: 葉パウチに実際のレンダー後の値/マルチを反映（履歴には積まない）
  // mode=null でキャンセル（snapshot に戻す）
  const handleMergeGroupTotalPouchPreview = useCallback((groupId: string, mode: 'value' | 'multi' | null) => {
    if (mode === null) {
      if (gtpPreviewSnapshotRef.current) {
        setPouches(gtpPreviewSnapshotRef.current);
        gtpPreviewSnapshotRef.current = null;
      }
      return;
    }

    // 初回プレビュー時に snapshot を保存（モード切替時は再利用）
    if (!gtpPreviewSnapshotRef.current) {
      gtpPreviewSnapshotRef.current = pouchesRef.current;
    }
    const snapshot = gtpPreviewSnapshotRef.current;

    const targetGroup = groupsRef.current.find(g => g.id === groupId);
    if (!targetGroup) return;

    const preview = currentSheet
      ? renderGroupInclusiveGtpForSheet(
          targetGroup,
          snapshot,
          groupsRef.current,
          mode,
          currentSheet,
          { commit: false }
        )
      : renderGroupInclusiveGtp(
          targetGroup,
          snapshot,
          groupsRef.current,
          mode,
          { commit: false }
        );
    if (!preview) {
      // レンダリング不可: スナップショットへ戻す
      setPouches(snapshot);
      gtpPreviewSnapshotRef.current = null;
      return;
    }
    setPouches(preview);
  }, [setPouches, currentSheet]);

  // 内包GTPを値/マルチにレンダリング（Merge Group Total Pouch）
  const handleMergeGroupTotalPouch = useCallback((groupId: string, mode: 'value' | 'multi') => {
    const targetGroup = groupsRef.current.find(g => g.id === groupId);
    if (!targetGroup) return;

    // プレビュー中はスナップショットを基準にする（二重適用防止）
    const basePouches = gtpPreviewSnapshotRef.current || pouchesRef.current;
    const updatedPouches = currentSheet
      ? renderGroupInclusiveGtpForSheet(
          targetGroup,
          basePouches,
          groupsRef.current,
          mode,
          currentSheet,
          { commit: true }
        )
      : renderGroupInclusiveGtp(
          targetGroup,
          basePouches,
          groupsRef.current,
          mode,
          { commit: true }
        );
    if (!updatedPouches) {
      // レンダリング不可: プレビューをキャンセル
      if (gtpPreviewSnapshotRef.current) {
        setPouches(gtpPreviewSnapshotRef.current);
        gtpPreviewSnapshotRef.current = null;
      }
      return;
    }

    gtpPreviewSnapshotRef.current = null;
    onUpdateActiveSheet(
      updatedPouches,
      groupsRef.current,
      true,
      true,
      undefined,
      HistoryActionType.MERGE_GTP
    );
  }, [onUpdateActiveSheet, setPouches, currentSheet]);

  /** 2階: 同一 value の葉パウチを 1 パウチ（value × 合計マルチ）に統合。グループ枠は維持 */
  const handleMergeGroupScalar = useCallback((groupId: string) => {
    const targetGroup = groupsRef.current.find(g => g.id === groupId);
    if (!targetGroup || !currentSheet) return;

    const leaves = getGroupScalarMergeLeaves(
      targetGroup,
      pouchesRef.current,
      groupsRef.current,
    );
    if (leaves.length < 2) return;

    const bounds = getPathBounds(targetGroup.path);
    if (!bounds) return;

    const merged = buildMergedScalarPouchFromLeaves(
      leaves,
      (bounds.left + bounds.right) / 2,
      (bounds.top + bounds.bottom) / 2,
      maxPouchZIndex,
      currentSheet,
    );
    if (!merged) return;

    const leafIds = new Set(leaves.map(p => p.id));
    const currentPouches = pouchesRef.current;
    const currentGroups = groupsRef.current;

    const finalPouches = [
      ...currentPouches.filter(p => !leafIds.has(p.id)),
      merged,
    ];
    const finalGroups = currentGroups.map(g => {
      if (g.id !== groupId) return g;
      const kept = (g.pouches ?? []).filter(id => !leafIds.has(id));
      return { ...g, pouches: [...kept, merged.id] };
    });

    const animationResult = HistoryLogic.applyAnimationFlags(
      currentPouches,
      finalPouches,
      currentGroups,
      finalGroups,
    );

    document.documentElement.classList.remove('undo', 'slideshow');
    document.documentElement.classList.add('undoredo', 'redo');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPouches(animationResult.pouches);
        setGroups(animationResult.groups);
        groupsRef.current = animationResult.groups;
      });
    });

    setMaxPouchZIndex(prev => prev + 1);

    window.setTimeout(() => {
      document.documentElement.classList.remove('undoredo', 'redo');

      const cleanFinalPouches = finalPouches.map(p => {
        const cleanPouch = { ...p };
        const pouchAny = cleanPouch as any;
        delete pouchAny._isDeleting;
        delete pouchAny._animateFromZero;
        return cleanPouch;
      });

      onUpdateActiveSheet(
        cleanFinalPouches,
        finalGroups,
        true,
        true,
        undefined,
        HistoryActionType.MERGE_GROUP,
      );
    }, 300);
  }, [currentSheet, getPathBounds, maxPouchZIndex, onUpdateActiveSheet, setGroups, setPouches]);

  const handleMergeGroup = useCallback((groupId: string, isShiftPressed: boolean = false, isAltControlPressed: boolean = false) => {
    const targetGroup = groupsRef.current.find(g => g.id === groupId);
    if (!targetGroup) return;
    
    // グループに所属するパウチを取得
    const pouchesInGroup = pouchesRef.current.filter(p => 
      targetGroup.pouches && targetGroup.pouches.includes(p.id)
    );
    
    // 所属パウチがない場合は何もしない
    if (pouchesInGroup.length === 0) return;
    
    // 所属パウチが1つでもShift+マージは実行する（条件を削除）
    
    // マージモードでグループ削除処理を実行
    if (isShiftPressed) {
      // Alt/Control+マージ: 内包グループのみ削除
      handleDeleteGroup(groupId, false, false, true, true);
    } else {
      // 通常マージ: グループ本体を削除
      handleDeleteGroup(groupId, true, false, false, true);
    }
  }, [groups, pouches]);

  // グループ削除処理
  const handleDeleteGroup = useCallback((groupId: string, isCtrlOptionPressed: boolean = false, isShiftPressed: boolean = false, isAltControlPressed: boolean = false, isMergeMode: boolean = false) => {
    const currentPouches = pouchesRef.current;
    // 削除モードを決定
    let deletePouches = false;
    let keepMainGroup = false;
    let targetGroups: string[] = [];
    
    if (isCtrlOptionPressed) {
      // Ctrl/Option: 本・内・パ または 本・内
      deletePouches = !isShiftPressed;
      keepMainGroup = false;
    } else if (isAltControlPressed) {
      // Alt/Control: 内・パ または 内
      deletePouches = !isShiftPressed;
      keepMainGroup = true;
    } else {
      // 通常: 本のみ
      targetGroups = [groupId];
    }
    
    // 削除対象を取得
    let pouchesToDelete: string[] = [];
    if (targetGroups.length === 0) {
      const result = deleteGroupWithContents(groupId, groups, currentPouches, deletePouches, keepMainGroup);
      targetGroups = result.groupsToDelete;
      pouchesToDelete = result.pouchesToDelete;      
    }
    
    // 最終的な状態を計算（削除後の状態）
    let finalPouches = deletePouches 
      ? currentPouches.filter(p => !pouchesToDelete.includes(p.id))
      : currentPouches;
    let finalGroups = groupsRef.current.filter(g => !targetGroups.includes(g.id));
    
    // 削除されたグループのIDをパウチのgroupIdsから除去
    if (targetGroups.length > 0) {
      finalPouches = finalPouches.map(p => {
        const pouch = p as any;
        if (pouch.groupIds && pouch.groupIds.length > 0) {
          // keepMainGroupがtrueの場合、本体グループのIDは除去しない
          const updatedGroupIds = pouch.groupIds.filter((pouchGroupId: string) => {
            if (isAltControlPressed && pouchGroupId === groupId) {
              // 本体グループのIDは除去しない
              return true;
            }
            const shouldRemove = targetGroups.includes(pouchGroupId);
            return !shouldRemove;
          });
          return {
            ...pouch,
            groupIds: updatedGroupIds
          };
        }
        return pouch;
      });
      
      // 削除されたグループを参照しているGTPも削除対象に追加
      // 旧実装（コメントアウト）
      // finalPouches = finalPouches.filter(p => {
      //   if (p.isGroupTotalPouch && p.referencedGroupId) {
      //     return !targetGroups.includes(p.referencedGroupId);
      //   }
      //   return true;
      // });urn true;
      // });
      
      // 新実装：削除されたグループを参照しているGTPはフラグ解除
      finalPouches = finalPouches.map(p => {
        if (p.isGroupTotalPouch && p.referencedGroupId && targetGroups.includes(p.referencedGroupId)) {
          // 削除されたグループを参照しているGTPはフラグ解除
          return {
            ...p,
            isGroupTotalPouch: false,
            referencedGroupId: undefined
          };
        }
        return p;
      });
    }
    
    // マージモードの場合は新しいパウチをfinalPouchesに追加
    if (isMergeMode) {
      const targetGroup = groupsRef.current.find(g => g.id === groupId);
      if (targetGroup) {
        // グループに所属するパウチを取得（GTPは除外）
        const pouchesInGroup = currentPouches.filter(p => 
          targetGroup.pouches && targetGroup.pouches.includes(p.id) && !p.isGroupTotalPouch
        );
        if (pouchesInGroup.length > 0 && currentSheet) {
          const sheetGroups = groupsRef.current;
          const { subtotal, layerValues } = buildGroupMergePouchLayerData(
            groupId,
            pouchesInGroup,
            sheetGroups,
            currentSheet
          );

          const bounds = getPathBounds(targetGroup.path);
          if (bounds) {
            const centerX = (bounds.left + bounds.right) / 2;
            const centerY = (bounds.top + bounds.bottom) / 2;
            const newPouchColor = getMatchingPouchColor(targetGroup.color || 'gray');
            const rounded = Math.round(subtotal);
            const newPouch = createPouch(
              centerX,
              centerY,
              rounded,
              newPouchColor,
              maxPouchZIndex,
              currentSheet
            );

            const pouchTitle = isAltControlPressed
              ? ''
              : targetGroup.title && targetGroup.title.trim() !== ''
                ? targetGroup.title
                : targetGroup.placeholderText || '';

            const finalNewPouch: Pouch = (() => {
              const membersAllTime = allScalarMergeLeavesAreTimePouches(
                pouchesInGroup,
                currentSheet,
              );
              const base: Pouch = {
                ...newPouch,
                ...(layerValues ? { layerValues } : {}),
                title: pouchTitle,
                color: newPouchColor,
              };
              if (membersAllTime) {
                if (layerValues) {
                  const synced = syncMergedTimePouchTopLevel(
                    { ...base, isTime: true },
                    currentSheet,
                  );
                  const timeLayerId = getPrimaryTimeLayerId(currentSheet);
                  const activeLayerId = resolveActiveLayerId(currentSheet);
                  const lid =
                    timeLayerId && getPouchLayerIsTime(synced, timeLayerId, currentSheet)
                      ? timeLayerId
                      : activeLayerId;
                  return {
                    ...synced,
                    isNegative:
                      getLayerValue(synced, lid) * getPouchLayerMultiplier(synced, lid) < 0,
                  };
                }
                const baked = timePouchStateFromEffectiveMinutes(rounded);
                return {
                  ...base,
                  isTime: true,
                  value: baked.value,
                  multiplier: baked.multiplier,
                  isNegative: rounded < 0,
                };
              }
              const timeLayerId = getPrimaryTimeLayerId(currentSheet);
              if (layerValues && timeLayerId && getPouchLayerIsTime(base, timeLayerId, currentSheet)) {
                const synced = syncMergedTimePouchTopLevel(
                  { ...base, isTime: true },
                  currentSheet,
                );
                return {
                  ...synced,
                  isNegative:
                    getLayerValue(synced, timeLayerId) *
                      getPouchLayerMultiplier(synced, timeLayerId) <
                    0,
                };
              }
              return {
                ...base,
                value: rounded,
                isNegative: rounded < 0,
              };
            })();

            finalPouches.push(finalNewPouch);
          }
        }
      }
    }
    // applyAnimationFlagsを使用してアニメーション用フラグを設定
    const animationResult = HistoryLogic.applyAnimationFlags(
      currentPouches,
      finalPouches,
      groups,
      finalGroups
    );
    
    // 状態を更新してアニメーションを開始
    setPouches(animationResult.pouches);
    setGroups(animationResult.groups);
    
    // 300ms後に実際の削除処理を実行
    setTimeout(() => {
      // アニメーション用フラグを除去して最終状態に更新
      const cleanFinalPouches = finalPouches.map(p => {
        const cleanPouch = { ...p };
        const pouchAny = cleanPouch as any;
        delete pouchAny._isDeleting;
        delete pouchAny._animateFromZero;
        return cleanPouch;
      });
      
      const cleanFinalGroups = finalGroups.map(g => {
        const cleanGroup = { ...g };
        const groupAny = cleanGroup as any;
        delete groupAny._isDeleting;
        delete groupAny._isAdding;
        return cleanGroup;
      });
      
      onUpdateActiveSheet(cleanFinalPouches, cleanFinalGroups, true, true, undefined, isMergeMode ? HistoryActionType.MERGE_GROUP : HistoryActionType.DELETE_GROUP);
    }, 300);
  }, [groups, onUpdateActiveSheet, setGroups, setPouches, createNewPouch, getPathBounds, getMatchingPouchColor, currentSheet, maxPouchZIndex]);

  // renderGroups関数を更新
  const renderGroups = useCallback(() => {
    return groups.map(group => (
      <GroupComponent
        key={group.id}
        group={group}
        displayTotals={groupDisplayTotals[group.id]}
        pouches={pouches}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        isInitialLoading={isInitialLoading}
        isSheetSwitching={isSheetSwitchingRef.current}
        lastGroupColor={lastGroupColor}
        onMergeGroup={handleMergeGroup}
        onMergeGroupScalar={handleMergeGroupScalar}
        onMergeGroupTotalPouch={handleMergeGroupTotalPouch}
        onMergeGroupTotalPouchPreview={handleMergeGroupTotalPouchPreview}
        onExportGroup={handleExportGroup}
        onResetInteraction={resetInteraction}
        onToggleGroupSign={handleToggleGroupSign}
        onChangeGroupColor={handleChangeGroupColor}
        onSetGroupColorById={handleSetGroupColorById}
        isTouchDevice={isCurrentTouchDevice}
        onDeleteGroup={handleDeleteGroup}
        onUpdateGroupTitle={handleUpdateGroupTitle}
        onEditConfirm={handleEditConfirm}
        onCreateGroupTotalPouch={handleCreateGroupTotalPouch}
        onCheckGroupDragging={(groupId) => {
          const group = groupsRef.current.find(g => g.id === groupId);
          const isDragging = group?._isDragging || false;
          return isDragging;
        }}
        allGroups={groups} // 全グループリストを渡す
        isTableSelected={selectedGroups[group.id]} // テーブル選択状態を渡す
        sheetTitle={currentSheet?.title} // シート名を渡す
        onGroupHover={handleGroupHover} // ホバーイベントハンドラーを渡す
        hoveredGroupId={hoveredGroupId} // ホバー状態を渡す
        hoveredGroupIdRef={hoveredGroupIdRef}
        gtpHoveredGroupId={gtpHoveredGroupId} // GTPホバー時のグループ状態を渡す
        allPouches={pouches} // 全パウチリストを渡す
        currentSheet={currentSheet}
      />
    ));
  }, [groups, pouches, groupDisplayTotals, handleMouseDown, handleTouchStart, isInitialLoading, isSheetSwitchingRef, 
      handleMergeGroup, handleMergeGroupScalar, handleMergeGroupTotalPouch, handleMergeGroupTotalPouchPreview, handleExportGroup, handleChangeGroupColor, handleSetGroupColorById, handleDeleteGroup, 
      handleUpdateGroupTitle, handleEditConfirm, handleCreateGroupTotalPouch, selectedGroups, currentSheet,
      handleGroupHover, hoveredGroupId, gtpHoveredGroupId, isCurrentTouchDevice]);



  // 全グループの z-index を一括更新（旧: 各 GroupComponent が同処理を重複実行）
  const groupZIndexSignature = useMemo(
    () => groups.map(g => `${g.id}:${g.path}`).join('|'),
    [groups]
  );
  useEffect(() => {
    if (groups.length <= 1) return;

    const zIndexManager = GroupZIndexManager.getInstance();
    const allGroupIds = groups.map(g => g.id);
    const timeoutId = window.setTimeout(() => {
      zIndexManager.updateAllGroupZIndices(allGroupIds);
    }, 100);

    return () => {
      window.clearTimeout(timeoutId);
      zIndexManager.clearCache();
    };
  }, [groupZIndexSignature, groups.length]);

  // 要素の変更を監視して計算エリアサイズを更新
  useEffect(() => {
    updateCalcAreaSize();
  }, [pouches, groups, updateCalcAreaSize]);

  // ウィンドウリサイズ時の判定
  useEffect(() => {
    const handleResize = () => {
      updateCalcAreaSize();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateCalcAreaSize]);

  // 左サイドバーの開閉時の判定
  useEffect(() => {
    const observer = new MutationObserver(() => {
      // 少し遅延させてCSSの適用を待つ
      setTimeout(() => {
        updateCalcAreaSize();
      }, 10);
    });

    // calculator-areaのtransition完了時にも判定
    const handleTransitionEnd = (e: Event) => {
      const transitionEvent = e as TransitionEvent;
      if (
        transitionEvent.target === document.querySelector('.calculator-area') &&
        transitionEvent.propertyName === 'margin-left'
      ) {
        updateCalcAreaSize();
      }
    };

    // 左サイドバー要素のクラス変更を監視
    const leftSidebar = document.querySelector('.left-sidebar');
    if (leftSidebar) {
      observer.observe(leftSidebar, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // calculator-area の margin-left トランジション（サイドバー開閉）
    const calculatorArea = document.querySelector('.calculator-area');
    if (calculatorArea) {
      calculatorArea.addEventListener('transitionend', handleTransitionEnd);
    }

    return () => {
      observer.disconnect();
      if (calculatorArea) {
        calculatorArea.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
  }, [isInitialLoading, initialState.settings.isLeftSidebarOpen, updateCalcAreaSize]);


  // 印刷開始時にスケールを計算するuseEffect
  useEffect(() => {
    const handleBeforePrint = () => {
      // 印刷時のスケール計算
      const calculatePrintScale = () => {
        const calculatorArea = document.querySelector('.calculator-area');
        if (!calculatorArea) return 1;
        
        // 実際のコンテンツの境界を計算
        let contentLeft = 0;
        let contentTop = 0;
        let contentRight = 0;
        let contentBottom = 0;
        
        // パウチの境界をチェック
        pouchesRef.current.forEach(pouch => {
          if (pouch._isDeleting) return;
          
          const pouchSize = 80; // パウチの基本サイズ
          const pouchLeft = pouch.x;
          const pouchRight = pouch.x + pouchSize;
          const pouchTop = pouch.y;
          const pouchBottom = pouch.y + pouchSize;
          
          contentLeft = Math.min(contentLeft, pouchLeft);
          contentTop = Math.min(contentTop, pouchTop);
          contentRight = Math.max(contentRight, pouchRight);
          contentBottom = Math.max(contentBottom, pouchBottom);
        });
        
        // グループの境界をチェック
        groupsRef.current.forEach(group => {
          if (!group.path) return;
          
          // グループの境界を取得
          const bounds = getPathBounds(group.path);
          if (!bounds) return;
          
          contentLeft = Math.min(contentLeft, bounds.left);
          contentTop = Math.min(contentTop, bounds.top);
          contentRight = Math.max(contentRight, bounds.right);
          contentBottom = Math.max(contentBottom, bounds.bottom);
        });
        
        // コンテンツの実際のサイズを計算
        const contentWidth = contentRight - contentLeft;
        const contentHeight = contentBottom - contentTop;
        
        // 最小サイズを保証
        if (contentWidth <= 0 || contentHeight <= 0) {
          return 1;
        }
        
        // A4横向きの印刷サイズ（mm）
        const pageWidth = 297; // A4横向きの幅
        const pageHeight = 210; // A4横向きの高さ
        
        // 余白を考慮
        const margin = 20; // 20mm
        const availableWidth = pageWidth - (margin * 2);
        const availableHeight = pageHeight - (margin * 2);
        
        const scaleX = availableWidth / contentWidth;
        const scaleY = availableHeight / contentHeight;
        
        // 小さい方のスケールを使用（全体が収まるように）
        const baseScale = Math.min(scaleX, scaleY, 1);
        
        // 算出した値の4倍が実際の最適スケール
        return baseScale * 3;
      };
      
      const scale = calculatePrintScale();
      document.documentElement.style.setProperty('--print-scale', scale.toString());
    };
    
    window.addEventListener('beforeprint', handleBeforePrint);
    
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, []);

  const handleCalcAreaTooltipAnchorEnter = useCallback(() => {
    if (isSheetTitleEditing) return;
    if (isCurrentTouchDevice) return;
    setCalcAreaGuideHoverTier(1);
    if (calcAreaGuideHoverTimerRef.current !== null) {
      window.clearTimeout(calcAreaGuideHoverTimerRef.current);
    }
    if (isGuideEnabled) {
      calcAreaGuideHoverTimerRef.current = window.setTimeout(() => {
        setCalcAreaGuideHoverTier(2);
        calcAreaGuideHoverTimerRef.current = null;
      }, CALC_AREA_GUIDE_HOVER_MS);
    } else {
      calcAreaGuideHoverTimerRef.current = null;
    }
  }, [isSheetTitleEditing, isCurrentTouchDevice, isGuideEnabled]);

  const handleCalcAreaTooltipAnchorLeave = useCallback(() => {
    if (calcAreaGuideHoverTimerRef.current !== null) {
      window.clearTimeout(calcAreaGuideHoverTimerRef.current);
      calcAreaGuideHoverTimerRef.current = null;
    }
    setCalcAreaGuideHoverTier(0);
  }, []);

  useEffect(() => {
    if (!isSheetTitleEditing) return;
    if (calcAreaGuideHoverTimerRef.current !== null) {
      window.clearTimeout(calcAreaGuideHoverTimerRef.current);
      calcAreaGuideHoverTimerRef.current = null;
    }
    setCalcAreaGuideHoverTier(0);
  }, [isSheetTitleEditing]);

  useEffect(() => {
    return () => {
      if (calcAreaGuideHoverTimerRef.current !== null) {
        window.clearTimeout(calcAreaGuideHoverTimerRef.current);
      }
    };
  }, []);

  const calculatorAreaTooltipHtml = useMemo(() => {
    if (isSheetTitleEditing) {
      if (!isGuideEnabled) return '';
      const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const enterHint = formatKeyDescLine('Enter', esc(tt.commitTitle));
      const escHint = formatKeyDescLine('Esc', esc(tt.discard));
      return `${enterHint}<br/>${escHint}`;
    }
    const base = formatTooltip(`Double Click: ${tt.doubleClickCreatePouch}\nDrag: ${tt.dragCreateGroup}`);
    if (calcAreaGuideHoverTier < 2 || isCurrentTouchDevice || !isGuideEnabled) {
      return base;
    }
    return `${base}<br/>${formatSheetTitleGuideShortcutHtml()}<br/>${formatEnterAndDigitsCreatePouchHtml()}<br/>${formatLayerGuideShortcutsHtml()}`;
  }, [isSheetTitleEditing, calcAreaGuideHoverTier, isCurrentTouchDevice, isGuideEnabled, tt]);

  return (
    <>
      
      <div className="calculator-area-shell">
      <div 
        ref={areaRef}
        className={`calculator-area ${isDrawingGroup ? 'group-drawing-mode' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleCalcAreaMouseMove}
        onMouseEnter={handleCalcAreaMouseEnter}
        onMouseLeave={handleCalcAreaMouseLeave}
        onTouchStart={handleTouchStart}
        data-debug="true"
        data-sheet-id={activeSheetId}
      >
      {/* 白オーバーレイ（::before グリッドと同じ範囲。padding トランジションに追従） */}
      <div className="grid-overlay" aria-hidden />
      <img className="bg-logo" alt="POUCHES" src={`${process.env.PUBLIC_URL || ''}/logo.svg`} />
      {/* 計算エリアツールチップ用（パウチ・グループの背後、空白部分のみでホバー） */}
      <div 
        className="calculator-area-tooltip-anchor"
        data-tooltip-id="tt" 
        data-tooltip-html={calculatorAreaTooltipHtml || undefined} 
        data-tooltip-float={true} 
        data-tooltip-offset={24}
        onMouseEnter={handleCalcAreaTooltipAnchorEnter}
        onMouseLeave={handleCalcAreaTooltipAnchorLeave}
      />
      
      {/* 描画中のグループ（pendingGroup）表示 */}
      {pendingGroup && (
        <PendingGroupComponent 
          pendingPath={pendingGroup} 
          lastGroupColor={lastGroupColor}
          isTooSmall={
            (() => {
              const bounds = getPathBounds(pendingGroup);
              return bounds ? 
                ((bounds.right - bounds.left) < 100 || (bounds.bottom - bounds.top) < 50) : 
                false;
            })()
          }
        />
      )}
      
      {/* グループコンポーネント */}
      {renderGroups()}
      
      {/* パウチコンポーネント */}
      {renderPouches()}
      
      {/* 保留中のパウチ表示 */}
      {pendingPouch && (
        <div 
          key={`pending-${pendingPouch.id}`} 
          className="pending-pouch"
          style={{
            left: `${pendingPouch.x}px`,
            top: `${pendingPouch.y}px`,
            opacity: 0,
            animation: `pending-pouch-fade ${PENDING_POUCH_DURATION}ms linear forwards 0.2s`,
            '--pouch-color': pendingPouch.color
          } as React.CSSProperties}
          onTouchStart={handlePendingPouchTouchStart}
        >
          <div className="pending-pouch-content">+</div>
        </div>
      )}
      </div>
      </div>
      <div id="scroll-pad" />
    </>
  );
};

export default forwardRef(ActiveSheetComponent); 
